const LOCALE_ATTRIBUTE = 'locale';
const DEFAULT_LOCALE_ATTRIBUTE = 'defaultLocale';
const TIME_ZONE_ATTRIBUTE = 'timeZone';
const I18N_DOMAIN_ATTRIBUTE = 'i18nDomain';
const DEFAULT_I18N_DOMAIN = '';

/**
 * This class is used as a heterogeneous map key that encodes the type of the value to maintain some type-safety.
 * The idea is that a properly implemented map (as in {@link AttributesMapLocalizationContext}) only accepts values
 * whose compile-time type corresponds to the type param of the key.
 * <p>
 * This class also maintains a static registry to make sure that no two key instances have the same underlying string,
 * to avoid defining multiple {@link AttributeKey} instances with the same key and different type parameters.
 *
 * @param <T> the type for values associated with the current key instance.
 * @see core-attribute.ts file for a list of keys supported by this library
 */
// @ts-ignore
class AttributeKey {
    constructor(_key) {
        this._key = _key;
    }
    /**
     * Creates a new AttributeKey after confirming that no other AttributeKey has been created with the same "key".
     * This may be used to define custom attributes that will be present a
     * {@link LocalizationContext}, and can be retrieved using the getAttribute method.
     * <p>
     * If you define a custom attribute, you are responsible for providing values to your
     * {@link AttributesMapLocalizationContextProvider}. See {@link AttributesProviderBuilder} for a convenient way to
     * do that.
     *
     * @param key the "name" of the new AttributeKey
     * @type <T> the type of values associated with this key
     * @return an AttributeKey with the given key, if none already exists.
     * @throws Error if an attribute with the given key already exists -- AttributeKeys should be
     *                               constants defined exactly once in your code.
     */
    static of(key) {
        if (this.KEY_REGISTRY.has(key)) {
            throw new Error(`Key with name ${key} already exist. AttributeKeys ${JSON.stringify(this.KEY_REGISTRY)} must only be created once.`);
        }
        this.KEY_REGISTRY.add(key);
        return new AttributeKey(key);
    }
    /**
     * @return the key in string
     */
    asString() {
        return this._key;
    }
}
AttributeKey.KEY_REGISTRY = new Set();

class CoreAttributes {
}
CoreAttributes.LOCALE_KEY = AttributeKey.of(LOCALE_ATTRIBUTE);
CoreAttributes.DEFAULT_LOCALE_KEY = AttributeKey.of(DEFAULT_LOCALE_ATTRIBUTE);
CoreAttributes.TIME_ZONE_KEY = AttributeKey.of(TIME_ZONE_ATTRIBUTE);
CoreAttributes.I18N_DOMAIN_KEY = AttributeKey.of(I18N_DOMAIN_ATTRIBUTE);
CoreAttributes.CX_TUPLE_KEY = AttributeKey.of('cxTuple');
CoreAttributes.CURRENCY_KEY = AttributeKey.of('currency');
CoreAttributes.DEFAULT_CURRENCY_KEY = AttributeKey.of('defaultCurrency');

/**
 * Implementation of LocalizationContext.
 */
class LocalizationContextImplementation {
    /**
     * Construct LocalizationContextImplementation via builder.
     * @param builder LocalizationContextBuilder
     */
    constructor(builder) {
        this._locale = builder.locale;
        this._defaultLocale = builder.defaultLocale;
        this._timeZone = builder.timeZone;
        this._i18nDomain = builder.i18nDomain;
    }
    /**
     * Get timeZone from localization context.
     * @returns timeZone.
     */
    getTimeZone() {
        return this._timeZone;
    }
    /**
     * Get locale from localization context.
     * @returns locale.
     */
    getLocale() {
        return this._locale;
    }
    /**
     * Get default locale from localization context.
     * @returns default locale.
     */
    getDefaultLocale() {
        return this._defaultLocale;
    }
    /**
     * Gets i18nDomain
     * @returns i18nDomain.
     */
    getI18nDomain() {
        return this._i18nDomain;
    }
    /**
     * Get all attributes from localization context.
     * @returns attributes.
     */
    asAttributesMap() {
        const result = new Map([
            [LOCALE_ATTRIBUTE, this._locale],
            [DEFAULT_LOCALE_ATTRIBUTE, this._defaultLocale],
            [TIME_ZONE_ATTRIBUTE, this._timeZone],
            [I18N_DOMAIN_ATTRIBUTE, this._i18nDomain],
        ]);
        return result;
    }
    toString() {
        let stringForm = 'LocalizationContext[';
        stringForm += `${LOCALE_ATTRIBUTE}=${this.getLocale()},`;
        stringForm += `${TIME_ZONE_ATTRIBUTE}=${this.getTimeZone()},`;
        stringForm += `${DEFAULT_LOCALE_ATTRIBUTE}=${this.getDefaultLocale()},`;
        stringForm += `${I18N_DOMAIN_ATTRIBUTE}=${this.getI18nDomain()}]`;
        return stringForm;
    }
    getAttribute(attributeKey) {
        switch (attributeKey) {
            case CoreAttributes.LOCALE_KEY:
                return this.getLocale();
            case CoreAttributes.DEFAULT_LOCALE_KEY:
                return this.getDefaultLocale();
            case CoreAttributes.TIME_ZONE_KEY:
                return this.getTimeZone();
            case CoreAttributes.I18N_DOMAIN_KEY:
                return this.getTimeZone();
            default:
                return undefined;
        }
    }
}

/**
 * Builder class for {@link LocalizationContextImplementation}.
 */
//TODO: Use options object instead of builder.
class LocalizationContextBuilder {
    constructor() {
        this._locale = LocalizationContextBuilder.DEFAULT_LOCALE;
        this._defaultLocale = undefined;
        this._timeZone = LocalizationContextBuilder.DEFAULT_TIME_ZONE;
        this._i18nDomain = DEFAULT_I18N_DOMAIN;
    }
    /**
     * Gets locale. Default value is OS locale.
     */
    get locale() {
        return this._locale;
    }
    /**
     * Gets default locale. Default value is undefined.
     */
    get defaultLocale() {
        return this._defaultLocale;
    }
    /**
     * Gets timeZone. Default value is set to local time zone.
     */
    get timeZone() {
        return this._timeZone;
    }
    /**
     * Gets i18nDomain. Default value is empty string.
     */
    get i18nDomain() {
        return this._i18nDomain;
    }
    /**
     * Sets locale on context.
     * @param locale
     * @returns a reference to this object with locale set
     */
    withLocale(locale) {
        try {
            // @ts-ignore
            this._locale = Intl.getCanonicalLocales(locale)[0];
        }
        catch (error) {
            console.error(`Provided Locale is invalid: locale=${locale}. Using default runtime locale: ${LocalizationContextBuilder.DEFAULT_LOCALE}`);
        }
        return this;
    }
    /**
     * Sets default locale.
     * @param defaultLocale
     * @returns a reference to this object with default locale set
     */
    withDefaultLocale(defaultLocale) {
        try {
            // @ts-ignore
            this._defaultLocale = Intl.getCanonicalLocales(defaultLocale)[0];
        }
        catch (error) {
            console.error(`Provided DefaultLocale is invalid: defaultLocale:${defaultLocale}.`);
        }
        return this;
    }
    /**
     * Sets i18nDomain.
     * @param i18nDomain
     * @returns a reference to this object with i18nDomain set
     */
    withI18nDomain(domain) {
        this._i18nDomain = domain;
        return this;
    }
    /**
     * Sets timeZone. TimeZone plain string is validated using Intl.
     * @param timeZone
     * @returns a reference to this object with timeZone set
     */
    withTimeZone(timeZone) {
        try {
            const dateTimeFormat = new Intl.DateTimeFormat(undefined, { timeZone });
            this._timeZone = dateTimeFormat.resolvedOptions().timeZone;
        }
        catch (error) {
            console.error(`Provided TimeZone is invalid: timeZone=${timeZone}. Using default TimeZone: ${LocalizationContextBuilder.DEFAULT_TIME_ZONE}`);
        }
        return this;
    }
    /**
     * Builds localization context.
     * @returns LocalizationContextImplementation
     */
    build() {
        return new LocalizationContextImplementation(this);
    }
}
LocalizationContextBuilder.DATE_TIME_OPTIONS = new Intl.DateTimeFormat().resolvedOptions();
LocalizationContextBuilder.DEFAULT_LOCALE = LocalizationContextBuilder.DATE_TIME_OPTIONS.locale;
LocalizationContextBuilder.DEFAULT_TIME_ZONE = LocalizationContextBuilder.DATE_TIME_OPTIONS.timeZone;

class CallbackLocalizationContextProvider {
    constructor(provideContext) {
        this.provideContext = provideContext;
    }
    getLocalizationContext() {
        return this.provideContext();
    }
}

/**
 * LocalizationContext for Amazon Retail.
 */
class RetailLocalizationContext extends LocalizationContextImplementation {
    /**
     * Construct RetailLocalizationContext via builder.
     * @param builder RetailLocalizationContextBuilder
     */
    constructor(builder) {
        super(builder);
        this._marketplace = builder.marketplace;
    }
    /**s
     * Get all attributes from localization context.
     * @returns attributes.
     */
    asAttributesMap() {
        const result = super.asAttributesMap();
        result.set(MARKETPLACE_ATTRIBUTE, this._marketplace);
        return result;
    }
    toString() {
        let stringForm = 'RetailLocalizationContext[';
        stringForm += `${LOCALE_ATTRIBUTE}=${this.getLocale()},`;
        stringForm += `${TIME_ZONE_ATTRIBUTE}=${this.getTimeZone()},`;
        stringForm += `${DEFAULT_LOCALE_ATTRIBUTE}=${this.getDefaultLocale()},`;
        stringForm += `${I18N_DOMAIN_ATTRIBUTE}=${this.getI18nDomain()},`;
        stringForm += `${MARKETPLACE_ATTRIBUTE}=${this._marketplace}]`;
        return stringForm;
    }
}
const MARKETPLACE_ATTRIBUTE = 'marketplace';

class Marketplace$1 {
    /**
     * Method for the platform to override to have the obfuscated marketplace id retrieval of the marketplace
     * based on the platform, and return obfuscated marketplace id of the marketplace
     * @return {undefined | string} undefined
     */
    getObfuscatedMarketplaceId() {
        return undefined;
    }
    /**
     * Method for the platform to override to have the default locale retrieval of the marketplace based on
     * the platform, and return default locale of the marketplace
     *
     * @return {undefined | string} undefined
     */
    getDefaultLocale() {
        return Intl.DateTimeFormat().resolvedOptions().locale;
    }
    /**
     * Method for the platform to override to have the default currency retrieval of the marketplace based on
     * the platform, and return default currency of the marketplace
     *
     * @return {undefined | string} undefined
     */
    getDefaultCurrency() {
        return undefined;
    }
}

/**
 * EmptyMarketplace with undefined marketplaceId, locale, and currencyCode
 */
class EmptyMarketplace$1 extends Marketplace$1 {
}
/**
 * This class providing a provider generate EmptyMarketplace,
 * as default marketplace provider in marketplace-creator
 */
// tslint:disable-next-line:max-classes-per-file
class EmptyMarketplaceProvider$1 {
    getMarketplace() {
        return EmptyMarketplaceProvider$1.emptyMarketplace;
    }
}
EmptyMarketplaceProvider$1.emptyMarketplace = new EmptyMarketplace$1();

class MarketplaceCreator$1 {
    /**
     * Set the customMarketplaceProvider as the singleton marketplaceProvider.
     * @param {MarketplaceProvider} customMarketplaceProvider - User specified marketplace marketplaceProvider
     */
    static setProvider(customMarketplaceProvider) {
        MarketplaceCreator$1.marketplaceProvider = customMarketplaceProvider;
    }
    /**
     * Create singleton Marketplace marketplaceProvider which creates marketplace configuration.
     * Return the singleton marketplaceProvider if it exists.
     * Otherwise, set an EmptyMarketplaceProvider object as singleton marketplaceProvider and return it.
     * @return {MarketplaceProvider} the singleton MarketplaceProvider marketplaceProvider
     */
    static provider() {
        if (!MarketplaceCreator$1.marketplaceProvider) {
            MarketplaceCreator$1.marketplaceProvider = new EmptyMarketplaceProvider$1();
        }
        return MarketplaceCreator$1.marketplaceProvider;
    }
}
MarketplaceCreator$1.marketplaceProvider = undefined;

const MARKETPLACE_DEFAULTS$1 = {
    // NA marketplaces
    A2Q3Y263D00KWC: {
        name: 'BR',
        locale: 'pt-BR',
        currency: 'BRL'
    },
    AZXD3QD5B39HD: {
        name: 'BR_DEVO',
        locale: 'pt-BR',
        currency: 'BRL'
    },
    A2EUQ1WTGCTBG2: {
        name: 'CA',
        locale: 'en-CA',
        currency: 'CAD'
    },
    A2WDZJZ5TCRMLH: {
        name: 'CL',
        locale: 'es-CL',
        currency: 'CLP'
    },
    A2IDBVIE7EAGN4: {
        name: 'CL_DEVO',
        locale: 'es-CL',
        currency: 'CLP'
    },
    A1ZXRLSQW2V54D: {
        name: 'CO',
        locale: 'es-CO',
        currency: 'COP'
    },
    A1NOHO22DE3OHQ: {
        name: 'CO_DEVO',
        locale: 'es-CO',
        currency: 'COP'
    },
    A1AM78C64UM0Y8: {
        name: 'MX',
        locale: 'es-MX',
        currency: 'MXN'
    },
    A3P3J5A7D2ZVXI: {
        name: 'MX_DEVO',
        locale: 'es-MX',
        currency: 'MXN'
    },
    ATVPDKIKX0DER: {
        name: 'US',
        locale: 'en-US',
        currency: 'USD'
    },
    AGWSWK15IEJJ7: {
        name: 'PYOP_US_PROD',
        locale: 'en-US',
        currency: 'USD'
    },
    A3BXB0YN3XH17H: {
        name: 'PYOP_US_PROD_SANDBOX',
        locale: 'en-US',
        currency: 'USD'
    },
    A35A4JO734ER04: {
        name: 'PYOP_US_DEVO',
        locale: 'en-US',
        currency: 'USD'
    },
    A341L3VCFKNMIY: {
        name: 'PYOP_US_DEVO_SANDBOX',
        locale: 'en-US',
        currency: 'USD'
    },
    A2CE4MDCUAASDP: {
        name: 'PYOP_CA_PROD',
        locale: 'en-CA',
        currency: 'CAD'
    },
    A2VJI655WT2I0B: {
        name: 'PYOP_CA_PROD_SANDBOX',
        locale: 'en-CA',
        currency: 'CAD'
    },
    A1NWP2E4EKQP94: {
        name: 'PYOP_CA_DEVO',
        locale: 'en-CA',
        currency: 'CAD'
    },
    ASBG27DKM7EX6: {
        name: 'PYOP_CA_DEVO_SANDBOX',
        locale: 'en-CA',
        currency: 'CAD'
    },
    // EU marketplaces
    A2VIGQ35RCS4UG: {
        name: 'AE',
        locale: 'en-AE',
        currency: 'AED'
    },
    A34GYYCZVDBSIK: {
        name: 'AE_DEVO',
        locale: 'en-AE',
        currency: 'AED'
    },
    A1PA6795UKMFR9: {
        name: 'DE',
        locale: 'de-DE',
        currency: 'EUR'
    },
    AMEN7PMS3EDWL: {
        name: 'BE',
        locale: 'fr-FR',
        currency: 'EUR'
    },
    A1YFOMBPFBRY22: {
        name: 'BE_DEVO',
        locale: 'fr-FR',
        currency: 'EUR'
    },
    ARBP9OOSHTCHU: {
        name: 'EG',
        locale: 'en-AE',
        currency: 'EGP'
    },
    AUJPM9XGFJRC7: {
        name: 'EG_DEVO',
        locale: 'en-AE',
        currency: 'EGP'
    },
    A1RKKUPIHCS9HS: {
        name: 'ES',
        locale: 'es-ES',
        currency: 'EUR'
    },
    AJZF8LZ1EJVJN: {
        name: 'ES_DEVO',
        locale: 'es-ES',
        currency: 'EUR'
    },
    A13V1IB3VIYZZH: {
        name: 'FR',
        locale: 'fr-FR',
        currency: 'EUR'
    },
    A1F83G8C2ARO7P: {
        name: 'GB',
        locale: 'en-GB',
        currency: 'GBP'
    },
    APJ6JRA9NG5V4: {
        name: 'IT',
        locale: 'it-IT',
        currency: 'EUR'
    },
    A3HOBANJMCMD83: {
        name: 'IT_DEVO',
        locale: 'it-IT',
        currency: 'EUR'
    },
    A21TJRUUN4KGV: {
        name: 'IN',
        locale: 'en-IN',
        currency: 'INR'
    },
    A2XZLSVIQ0F4JT: {
        name: 'IN_DEVO',
        locale: 'en-IN',
        currency: 'INR'
    },
    A3OCL2LJ6GDH9T: {
        name: 'NG',
        locale: 'en-NG',
        currency: 'NGN'
    },
    A3PJZM0QIA8QDU: {
        name: 'NG_DEVO',
        locale: 'en-NG',
        currency: 'NGN'
    },
    A1805IZSGTT6HS: {
        name: 'NL',
        locale: 'nl-NL',
        currency: 'EUR'
    },
    A1M3WC0SJ3A38T: {
        name: 'NL_DEVO',
        locale: 'nl-NL',
        currency: 'EUR'
    },
    A1C3SOZRARQ6R3: {
        name: 'PL',
        locale: 'pl-PL',
        currency: 'PLN'
    },
    AKY4K4WKH21YQ: {
        name: 'PL_DEVO',
        locale: 'pl-PL',
        currency: 'PLN'
    },
    A17E79C6D8DWNP: {
        name: 'SA',
        locale: 'en-AE',
        currency: 'SAR'
    },
    A1MQPSGJ6U9Q54: {
        name: 'SA_DEVO',
        locale: 'en-AE',
        currency: 'SAR'
    },
    A2NODRKZP88ZB9: {
        name: 'SE',
        locale: 'sv-SE',
        currency: 'SEK'
    },
    AT614YYYNOC1S: {
        name: 'SE_DEVO',
        locale: 'sv-SE',
        currency: 'SEK'
    },
    A33AVAJ2PDY3EV: {
        name: 'TR',
        locale: 'tr-TR',
        currency: 'TRY'
    },
    A3CQBQD3RGPJR8: {
        name: 'TR_DEVO',
        locale: 'tr-TR',
        currency: 'TRY'
    },
    AE08WJ6YKNBMC: {
        name: 'ZA',
        locale: 'en-ZA',
        currency: 'ZAR'
    },
    A3TFQ1G9L8TQ5L: {
        name: 'ZA_DEVO',
        locale: 'en-ZA',
        currency: 'ZAR'
    },
    AZAJMM36N6WQL: {
        name: 'PYOP_UK_PROD',
        locale: 'en-GB',
        currency: 'GBP'
    },
    A3M3RRFO9XDT2G: {
        name: 'PYOP_UK_PROD_SANDBOX',
        locale: 'en-GB',
        currency: 'GBP'
    },
    A2UWQKHEFGNQY3: {
        name: 'PYOP_UK_DEVO',
        locale: 'en-GB',
        currency: 'GBP'
    },
    A2V84NPDZD3ZEH: {
        name: 'PYOP_UK_DEVO_SANDBOX',
        locale: 'en-GB',
        currency: 'GBP'
    },
    A53RDEWN57UU5: {
        name: 'PYOP_DE_PROD',
        locale: 'de-DE',
        currency: 'EUR'
    },
    A1G8446IYHA4MR: {
        name: 'PYOP_DE_PROD_SANDBOX',
        locale: 'de-DE',
        currency: 'EUR'
    },
    A34RX6LWLXD2KA: {
        name: 'PYOP_DE_DEVO',
        locale: 'de-DE',
        currency: 'EUR'
    },
    A225AO9WA6YYOF: {
        name: 'PYOP_DE_DEVO_SANDBOX',
        locale: 'de-DE',
        currency: 'EUR'
    },
    A3FDG49KKM823Y: {
        name: 'PYOP_IN_PROD',
        locale: 'en-IN',
        currency: 'INR'
    },
    A1IAUC0NN979H6: {
        name: 'PYOP_IN_PROD_SANDBOX',
        locale: 'en-IN',
        currency: 'INR'
    },
    A18VPDB9UTK24D: {
        name: 'PYOP_IN_DEVO',
        locale: 'en-IN',
        currency: 'INR'
    },
    A16Z2XWZJTFS70: {
        name: 'PYOP_IN_DEVO_SANDBOX',
        locale: 'en-IN',
        currency: 'INR'
    },
    // FE marketplaces
    A39IBJ37TRP1C6: {
        name: 'AU',
        locale: 'en-AU',
        currency: 'AUD'
    },
    A1RNPCQ4K8U27I: {
        name: 'AU_DEVO',
        locale: 'en-AU',
        currency: 'AUD'
    },
    A1VC38T7YXB528: {
        name: 'JP',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    AD2EMQ3L3PG8S: {
        name: 'RU',
        locale: 'ru-RU',
        currency: 'RUB'
    },
    A38NPJYVS5YHNH: {
        name: 'RU_DEVO',
        locale: 'ru-RU',
        currency: 'RUB'
    },
    A19VAU5U5O7RUS: {
        name: 'SG',
        locale: 'en-SG',
        currency: 'SGD'
    },
    A1DQ6JV7I20JSG: {
        name: 'SG_DEVO',
        locale: 'en-SG',
        currency: 'SGD'
    },
    A75P1MG8VMU51: {
        name: 'PYOP_JP_PROD',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    A31YDYE76E6TCP: {
        name: 'PYOP_JP_PROD_SANDBOX',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    A2EFPHRCZ60KI3: {
        name: 'PYOP_JP_DEVO',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    A1U1OSY46WLA42: {
        name: 'PYOP_JP_DEVO_SANDBOX',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    // CN Marketplaces
    AAHKV2X7AFYLW: {
        name: 'CN',
        locale: 'zh-CN',
        currency: 'CNY'
    }
};

/**
 * This class is based on the Java DefaultMarketplaceImpl,
 * providing default marketplace specific implementation.
 */
class DefaultMarketplaceImpl$1 extends Marketplace$1 {
    /**
     * This constructor enables consumers to override the default locale and default currency for the valid amazon
     * marketplace IDs.
     * If the default locale or default currency is passed as undefined for the valid obfuscatedMarketplaceId as key,
     * then the default locale or default currency will be retrieved from the MARKETPLACE_DEFAULTS mapping.
     * If it is a invalid amazon retail marketplace ID, and will have, the associated values will be assigned as undefined
     *
     * @param {undefined | string} obfuscatedMarketplaceIdParam - obfuscated marketplace id of the marketplace
     * @param {undefined | string} defaultLocaleParam - default locale of the marketplace
     * @param {undefined | string} defaultCurrencyParam - default currency of the marketplace
     */
    constructor(obfuscatedMarketplaceId, defaultLocale, defaultCurrency) {
        super();
        // the obfuscatedMarketplaceId must be presented to look up the default locale and currency
        if (obfuscatedMarketplaceId) {
            this.obfuscatedMarketplaceId = obfuscatedMarketplaceId;
            const defaultValues = MARKETPLACE_DEFAULTS$1[obfuscatedMarketplaceId];
            this.defaultLocale = defaultLocale
                ? defaultLocale
                : defaultValues
                    ? defaultValues.locale
                    : super.getDefaultLocale();
            this.defaultCurrency = defaultCurrency
                ? defaultCurrency
                : defaultValues
                    ? defaultValues.currency
                    : undefined;
        }
        else {
            this.obfuscatedMarketplaceId = undefined;
            this.defaultLocale = undefined;
            this.defaultCurrency = undefined;
        }
    }
    /**
     * get the default obfuscatedMarketplaceId of the marketplace with default implementation behavior
     * @return {undefined | string} obfuscatedMarketplaceId - default obfuscatedMarketplaceId of the marketplace
     */
    getObfuscatedMarketplaceId() {
        return this.obfuscatedMarketplaceId;
    }
    /**
     * get the default locale of the marketplace with default implementation behavior
     * @return {undefined | string} defaultLocale - default locale of the marketplace
     */
    getDefaultLocale() {
        return this.defaultLocale;
    }
    /**
     * get the default currency of the marketplace with default implementation behavior
     * @return {undefined | string} defaultCurrency - default currency of the marketplace
     */
    getDefaultCurrency() {
        return this.defaultCurrency;
    }
    toString() {
        return `Marketplace[obfuscatedMarketplaceId=${this.getObfuscatedMarketplaceId()},defaultLocale=${this.getDefaultLocale()},defaultCurrency=${this.getDefaultCurrency()}]`;
    }
    /**
     * Returns a map of all Amazon Marketplaces and their corresponding ObfuscatedIds.
     * e.g. GB = A1F83G8C2ARO7P
     *      AE_DEVO = A34GYYCZVDBSIK
     *
     * @return {Map<string, string>} map of marketplaces and their obfuscated ids.
     */
    static getObfuscatedMarketplaceIdMap() {
        const map = new Map();
        for (const marketplaceId of Object.keys(MARKETPLACE_DEFAULTS$1)) {
            map.set(MARKETPLACE_DEFAULTS$1[marketplaceId].name, marketplaceId);
        }
        return map;
    }
}

/**
 * Builder class for {@link RetailLocalizationContext}.
 */
class RetailLocalizationContextBuilder extends LocalizationContextBuilder {
    constructor() {
        super();
        this._marketplace = MarketplaceCreator$1.provider().getMarketplace();
    }
    /**
     * Gets marketplace.
     */
    get marketplace() {
        return this._marketplace;
    }
    /**
     * Sets marketplace on context corresponding to the given obfuscated marketplace id.
     * @param obfuscatedMarketplaceId
     * @returns a reference to this object with marketplace set
     */
    withMarketplace(obfuscatedMarketplaceId) {
        this._marketplace = new DefaultMarketplaceImpl$1(obfuscatedMarketplaceId);
        return this;
    }
    /**
     * Builds retail localization context.
     * @returns RetailLocalizationContext
     */
    build() {
        return new RetailLocalizationContext(this);
    }
}

/**
 * CXProgram is a customer experience variations which may have unique messaging, configuration, or other features.
 * For example, exports, smile or b2b.
 */
class CXProgram {
    constructor(programs) {
        this._programs = [...programs];
        this._programs.sort();
    }
    /**
     * Construct a CXProgram instance with the list of programs
     */
    static of(programs) {
        return new CXProgram(programs);
    }
    /**
     * Check if the CXProgram contains the given program
     */
    has(program) {
        return this._programs.includes(program);
    }
    /**
     * Check if the CXProgram is empty
     */
    isEmpty() {
        return this._programs.length == 0;
    }
    toString() {
        const programStr = this._programs.join(CXProgram.DELIMITER);
        return CXProgram.OPENING_CHAR + programStr + CXProgram.CLOSING_CHAR;
    }
    /**
     * Construct CXTuple instance from qualified string of form "[program1,program2]"
     */
    static fromString(fullyQualifiedString) {
        if (!fullyQualifiedString.startsWith(CXProgram.OPENING_CHAR)
            || !fullyQualifiedString.endsWith(CXProgram.CLOSING_CHAR)) {
            throw new Error(`Invalid fully qualified CXProgram: ${fullyQualifiedString}. Valid example: [exports].`);
        }
        const programStr = fullyQualifiedString.substring(1, fullyQualifiedString.length - 1).trim();
        if (programStr.length == 0) {
            return CXProgram.EMPTY;
        }
        const programs = [];
        for (const program of programStr.split(CXProgram.DELIMITER)) {
            const programEnum = program.trim().toLowerCase();
            if (!Object.values(Program).includes(programEnum)) {
                throw new Error(`Unrecognized program ${program}`);
            }
            programs.push(programEnum);
        }
        return new CXProgram(programs);
    }
    equals(o) {
        if (!o) {
            return false;
        }
        // can compare the string directly as we sort the program in constructor.
        return this.toString() === o.toString();
    }
}
/**
 * Reserved CXProgram instance that contains empty program list.
 */
CXProgram.EMPTY = CXProgram.of([]);
CXProgram.DELIMITER = ',';
CXProgram.OPENING_CHAR = '[';
CXProgram.CLOSING_CHAR = ']';
/**
 * Program that represents the customers' shopping mode.
 */
var Program;
(function (Program) {
    Program["EXPORTS"] = "exports";
    Program["B2B"] = "b2b";
})(Program || (Program = {}));

const CXFAMILY_CXID_SEPARATOR = '/';
/**
 * A representation of a CXFamily + CXId pari (see https://w.amazon.com/bin/view/INTech/CXFamilyCXId/).
 *
 * Valid characters in a CXFamily or CXId include
 * English ASCII letters (upper and lower case), dot (.), and dash (-).
 */
class CXTuple {
    constructor(cxFamily, cxId, cxProgram = CXProgram.EMPTY) {
        this._cxFamily = CXTuple.validateString('cxFamily', cxFamily);
        this._cxId = CXTuple.validateString('cxId', cxId);
        this._cxProgram = cxProgram;
    }
    get cxFamily() {
        return this._cxFamily;
    }
    get cxId() {
        return this._cxId;
    }
    get cxProgram() {
        return this._cxProgram;
    }
    /**
     * Create a CXTuple instance with given cxFamily and cxId.
     */
    static of(cxFamily, cxId, cxProgram) {
        return new CXTuple(cxFamily, cxId, cxProgram);
    }
    /**
     * Create a CXTuple instance with given qualified string.
     * @param fullyQualifiedCXTuple a string of the form "cxFamily/cxId" or "cxFamily/cxId/[exports]"
     */
    static fromString(fullyQualifiedCXTuple) {
        const parts = fullyQualifiedCXTuple.split(CXFAMILY_CXID_SEPARATOR);
        if (parts.length != 2 && parts.length != 3) {
            throw new Error(`Invalid fully qualified CXTuple: ${fullyQualifiedCXTuple}`);
        }
        const cxFamily = parts[0];
        const cxId = parts[1];
        if (parts.length == 3) {
            const cxProgram = CXProgram.fromString(parts[2]);
            return new CXTuple(cxFamily, cxId, cxProgram);
        }
        return new CXTuple(cxFamily, cxId);
    }
    /**
     * Convert the CXTuple to String in the form of "cxFamily/cxId".
     */
    toString() {
        if (!this._cxProgram.isEmpty()) {
            return this._cxFamily + CXFAMILY_CXID_SEPARATOR + this._cxId + CXFAMILY_CXID_SEPARATOR + this._cxProgram.toString();
        }
        return this._cxFamily + CXFAMILY_CXID_SEPARATOR + this._cxId;
    }
    /**
     * Verify if two cxTuple instances are equals.
     *
     * Two instances are equal only when the cxFamily/cxId are identical.
     */
    equals(o) {
        return this._cxFamily === o._cxFamily && this._cxId === this._cxId && this._cxProgram === o._cxProgram;
    }
    static validateString(fieldName, value) {
        if (!value) {
            throw new Error(`${fieldName} must not be empty`);
        }
        for (let i = 0; i < value.length; i++) {
            const c = value.charAt(i);
            // Valid characters are alphanumeric, dot (e.g. "amazon.com"), and dash (e.g. "prime-video").
            // We may allow more characters later, but this is the minimum set required to support existing values.
            if (!(c === '.' || c === '-' || (c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9'))) {
                throw new Error(`Invalid character ${c} in ${fieldName}: ${value}`);
            }
        }
        return value;
    }
}
CXTuple.UNKNOWN = new CXTuple('unknown', 'unknown');

/**
 * Implementation of a LocalizationContext as a heterogeneous map of "attributes".
 * <p>
 * Each attribute is identified by an {@link AttributeKey} whose type parameter enforces (at compile-time) the type
 * of the attribute's value.
 * <p>
 * This allows new attributes to be added without modifying the {@link LocalizationContext} interface, as the new
 * attributes can be accessed via the getAttribute method.
 */
class AttributesMapLocalizationContext {
    constructor(_attributes) {
        this._attributes = _attributes;
    }
    getAttribute(attributeKey) {
        return this._attributes.get(attributeKey);
    }
    asAttributesMap() {
        const attributeMap = new Map();
        for (const key of this._attributes.keys()) {
            attributeMap.set(key.asString(), this._attributes.get(key));
        }
        return attributeMap;
    }
    getDefaultLocale() {
        return this.getAttribute(CoreAttributes.DEFAULT_LOCALE_KEY);
    }
    getI18nDomain() {
        return this.getAttribute(CoreAttributes.I18N_DOMAIN_KEY);
    }
    getLocale() {
        return this.getAttribute(CoreAttributes.LOCALE_KEY);
    }
    getTimeZone() {
        return this.getAttribute(CoreAttributes.TIME_ZONE_KEY);
    }
}

/**
 * Helper implementation for a provider of a single attribute whose value does not change.
 */
class StaticAttributesProvider {
    constructor(attributeKey, value) {
        this.attributeKey = attributeKey;
        this.value = value;
    }
    // @ts-ignore
    getAttribute(attributeKey) {
        return this.value;
    }
    getProvidedAttributes() {
        const attributes = new Set();
        attributes.add(this.attributeKey);
        return attributes;
    }
}

/**
 * The implementation of {@link LocalizationContextProvider} that delegates to one or more {@link AttributesProvider} instances
 * to compute individual attributes for the {@link LocalizationContext}.
 * <p>
 * This allows implementers to focus on providing individual attributes or sets of attributes via reusable building
 * blocks that can be combined to produce a single {@link LocalizationContextProvider}.
 */
class AttributesMapLocalizationContextProvider {
    /**
     * @param attributesProviders zero or more providers of individual {@link LocalizationContext} attributes
     * @throws Error if multiple attributesProviders provide the same attribute. You can avoid this
     *                                  by decorating individual attributesProviders to suppress attributes (by
     *                                  filtering the output of getProvidedAttributes).
     */
    constructor(...attributesProviders) {
        const providerMap = new Map();
        for (const provider of attributesProviders) {
            for (const attributeKey of provider.getProvidedAttributes()) {
                if (providerMap.has(attributeKey)) {
                    throw new Error(`Attribute ${attributeKey.asString()} is already provided by ${providerMap.get(attributeKey)}.`);
                }
                providerMap.set(attributeKey, provider);
            }
        }
        if (!providerMap.has(CoreAttributes.LOCALE_KEY)) {
            providerMap.set(CoreAttributes.LOCALE_KEY, AttributesMapLocalizationContextProvider.JVM_LOCALE_PROVIDER);
        }
        if (!providerMap.has(CoreAttributes.TIME_ZONE_KEY)) {
            providerMap.set(CoreAttributes.TIME_ZONE_KEY, AttributesMapLocalizationContextProvider.JVM_TIME_ZONE_PROVIDER);
        }
        if (!providerMap.has(CoreAttributes.I18N_DOMAIN_KEY)) {
            providerMap.set(CoreAttributes.I18N_DOMAIN_KEY, AttributesMapLocalizationContextProvider.DEFAULT_I18N_DOMAIN_PROVIDER);
        }
        this._attributeProviders = providerMap;
    }
    getLocalizationContext() {
        const attributes = new Map();
        for (const [key, value] of this._attributeProviders) {
            attributes.set(key, value.getAttribute(key));
        }
        return new AttributesMapLocalizationContext(attributes);
    }
}
AttributesMapLocalizationContextProvider.DATE_TIME_OPTIONS = new Intl.DateTimeFormat().resolvedOptions();
AttributesMapLocalizationContextProvider.JVM_LOCALE_PROVIDER = new StaticAttributesProvider(CoreAttributes.LOCALE_KEY, AttributesMapLocalizationContextProvider.DATE_TIME_OPTIONS.locale);
AttributesMapLocalizationContextProvider.JVM_TIME_ZONE_PROVIDER = new StaticAttributesProvider(CoreAttributes.TIME_ZONE_KEY, AttributesMapLocalizationContextProvider.DATE_TIME_OPTIONS.timeZone);
AttributesMapLocalizationContextProvider.DEFAULT_I18N_DOMAIN_PROVIDER = new StaticAttributesProvider(CoreAttributes.TIME_ZONE_KEY, DEFAULT_I18N_DOMAIN);

/**
 * A helper class to simplify constructing an {@link AttributesProvider} with one or more attribute(s).
 * <p>
 * With this builder, you specify logic to resolve multiple attributes one-by-one and get a resulting
 * {@link AttributesProvider} that provides all of the attributes (for use in
 * {@link AttributesMapLocalizationContextProvider}).
 */
class AttributesProviderBuilder {
    constructor() {
        this._attributeSuppliers = new Map();
    }
    /**
     * Method to add extra attribute supplier to the provider.
     *
     * @param attributeKey the identifier for an attribute being provided
     * @param supplier     the business logic that is able to produce an attribute value appropriate for the current
     *                     thread
     * @type <T>           the type associated with the attributeKey and the type of the supplied attribute value
     *                     the type signature of this method ensures that they must match in order to compile.
     * @return this builder
     */
    withAttributeSupplier(attributeKey, supplier) {
        this._attributeSuppliers.set(attributeKey, supplier);
        return this;
    }
    /**
     * @return an {@link AttributesProvider} that provides the attributes given in withAttributeSupplier (by invoking the
     * suppliers passed).
     */
    build() {
        const suppliers = this._attributeSuppliers;
        return new class {
            getAttribute(attributeKey) {
                const supplier = suppliers.get(attributeKey);
                if (!supplier) {
                    throw new Error(`Unsupported attribute ${attributeKey.asString()}.`);
                }
                return supplier();
            }
            getProvidedAttributes() {
                return suppliers.keys();
            }
        };
    }
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function getAugmentedNamespace(n) {
	if (n.__esModule) return n;
	var a = Object.defineProperty({}, '__esModule', {value: true});
	Object.keys(n).forEach(function (k) {
		var d = Object.getOwnPropertyDescriptor(n, k);
		Object.defineProperty(a, k, d.get ? d : {
			enumerable: true,
			get: function () {
				return n[k];
			}
		});
	});
	return a;
}

class QuickLRU {
	constructor(options = {}) {
		if (!(options.maxSize && options.maxSize > 0)) {
			throw new TypeError('`maxSize` must be a number greater than 0');
		}

		this.maxSize = options.maxSize;
		this.onEviction = options.onEviction;
		this.cache = new Map();
		this.oldCache = new Map();
		this._size = 0;
	}

	_set(key, value) {
		this.cache.set(key, value);
		this._size++;

		if (this._size >= this.maxSize) {
			this._size = 0;

			if (typeof this.onEviction === 'function') {
				for (const [key, value] of this.oldCache.entries()) {
					this.onEviction(key, value);
				}
			}

			this.oldCache = this.cache;
			this.cache = new Map();
		}
	}

	get(key) {
		if (this.cache.has(key)) {
			return this.cache.get(key);
		}

		if (this.oldCache.has(key)) {
			const value = this.oldCache.get(key);
			this.oldCache.delete(key);
			this._set(key, value);
			return value;
		}
	}

	set(key, value) {
		if (this.cache.has(key)) {
			this.cache.set(key, value);
		} else {
			this._set(key, value);
		}

		return this;
	}

	has(key) {
		return this.cache.has(key) || this.oldCache.has(key);
	}

	peek(key) {
		if (this.cache.has(key)) {
			return this.cache.get(key);
		}

		if (this.oldCache.has(key)) {
			return this.oldCache.get(key);
		}
	}

	delete(key) {
		const deleted = this.cache.delete(key);
		if (deleted) {
			this._size--;
		}

		return this.oldCache.delete(key) || deleted;
	}

	clear() {
		this.cache.clear();
		this.oldCache.clear();
		this._size = 0;
	}

	* keys() {
		for (const [key] of this) {
			yield key;
		}
	}

	* values() {
		for (const [, value] of this) {
			yield value;
		}
	}

	* [Symbol.iterator]() {
		for (const item of this.cache) {
			yield item;
		}

		for (const item of this.oldCache) {
			const [key] = item;
			if (!this.cache.has(key)) {
				yield item;
			}
		}
	}

	get size() {
		let oldCacheSize = 0;
		for (const key of this.oldCache.keys()) {
			if (!this.cache.has(key)) {
				oldCacheSize++;
			}
		}

		return Math.min(this._size + oldCacheSize, this.maxSize);
	}
}

var quickLru = QuickLRU;

var QuickLRU$1 = quickLru;

/**
 * Default serialize key function.
 * @param key
 * @returns serialized key
 */
const defaultSerializeKeyFn = (key) => String(key);
/**
 * [Loading cache](https://stackoverflow.com/questions/43993731/what-is-a-loadingcache) backed by QuickLRU to
 * evict the resources. CacheKey function, can be provided for supporting injection of the Object types with
 * value equality.
 * const obj1 = {key: 'value'} is same as const obj2 = {key: 'value'} in cache (different references).
 *
 * Reference equality for the objects would not work in this case and we do not use it.
 */
class LoadingCache {
    /**
     * Construct loading cache.
     *
     * @param loader Loader function for getting the cache values.
     * @param cacheOptions QuickLRU plus cache key options.
     */
    constructor(loader, cacheOptions) {
        this.loader = loader;
        this.storage = new QuickLRU$1(cacheOptions);
        this.serializeKeyFn = cacheOptions.keyFn !== undefined ? cacheOptions.keyFn : defaultSerializeKeyFn;
    }
    /**
     * Retrieve the existing value from the cache or use loader to compute and cache it.
     * @param key Key to lookup.
     * @returns value.
     */
    get(key) {
        const cacheKey = this.serializeKeyFn(key);
        const cached = this.storage.get(cacheKey);
        if (cached) {
            return cached;
        }
        const content = this.loader(key);
        if (content === undefined) {
            return undefined;
        }
        this.storage.set(cacheKey, content);
        return content;
    }
    /**
     * Size of loading cache.
     * @returns size
     */
    size() {
        return this.storage.size;
    }
    /**
     * Check if key exists.
     * @param key
     * @returns true if has
     */
    has(key) {
        return this.storage.has(this.serializeKeyFn(key));
    }
}

/**
 * Message bundle cache key based upon Panther resource bundle and localization context.
 */
class MessageBundleCacheKey {
    constructor(_bundle, _context) {
        this._bundle = _bundle;
        this._context = _context;
    }
    /**
     * Gets bundle from MessageBundleCacheKey.
     */
    get bundle() {
        return this._bundle;
    }
    /**
     * Gets context from MessageBundleCacheKey.
     */
    get context() {
        return this._context;
    }
    /**
     * Creating cache key from Panther resource bundle and context.
     * @param messageBundleKey
     * @returns cache key
     */
    static generateCacheKey(messageBundleKey) {
        let bundleKey = messageBundleKey.bundle;
        const attributes = messageBundleKey.context.asAttributesMap();
        for (const key of MessageBundleCacheKey.LOCALIZATION_CONTEXT_ATTRIBUTES_FOR_CACHE_KEY) {
            const value = attributes.get(key);
            if (value) {
                bundleKey += `#${key}#${value}`;
            }
        }
        return bundleKey;
    }
}
/**
 * LocalizationContext attributes for cache key construction
 */
MessageBundleCacheKey.LOCALIZATION_CONTEXT_ATTRIBUTES_FOR_CACHE_KEY = [
    LOCALE_ATTRIBUTE,
    DEFAULT_LOCALE_ATTRIBUTE,
    I18N_DOMAIN_ATTRIBUTE,
];

const CONSTANTS = {
    rootLocale: 'und',
    noManifestError: 'Manifest can not be null!',
    invalidBundleError: 'Invalid bundle is passed!',
};

/**
 * Resource fallback item representing the resource to load. Bundle, variants and locale map to the
 * the resource file in ARB.
 */
class FallbackItem {
    /**
     * Creates an instance of fallback item.
     * @param bundle Panther resource bundle.
     * @param locale Locale of the resource.
     * @param [variants] Variants like i18nDomain.
     */
    constructor(bundle, locale, variants) {
        this._bundle = bundle;
        this._variants = variants;
        this._locale = locale;
    }
    /**
     * Gets Panther resource bundle
     */
    get bundle() {
        return this._bundle;
    }
    /**
     * Gets variants
     */
    get variants() {
        return this._variants;
    }
    /**
     * Gets locale
     */
    get locale() {
        return this._locale;
    }
    /**
     * Cache key for the fallback item to be used while inserting into the loading cache.
     * @param item Fallback item.
     * @returns cache key.
     */
    static cacheKey(item) {
        let result = item.bundle;
        if (item.variants) {
            item.variants.forEach((value, key) => {
                result += `#var-${key}#${value}`;
            });
        }
        result += `#${item.locale}`;
        return result;
    }
}
/**
 * Locale and variant fallback generator for provided localization context using iterator.
 */
class ResourceFallbackHandler {
    /**
     * Sparse fallback chains are created using iterators in the following format:
     *
     * @param bundle : Panther resource bundle.
     * @param variantsFallbackChain : Fallback chain coming from manifest.
     * @param localizationContext : Localization context.
     * @returns fallback chains : Sparse fallback chains.
     */
    getFallbackChains(bundle, variantsFallbackChain, localizationContext) {
        const localeChain = this.createLocaleFallbackChain(localizationContext.getLocale(), localizationContext.getDefaultLocale());
        const variantChainIterator = this.getVariantFallbackChain(variantsFallbackChain, localizationContext);
        return {
            *[Symbol.iterator]() {
                // Using generator for lazy looping.
                for (const currentVariantChain of variantChainIterator) {
                    for (const variants of currentVariantChain) {
                        for (const locale of localeChain) {
                            yield new FallbackItem(bundle, locale, variants);
                        }
                    }
                }
                // plain locale chain at the end.
                for (const locale of localeChain) {
                    yield new FallbackItem(bundle, locale, undefined);
                }
            },
        };
    }
    /**
     * Form variant fallback chains.
     * @param fallbackDefinitions  : Fallback chain coming from manifest.
     * @param localizationContext : Localization context.
     * @returns variant fallback chain
     */
    getVariantFallbackChain(fallbackDefinitions, localizationContext) {
        const contextAttributes = localizationContext.asAttributesMap();
        return {
            *[Symbol.iterator]() {
                for (let index = 0; index < fallbackDefinitions.length; index++) {
                    let nextChain = [];
                    if (index < fallbackDefinitions.length - 1) {
                        nextChain = fallbackDefinitions[index + 1];
                    }
                    const currentChain = fallbackDefinitions[index];
                    // To avoid creating duplicate chains if the prefix of the next fallback chain matches.
                    let nextChainPrefix = true;
                    const variants = [];
                    const currentVariant = new Map();
                    for (let currentChainIndex = 0; currentChainIndex < currentChain.length; currentChainIndex++) {
                        if (!contextAttributes) {
                            continue;
                        }
                        const variant = currentChain[currentChainIndex];
                        const variantValue = contextAttributes.get(variant);
                        if (!variantValue) {
                            continue;
                        }
                        currentVariant.set(variant, variantValue);
                        if (nextChainPrefix &&
                            currentChainIndex < nextChain.length &&
                            nextChain[currentChainIndex] === currentChain[currentChainIndex]) {
                            continue;
                        }
                        nextChainPrefix = false;
                        variants.push(new Map(currentVariant));
                    }
                    yield variants.reverse();
                }
            },
        };
    }
    /**
     * Creates locale fallback chain.
     * @param locale
     * @param [defaultLocale]
     * @returns locale fallback chain
     */
    createLocaleFallbackChain(locale, defaultLocale) {
        // @ts-ignore
        const canonicalLocale = Intl.getCanonicalLocales(locale)[0];
        const canonicalDefaultLocale = 
        // @ts-ignore
        defaultLocale !== undefined ? Intl.getCanonicalLocales(defaultLocale)[0] : defaultLocale;
        const localeChain = this.getFallbackChain(canonicalLocale);
        // add default locale.
        if (canonicalDefaultLocale && !localeChain.includes(canonicalDefaultLocale)) {
            const defaultLocaleChain = this.getFallbackChain(canonicalDefaultLocale);
            localeChain.push(...defaultLocaleChain);
        }
        localeChain.push(CONSTANTS.rootLocale);
        return localeChain;
    }
    /**
     * Gets fallback chain for provided locale.
     * @param locale
     * @returns fallback chain
     */
    getFallbackChain(locale) {
        const localeChain = [];
        // @ts-ignore
        const intlLocale = new Intl.Locale(locale);
        let fallbackLocale = intlLocale.baseName;
        if (fallbackLocale !== CONSTANTS.rootLocale) {
            localeChain.push(fallbackLocale);
            let toTrim = fallbackLocale.lastIndexOf(ResourceFallbackHandler.LOCALE_TAG_SEPARATOR);
            while (toTrim > 0) {
                fallbackLocale = fallbackLocale.substr(0, toTrim);
                localeChain.push(fallbackLocale);
                toTrim = fallbackLocale.lastIndexOf(ResourceFallbackHandler.LOCALE_TAG_SEPARATOR);
            }
        }
        return localeChain;
    }
}
ResourceFallbackHandler.LOCALE_TAG_SEPARATOR = '-';

var ErrorKind;
(function (ErrorKind) {
    /** Argument is unclosed (e.g. `{0`) */
    ErrorKind[ErrorKind["EXPECT_ARGUMENT_CLOSING_BRACE"] = 1] = "EXPECT_ARGUMENT_CLOSING_BRACE";
    /** Argument is empty (e.g. `{}`). */
    ErrorKind[ErrorKind["EMPTY_ARGUMENT"] = 2] = "EMPTY_ARGUMENT";
    /** Argument is malformed (e.g. `{foo!}``) */
    ErrorKind[ErrorKind["MALFORMED_ARGUMENT"] = 3] = "MALFORMED_ARGUMENT";
    /** Expect an argument type (e.g. `{foo,}`) */
    ErrorKind[ErrorKind["EXPECT_ARGUMENT_TYPE"] = 4] = "EXPECT_ARGUMENT_TYPE";
    /** Unsupported argument type (e.g. `{foo,foo}`) */
    ErrorKind[ErrorKind["INVALID_ARGUMENT_TYPE"] = 5] = "INVALID_ARGUMENT_TYPE";
    /** Expect an argument style (e.g. `{foo, number, }`) */
    ErrorKind[ErrorKind["EXPECT_ARGUMENT_STYLE"] = 6] = "EXPECT_ARGUMENT_STYLE";
    /** The number skeleton is invalid. */
    ErrorKind[ErrorKind["INVALID_NUMBER_SKELETON"] = 7] = "INVALID_NUMBER_SKELETON";
    /** The date time skeleton is invalid. */
    ErrorKind[ErrorKind["INVALID_DATE_TIME_SKELETON"] = 8] = "INVALID_DATE_TIME_SKELETON";
    /** Exepct a number skeleton following the `::` (e.g. `{foo, number, ::}`) */
    ErrorKind[ErrorKind["EXPECT_NUMBER_SKELETON"] = 9] = "EXPECT_NUMBER_SKELETON";
    /** Exepct a date time skeleton following the `::` (e.g. `{foo, date, ::}`) */
    ErrorKind[ErrorKind["EXPECT_DATE_TIME_SKELETON"] = 10] = "EXPECT_DATE_TIME_SKELETON";
    /** Unmatched apostrophes in the argument style (e.g. `{foo, number, 'test`) */
    ErrorKind[ErrorKind["UNCLOSED_QUOTE_IN_ARGUMENT_STYLE"] = 11] = "UNCLOSED_QUOTE_IN_ARGUMENT_STYLE";
    /** Missing select argument options (e.g. `{foo, select}`) */
    ErrorKind[ErrorKind["EXPECT_SELECT_ARGUMENT_OPTIONS"] = 12] = "EXPECT_SELECT_ARGUMENT_OPTIONS";
    /** Expecting an offset value in `plural` or `selectordinal` argument (e.g `{foo, plural, offset}`) */
    ErrorKind[ErrorKind["EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE"] = 13] = "EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE";
    /** Offset value in `plural` or `selectordinal` is invalid (e.g. `{foo, plural, offset: x}`) */
    ErrorKind[ErrorKind["INVALID_PLURAL_ARGUMENT_OFFSET_VALUE"] = 14] = "INVALID_PLURAL_ARGUMENT_OFFSET_VALUE";
    /** Expecting a selector in `select` argument (e.g `{foo, select}`) */
    ErrorKind[ErrorKind["EXPECT_SELECT_ARGUMENT_SELECTOR"] = 15] = "EXPECT_SELECT_ARGUMENT_SELECTOR";
    /** Expecting a selector in `plural` or `selectordinal` argument (e.g `{foo, plural}`) */
    ErrorKind[ErrorKind["EXPECT_PLURAL_ARGUMENT_SELECTOR"] = 16] = "EXPECT_PLURAL_ARGUMENT_SELECTOR";
    /** Expecting a message fragment after the `select` selector (e.g. `{foo, select, apple}`) */
    ErrorKind[ErrorKind["EXPECT_SELECT_ARGUMENT_SELECTOR_FRAGMENT"] = 17] = "EXPECT_SELECT_ARGUMENT_SELECTOR_FRAGMENT";
    /**
     * Expecting a message fragment after the `plural` or `selectordinal` selector
     * (e.g. `{foo, plural, one}`)
     */
    ErrorKind[ErrorKind["EXPECT_PLURAL_ARGUMENT_SELECTOR_FRAGMENT"] = 18] = "EXPECT_PLURAL_ARGUMENT_SELECTOR_FRAGMENT";
    /** Selector in `plural` or `selectordinal` is malformed (e.g. `{foo, plural, =x {#}}`) */
    ErrorKind[ErrorKind["INVALID_PLURAL_ARGUMENT_SELECTOR"] = 19] = "INVALID_PLURAL_ARGUMENT_SELECTOR";
    /**
     * Duplicate selectors in `plural` or `selectordinal` argument.
     * (e.g. {foo, plural, one {#} one {#}})
     */
    ErrorKind[ErrorKind["DUPLICATE_PLURAL_ARGUMENT_SELECTOR"] = 20] = "DUPLICATE_PLURAL_ARGUMENT_SELECTOR";
    /** Duplicate selectors in `select` argument.
     * (e.g. {foo, select, apple {apple} apple {apple}})
     */
    ErrorKind[ErrorKind["DUPLICATE_SELECT_ARGUMENT_SELECTOR"] = 21] = "DUPLICATE_SELECT_ARGUMENT_SELECTOR";
    /** Plural or select argument option must have `other` clause. */
    ErrorKind[ErrorKind["MISSING_OTHER_CLAUSE"] = 22] = "MISSING_OTHER_CLAUSE";
    /** The tag is malformed. (e.g. `<bold!>foo</bold!>) */
    ErrorKind[ErrorKind["INVALID_TAG"] = 23] = "INVALID_TAG";
    /** The tag name is invalid. (e.g. `<123>foo</123>`) */
    ErrorKind[ErrorKind["INVALID_TAG_NAME"] = 25] = "INVALID_TAG_NAME";
    /** The closing tag does not match the opening tag. (e.g. `<bold>foo</italic>`) */
    ErrorKind[ErrorKind["UNMATCHED_CLOSING_TAG"] = 26] = "UNMATCHED_CLOSING_TAG";
    /** The opening tag has unmatched closing tag. (e.g. `<bold>foo`) */
    ErrorKind[ErrorKind["UNCLOSED_TAG"] = 27] = "UNCLOSED_TAG";
})(ErrorKind || (ErrorKind = {}));

var TYPE;
(function (TYPE) {
    /**
     * Raw text
     */
    TYPE[TYPE["literal"] = 0] = "literal";
    /**
     * Variable w/o any format, e.g `var` in `this is a {var}`
     */
    TYPE[TYPE["argument"] = 1] = "argument";
    /**
     * Variable w/ number format
     */
    TYPE[TYPE["number"] = 2] = "number";
    /**
     * Variable w/ date format
     */
    TYPE[TYPE["date"] = 3] = "date";
    /**
     * Variable w/ time format
     */
    TYPE[TYPE["time"] = 4] = "time";
    /**
     * Variable w/ select format
     */
    TYPE[TYPE["select"] = 5] = "select";
    /**
     * Variable w/ plural format
     */
    TYPE[TYPE["plural"] = 6] = "plural";
    /**
     * Only possible within plural argument.
     * This is the `#` symbol that will be substituted with the count.
     */
    TYPE[TYPE["pound"] = 7] = "pound";
    /**
     * XML-like tag
     */
    TYPE[TYPE["tag"] = 8] = "tag";
    // AMAZON_CHANGE_START
    /**
     * Variable w/ price format
     */
    TYPE[TYPE["price"] = 9] = "price";
    // AMAZON_CHANGE_END
})(TYPE || (TYPE = {}));
var SKELETON_TYPE;
(function (SKELETON_TYPE) {
    SKELETON_TYPE[SKELETON_TYPE["number"] = 0] = "number";
    SKELETON_TYPE[SKELETON_TYPE["dateTime"] = 1] = "dateTime";
})(SKELETON_TYPE || (SKELETON_TYPE = {}));
/**
 * Type Guards
 */
function isLiteralElement(el) {
    return el.type === TYPE.literal;
}
function isArgumentElement(el) {
    return el.type === TYPE.argument;
}
function isNumberElement(el) {
    return el.type === TYPE.number;
}
function isDateElement(el) {
    return el.type === TYPE.date;
}
function isTimeElement(el) {
    return el.type === TYPE.time;
}
function isSelectElement(el) {
    return el.type === TYPE.select;
}
function isPluralElement(el) {
    return el.type === TYPE.plural;
}
function isPoundElement(el) {
    return el.type === TYPE.pound;
}
function isTagElement(el) {
    return el.type === TYPE.tag;
}
function isNumberSkeleton(el) {
    return !!(el && typeof el === 'object' && el.type === SKELETON_TYPE.number);
}
function isDateTimeSkeleton(el) {
    return !!(el && typeof el === 'object' && el.type === SKELETON_TYPE.dateTime);
}
// AMAZON_CHANGE_START
function isPriceElement(el) {
    return el.type === TYPE.price;
}

// @generated from regex-gen.ts
const SPACE_SEPARATOR_REGEX = /[ \xA0\u1680\u2000-\u200A\u202F\u205F\u3000]/;

/**
 * https://unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * Credit: https://github.com/caridy/intl-datetimeformat-pattern/blob/master/index.js
 * with some tweaks
 */
var DATE_TIME_REGEX = /(?:[Eec]{1,6}|G{1,5}|[Qq]{1,5}|(?:[yYur]+|U{1,5})|[ML]{1,5}|d{1,2}|D{1,3}|F{1}|[abB]{1,5}|[hkHK]{1,2}|w{1,2}|W{1}|m{1,2}|s{1,2}|[zZOvVxX]{1,4})(?=([^']*'[^']*')*[^']*$)/g;
/**
 * Parse Date time skeleton into Intl.DateTimeFormatOptions
 * Ref: https://unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * @public
 * @param skeleton skeleton string
 */
function parseDateTimeSkeleton(skeleton) {
    var result = {};
    skeleton.replace(DATE_TIME_REGEX, function (match) {
        var len = match.length;
        switch (match[0]) {
            // Era
            case 'G':
                result.era = len === 4 ? 'long' : len === 5 ? 'narrow' : 'short';
                break;
            // Year
            case 'y':
                result.year = len === 2 ? '2-digit' : 'numeric';
                break;
            case 'Y':
            case 'u':
            case 'U':
            case 'r':
                throw new RangeError('`Y/u/U/r` (year) patterns are not supported, use `y` instead');
            // Quarter
            case 'q':
            case 'Q':
                throw new RangeError('`q/Q` (quarter) patterns are not supported');
            // Month
            case 'M':
            case 'L':
                result.month = ['numeric', '2-digit', 'short', 'long', 'narrow'][len - 1];
                break;
            // Week
            case 'w':
            case 'W':
                throw new RangeError('`w/W` (week) patterns are not supported');
            case 'd':
                result.day = ['numeric', '2-digit'][len - 1];
                break;
            case 'D':
            case 'F':
            case 'g':
                throw new RangeError('`D/F/g` (day) patterns are not supported, use `d` instead');
            // Weekday
            case 'E':
                result.weekday = len === 4 ? 'short' : len === 5 ? 'narrow' : 'short';
                break;
            case 'e':
                if (len < 4) {
                    throw new RangeError('`e..eee` (weekday) patterns are not supported');
                }
                result.weekday = ['short', 'long', 'narrow', 'short'][len - 4];
                break;
            case 'c':
                if (len < 4) {
                    throw new RangeError('`c..ccc` (weekday) patterns are not supported');
                }
                result.weekday = ['short', 'long', 'narrow', 'short'][len - 4];
                break;
            // Period
            case 'a': // AM, PM
                result.hour12 = true;
                break;
            case 'b': // am, pm, noon, midnight
            case 'B': // flexible day periods
                throw new RangeError('`b/B` (period) patterns are not supported, use `a` instead');
            // Hour
            case 'h':
                result.hourCycle = 'h12';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'H':
                result.hourCycle = 'h23';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'K':
                result.hourCycle = 'h11';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'k':
                result.hourCycle = 'h24';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'j':
            case 'J':
            case 'C':
                throw new RangeError('`j/J/C` (hour) patterns are not supported, use `h/H/K/k` instead');
            // Minute
            case 'm':
                result.minute = ['numeric', '2-digit'][len - 1];
                break;
            // Second
            case 's':
                result.second = ['numeric', '2-digit'][len - 1];
                break;
            case 'S':
            case 'A':
                throw new RangeError('`S/A` (second) patterns are not supported, use `s` instead');
            // Zone
            case 'z': // 1..3, 4: specific non-location format
                result.timeZoneName = len < 4 ? 'short' : 'long';
                break;
            case 'Z': // 1..3, 4, 5: The ISO8601 varios formats
            case 'O': // 1, 4: miliseconds in day short, long
            case 'v': // 1, 4: generic non-location format
            case 'V': // 1, 2, 3, 4: time zone ID or city
            case 'X': // 1, 2, 3, 4: The ISO8601 varios formats
            case 'x': // 1, 2, 3, 4: The ISO8601 varios formats
                throw new RangeError('`Z/O/v/V/X/x` (timeZone) patterns are not supported, use `z` instead');
        }
        return '';
    });
    return result;
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

// @generated from regex-gen.ts
var WHITE_SPACE_REGEX = /[\t-\r \x85\u200E\u200F\u2028\u2029]/i;

function parseNumberSkeletonFromString(skeleton) {
    if (skeleton.length === 0) {
        throw new Error('Number skeleton cannot be empty');
    }
    // Parse the skeleton
    var stringTokens = skeleton
        .split(WHITE_SPACE_REGEX)
        .filter(function (x) { return x.length > 0; });
    var tokens = [];
    for (var _i = 0, stringTokens_1 = stringTokens; _i < stringTokens_1.length; _i++) {
        var stringToken = stringTokens_1[_i];
        var stemAndOptions = stringToken.split('/');
        if (stemAndOptions.length === 0) {
            throw new Error('Invalid number skeleton');
        }
        var stem = stemAndOptions[0], options = stemAndOptions.slice(1);
        for (var _a = 0, options_1 = options; _a < options_1.length; _a++) {
            var option = options_1[_a];
            if (option.length === 0) {
                throw new Error('Invalid number skeleton');
            }
        }
        tokens.push({ stem: stem, options: options });
    }
    return tokens;
}
function icuUnitToEcma(unit) {
    return unit.replace(/^(.*?)-/, '');
}
var FRACTION_PRECISION_REGEX = /^\.(?:(0+)(\*)?|(#+)|(0+)(#+))$/g;
var SIGNIFICANT_PRECISION_REGEX = /^(@+)?(\+|#+)?[rs]?$/g;
var INTEGER_WIDTH_REGEX = /(\*)(0+)|(#+)(0+)|(0+)/g;
var CONCISE_INTEGER_WIDTH_REGEX = /^(0+)$/;
function parseSignificantPrecision(str) {
    var result = {};
    if (str[str.length - 1] === 'r') {
        result.roundingPriority = 'morePrecision';
    }
    else if (str[str.length - 1] === 's') {
        result.roundingPriority = 'lessPrecision';
    }
    str.replace(SIGNIFICANT_PRECISION_REGEX, function (_, g1, g2) {
        // @@@ case
        if (typeof g2 !== 'string') {
            result.minimumSignificantDigits = g1.length;
            result.maximumSignificantDigits = g1.length;
        }
        // @@@+ case
        else if (g2 === '+') {
            result.minimumSignificantDigits = g1.length;
        }
        // .### case
        else if (g1[0] === '#') {
            result.maximumSignificantDigits = g1.length;
        }
        // .@@## or .@@@ case
        else {
            result.minimumSignificantDigits = g1.length;
            result.maximumSignificantDigits =
                g1.length + (typeof g2 === 'string' ? g2.length : 0);
        }
        return '';
    });
    return result;
}
function parseSign(str) {
    switch (str) {
        case 'sign-auto':
            return {
                signDisplay: 'auto',
            };
        case 'sign-accounting':
        case '()':
            return {
                currencySign: 'accounting',
            };
        case 'sign-always':
        case '+!':
            return {
                signDisplay: 'always',
            };
        case 'sign-accounting-always':
        case '()!':
            return {
                signDisplay: 'always',
                currencySign: 'accounting',
            };
        case 'sign-except-zero':
        case '+?':
            return {
                signDisplay: 'exceptZero',
            };
        case 'sign-accounting-except-zero':
        case '()?':
            return {
                signDisplay: 'exceptZero',
                currencySign: 'accounting',
            };
        case 'sign-never':
        case '+_':
            return {
                signDisplay: 'never',
            };
    }
}
function parseConciseScientificAndEngineeringStem(stem) {
    // Engineering
    var result;
    if (stem[0] === 'E' && stem[1] === 'E') {
        result = {
            notation: 'engineering',
        };
        stem = stem.slice(2);
    }
    else if (stem[0] === 'E') {
        result = {
            notation: 'scientific',
        };
        stem = stem.slice(1);
    }
    if (result) {
        var signDisplay = stem.slice(0, 2);
        if (signDisplay === '+!') {
            result.signDisplay = 'always';
            stem = stem.slice(2);
        }
        else if (signDisplay === '+?') {
            result.signDisplay = 'exceptZero';
            stem = stem.slice(2);
        }
        if (!CONCISE_INTEGER_WIDTH_REGEX.test(stem)) {
            throw new Error('Malformed concise eng/scientific notation');
        }
        result.minimumIntegerDigits = stem.length;
    }
    return result;
}
function parseNotationOptions(opt) {
    var result = {};
    var signOpts = parseSign(opt);
    if (signOpts) {
        return signOpts;
    }
    return result;
}
/**
 * https://github.com/unicode-org/icu/blob/master/docs/userguide/format_parse/numbers/skeletons.md#skeleton-stems-and-options
 */
function parseNumberSkeleton(tokens) {
    var result = {};
    for (var _i = 0, tokens_1 = tokens; _i < tokens_1.length; _i++) {
        var token = tokens_1[_i];
        switch (token.stem) {
            case 'percent':
            case '%':
                result.style = 'percent';
                continue;
            case '%x100':
                result.style = 'percent';
                result.scale = 100;
                continue;
            case 'currency':
                result.style = 'currency';
                result.currency = token.options[0];
                continue;
            case 'group-off':
            case ',_':
                result.useGrouping = false;
                continue;
            case 'precision-integer':
            case '.':
                result.maximumFractionDigits = 0;
                continue;
            case 'measure-unit':
            case 'unit':
                result.style = 'unit';
                result.unit = icuUnitToEcma(token.options[0]);
                continue;
            case 'compact-short':
            case 'K':
                result.notation = 'compact';
                result.compactDisplay = 'short';
                continue;
            case 'compact-long':
            case 'KK':
                result.notation = 'compact';
                result.compactDisplay = 'long';
                continue;
            case 'scientific':
                result = __assign(__assign(__assign({}, result), { notation: 'scientific' }), token.options.reduce(function (all, opt) { return (__assign(__assign({}, all), parseNotationOptions(opt))); }, {}));
                continue;
            case 'engineering':
                result = __assign(__assign(__assign({}, result), { notation: 'engineering' }), token.options.reduce(function (all, opt) { return (__assign(__assign({}, all), parseNotationOptions(opt))); }, {}));
                continue;
            case 'notation-simple':
                result.notation = 'standard';
                continue;
            // https://github.com/unicode-org/icu/blob/master/icu4c/source/i18n/unicode/unumberformatter.h
            case 'unit-width-narrow':
                result.currencyDisplay = 'narrowSymbol';
                result.unitDisplay = 'narrow';
                continue;
            case 'unit-width-short':
                result.currencyDisplay = 'code';
                result.unitDisplay = 'short';
                continue;
            case 'unit-width-full-name':
                result.currencyDisplay = 'name';
                result.unitDisplay = 'long';
                continue;
            case 'unit-width-iso-code':
                result.currencyDisplay = 'symbol';
                continue;
            case 'scale':
                result.scale = parseFloat(token.options[0]);
                continue;
            // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#integer-width
            case 'integer-width':
                if (token.options.length > 1) {
                    throw new RangeError('integer-width stems only accept a single optional option');
                }
                token.options[0].replace(INTEGER_WIDTH_REGEX, function (_, g1, g2, g3, g4, g5) {
                    if (g1) {
                        result.minimumIntegerDigits = g2.length;
                    }
                    else if (g3 && g4) {
                        throw new Error('We currently do not support maximum integer digits');
                    }
                    else if (g5) {
                        throw new Error('We currently do not support exact integer digits');
                    }
                    return '';
                });
                continue;
        }
        // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#integer-width
        if (CONCISE_INTEGER_WIDTH_REGEX.test(token.stem)) {
            result.minimumIntegerDigits = token.stem.length;
            continue;
        }
        if (FRACTION_PRECISION_REGEX.test(token.stem)) {
            // Precision
            // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#fraction-precision
            // precision-integer case
            if (token.options.length > 1) {
                throw new RangeError('Fraction-precision stems only accept a single optional option');
            }
            token.stem.replace(FRACTION_PRECISION_REGEX, function (_, g1, g2, g3, g4, g5) {
                // .000* case (before ICU67 it was .000+)
                if (g2 === '*') {
                    result.minimumFractionDigits = g1.length;
                }
                // .### case
                else if (g3 && g3[0] === '#') {
                    result.maximumFractionDigits = g3.length;
                }
                // .00## case
                else if (g4 && g5) {
                    result.minimumFractionDigits = g4.length;
                    result.maximumFractionDigits = g4.length + g5.length;
                }
                else {
                    result.minimumFractionDigits = g1.length;
                    result.maximumFractionDigits = g1.length;
                }
                return '';
            });
            var opt = token.options[0];
            // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#trailing-zero-display
            if (opt === 'w') {
                result = __assign(__assign({}, result), { trailingZeroDisplay: 'stripIfInteger' });
            }
            else if (opt) {
                result = __assign(__assign({}, result), parseSignificantPrecision(opt));
            }
            continue;
        }
        // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#significant-digits-precision
        if (SIGNIFICANT_PRECISION_REGEX.test(token.stem)) {
            result = __assign(__assign({}, result), parseSignificantPrecision(token.stem));
            continue;
        }
        var signOpts = parseSign(token.stem);
        if (signOpts) {
            result = __assign(__assign({}, result), signOpts);
        }
        var conciseScientificAndEngineeringOpts = parseConciseScientificAndEngineeringStem(token.stem);
        if (conciseScientificAndEngineeringOpts) {
            result = __assign(__assign({}, result), conciseScientificAndEngineeringOpts);
        }
    }
    return result;
}

// @generated from time-data-gen.ts
// prettier-ignore  
const timeData = {
    "AX": [
        "H"
    ],
    "BQ": [
        "H"
    ],
    "CP": [
        "H"
    ],
    "CZ": [
        "H"
    ],
    "DK": [
        "H"
    ],
    "FI": [
        "H"
    ],
    "ID": [
        "H"
    ],
    "IS": [
        "H"
    ],
    "ML": [
        "H"
    ],
    "NE": [
        "H"
    ],
    "RU": [
        "H"
    ],
    "SE": [
        "H"
    ],
    "SJ": [
        "H"
    ],
    "SK": [
        "H"
    ],
    "AS": [
        "h",
        "H"
    ],
    "BT": [
        "h",
        "H"
    ],
    "DJ": [
        "h",
        "H"
    ],
    "ER": [
        "h",
        "H"
    ],
    "GH": [
        "h",
        "H"
    ],
    "IN": [
        "h",
        "H"
    ],
    "LS": [
        "h",
        "H"
    ],
    "PG": [
        "h",
        "H"
    ],
    "PW": [
        "h",
        "H"
    ],
    "SO": [
        "h",
        "H"
    ],
    "TO": [
        "h",
        "H"
    ],
    "VU": [
        "h",
        "H"
    ],
    "WS": [
        "h",
        "H"
    ],
    "001": [
        "H",
        "h"
    ],
    "AL": [
        "h",
        "H",
        "hB"
    ],
    "TD": [
        "h",
        "H",
        "hB"
    ],
    "ca-ES": [
        "H",
        "h",
        "hB"
    ],
    "CF": [
        "H",
        "h",
        "hB"
    ],
    "CM": [
        "H",
        "h",
        "hB"
    ],
    "fr-CA": [
        "H",
        "h",
        "hB"
    ],
    "gl-ES": [
        "H",
        "h",
        "hB"
    ],
    "it-CH": [
        "H",
        "h",
        "hB"
    ],
    "it-IT": [
        "H",
        "h",
        "hB"
    ],
    "LU": [
        "H",
        "h",
        "hB"
    ],
    "NP": [
        "H",
        "h",
        "hB"
    ],
    "PF": [
        "H",
        "h",
        "hB"
    ],
    "SC": [
        "H",
        "h",
        "hB"
    ],
    "SM": [
        "H",
        "h",
        "hB"
    ],
    "SN": [
        "H",
        "h",
        "hB"
    ],
    "TF": [
        "H",
        "h",
        "hB"
    ],
    "VA": [
        "H",
        "h",
        "hB"
    ],
    "CY": [
        "h",
        "H",
        "hb",
        "hB"
    ],
    "GR": [
        "h",
        "H",
        "hb",
        "hB"
    ],
    "CO": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "DO": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "KP": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "KR": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "NA": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "PA": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "PR": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "VE": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "AC": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "AI": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "BW": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "BZ": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "CC": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "CK": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "CX": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "DG": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "FK": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "GB": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "GG": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "GI": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "IE": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "IM": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "IO": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "JE": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "LT": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "MK": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "MN": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "MS": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "NF": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "NG": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "NR": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "NU": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "PN": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "SH": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "SX": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "TA": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "ZA": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "af-ZA": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "AR": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "CL": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "CR": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "CU": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "EA": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-BO": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-BR": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-EC": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-ES": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-GQ": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-PE": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "GT": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "HN": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "IC": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "KG": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "KM": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "LK": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "MA": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "MX": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "NI": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "PY": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "SV": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "UY": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "JP": [
        "H",
        "h",
        "K"
    ],
    "AD": [
        "H",
        "hB"
    ],
    "AM": [
        "H",
        "hB"
    ],
    "AO": [
        "H",
        "hB"
    ],
    "AT": [
        "H",
        "hB"
    ],
    "AW": [
        "H",
        "hB"
    ],
    "BE": [
        "H",
        "hB"
    ],
    "BF": [
        "H",
        "hB"
    ],
    "BJ": [
        "H",
        "hB"
    ],
    "BL": [
        "H",
        "hB"
    ],
    "BR": [
        "H",
        "hB"
    ],
    "CG": [
        "H",
        "hB"
    ],
    "CI": [
        "H",
        "hB"
    ],
    "CV": [
        "H",
        "hB"
    ],
    "DE": [
        "H",
        "hB"
    ],
    "EE": [
        "H",
        "hB"
    ],
    "FR": [
        "H",
        "hB"
    ],
    "GA": [
        "H",
        "hB"
    ],
    "GF": [
        "H",
        "hB"
    ],
    "GN": [
        "H",
        "hB"
    ],
    "GP": [
        "H",
        "hB"
    ],
    "GW": [
        "H",
        "hB"
    ],
    "HR": [
        "H",
        "hB"
    ],
    "IL": [
        "H",
        "hB"
    ],
    "IT": [
        "H",
        "hB"
    ],
    "KZ": [
        "H",
        "hB"
    ],
    "MC": [
        "H",
        "hB"
    ],
    "MD": [
        "H",
        "hB"
    ],
    "MF": [
        "H",
        "hB"
    ],
    "MQ": [
        "H",
        "hB"
    ],
    "MZ": [
        "H",
        "hB"
    ],
    "NC": [
        "H",
        "hB"
    ],
    "NL": [
        "H",
        "hB"
    ],
    "PM": [
        "H",
        "hB"
    ],
    "PT": [
        "H",
        "hB"
    ],
    "RE": [
        "H",
        "hB"
    ],
    "RO": [
        "H",
        "hB"
    ],
    "SI": [
        "H",
        "hB"
    ],
    "SR": [
        "H",
        "hB"
    ],
    "ST": [
        "H",
        "hB"
    ],
    "TG": [
        "H",
        "hB"
    ],
    "TR": [
        "H",
        "hB"
    ],
    "WF": [
        "H",
        "hB"
    ],
    "YT": [
        "H",
        "hB"
    ],
    "BD": [
        "h",
        "hB",
        "H"
    ],
    "PK": [
        "h",
        "hB",
        "H"
    ],
    "AZ": [
        "H",
        "hB",
        "h"
    ],
    "BA": [
        "H",
        "hB",
        "h"
    ],
    "BG": [
        "H",
        "hB",
        "h"
    ],
    "CH": [
        "H",
        "hB",
        "h"
    ],
    "GE": [
        "H",
        "hB",
        "h"
    ],
    "LI": [
        "H",
        "hB",
        "h"
    ],
    "ME": [
        "H",
        "hB",
        "h"
    ],
    "RS": [
        "H",
        "hB",
        "h"
    ],
    "UA": [
        "H",
        "hB",
        "h"
    ],
    "UZ": [
        "H",
        "hB",
        "h"
    ],
    "XK": [
        "H",
        "hB",
        "h"
    ],
    "AG": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "AU": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "BB": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "BM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "BS": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "CA": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "DM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "en-001": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "FJ": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "FM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "GD": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "GM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "GU": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "GY": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "JM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "KI": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "KN": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "KY": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "LC": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "LR": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "MH": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "MP": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "MW": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "NZ": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "SB": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "SG": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "SL": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "SS": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "SZ": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "TC": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "TT": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "UM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "US": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "VC": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "VG": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "VI": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "ZM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "BO": [
        "H",
        "hB",
        "h",
        "hb"
    ],
    "EC": [
        "H",
        "hB",
        "h",
        "hb"
    ],
    "ES": [
        "H",
        "hB",
        "h",
        "hb"
    ],
    "GQ": [
        "H",
        "hB",
        "h",
        "hb"
    ],
    "PE": [
        "H",
        "hB",
        "h",
        "hb"
    ],
    "AE": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "ar-001": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "BH": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "DZ": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "EG": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "EH": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "HK": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "IQ": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "JO": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "KW": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "LB": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "LY": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "MO": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "MR": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "OM": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "PH": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "PS": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "QA": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "SA": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "SD": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "SY": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "TN": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "YE": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "AF": [
        "H",
        "hb",
        "hB",
        "h"
    ],
    "LA": [
        "H",
        "hb",
        "hB",
        "h"
    ],
    "CN": [
        "H",
        "hB",
        "hb",
        "h"
    ],
    "LV": [
        "H",
        "hB",
        "hb",
        "h"
    ],
    "TL": [
        "H",
        "hB",
        "hb",
        "h"
    ],
    "zu-ZA": [
        "H",
        "hB",
        "hb",
        "h"
    ],
    "CD": [
        "hB",
        "H"
    ],
    "IR": [
        "hB",
        "H"
    ],
    "hi-IN": [
        "hB",
        "h",
        "H"
    ],
    "kn-IN": [
        "hB",
        "h",
        "H"
    ],
    "ml-IN": [
        "hB",
        "h",
        "H"
    ],
    "te-IN": [
        "hB",
        "h",
        "H"
    ],
    "KH": [
        "hB",
        "h",
        "H",
        "hb"
    ],
    "ta-IN": [
        "hB",
        "h",
        "hb",
        "H"
    ],
    "BN": [
        "hb",
        "hB",
        "h",
        "H"
    ],
    "MY": [
        "hb",
        "hB",
        "h",
        "H"
    ],
    "ET": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "gu-IN": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "mr-IN": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "pa-IN": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "TW": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "KE": [
        "hB",
        "hb",
        "H",
        "h"
    ],
    "MM": [
        "hB",
        "hb",
        "H",
        "h"
    ],
    "TZ": [
        "hB",
        "hb",
        "H",
        "h"
    ],
    "UG": [
        "hB",
        "hb",
        "H",
        "h"
    ]
};

/**
 * Returns the best matching date time pattern if a date time skeleton
 * pattern is provided with a locale. Follows the Unicode specification:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#table-mapping-requested-time-skeletons-to-patterns
 * @param skeleton date time skeleton pattern that possibly includes j, J or C
 * @param locale
 */
// TODO: Fix Intl.Locale throwing TS error
// @ts-ignore
function getBestPattern(skeleton, locale) {
    let skeletonCopy = '';
    for (let patternPos = 0; patternPos < skeleton.length; patternPos++) {
        const patternChar = skeleton.charAt(patternPos);
        if (patternChar === 'j') {
            let extraLength = 0;
            while (patternPos + 1 < skeleton.length &&
                skeleton.charAt(patternPos + 1) === patternChar) {
                extraLength++;
                patternPos++;
            }
            let hourLen = 1 + (extraLength & 1);
            let dayPeriodLen = extraLength < 2 ? 1 : 3 + (extraLength >> 1);
            let dayPeriodChar = 'a';
            let hourChar = getDefaultHourSymbolFromLocale(locale);
            if (hourChar == 'H' || hourChar == 'k') {
                dayPeriodLen = 0;
            }
            while (dayPeriodLen-- > 0) {
                skeletonCopy += dayPeriodChar;
            }
            while (hourLen-- > 0) {
                skeletonCopy = hourChar + skeletonCopy;
            }
        }
        else if (patternChar === 'J') {
            skeletonCopy += 'H';
        }
        else {
            skeletonCopy += patternChar;
        }
    }
    return skeletonCopy;
}
/**
 * Maps the [hour cycle type](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale/hourCycle)
 * of the given `locale` to the corresponding time pattern.
 * @param locale
 */
// @ts-ignore
function getDefaultHourSymbolFromLocale(locale) {
    let hourCycle = locale.hourCycle;
    if (hourCycle === undefined &&
        // @ts-ignore hourCycle(s) is not identified yet
        locale.hourCycles &&
        // @ts-ignore
        locale.hourCycles.length) {
        // @ts-ignore
        hourCycle = locale.hourCycles[0];
    }
    if (hourCycle) {
        switch (hourCycle) {
            case 'h24':
                return 'k';
            case 'h23':
                return 'H';
            case 'h12':
                return 'h';
            case 'h11':
                return 'K';
            default:
                throw new Error('Invalid hourCycle');
        }
    }
    // TODO: Once hourCycle is fully supported remove the following with data generation
    const languageTag = locale.language;
    let regionTag;
    if (languageTag !== 'root') {
        regionTag = locale.maximize().region;
    }
    const hourCycles = timeData[regionTag || ''] ||
        timeData[languageTag || ''] ||
        timeData[`${languageTag}-001`] ||
        timeData['001'];
    return hourCycles[0];
}

const SPACE_SEPARATOR_START_REGEX = new RegExp(`^${SPACE_SEPARATOR_REGEX.source}*`);
const SPACE_SEPARATOR_END_REGEX = new RegExp(`${SPACE_SEPARATOR_REGEX.source}*$`);
function createLocation(start, end) {
    return { start, end };
}
// #region Ponyfills
// Consolidate these variables up top for easier toggling during debugging
const hasNativeStartsWith = !!String.prototype.startsWith;
const hasNativeFromCodePoint = !!String.fromCodePoint;
const hasNativeFromEntries = !!Object.fromEntries;
const hasNativeCodePointAt = !!String.prototype.codePointAt;
const hasTrimStart = !!String.prototype.trimStart;
const hasTrimEnd = !!String.prototype.trimEnd;
const hasNativeIsSafeInteger = !!Number.isSafeInteger;
const isSafeInteger = hasNativeIsSafeInteger
    ? Number.isSafeInteger
    : (n) => {
        return (typeof n === 'number' &&
            isFinite(n) &&
            Math.floor(n) === n &&
            Math.abs(n) <= 0x1fffffffffffff);
    };
// IE11 does not support y and u.
let REGEX_SUPPORTS_U_AND_Y = true;
try {
    const re = RE('([^\\p{White_Space}\\p{Pattern_Syntax}]*)', 'yu');
    /**
     * legacy Edge or Xbox One browser
     * Unicode flag support: supported
     * Pattern_Syntax support: not supported
     * See https://github.com/formatjs/formatjs/issues/2822
     */
    const result = re.exec('a');
    REGEX_SUPPORTS_U_AND_Y = (result && result[0]) === 'a';
}
catch (_) {
    REGEX_SUPPORTS_U_AND_Y = false;
}
const startsWith = hasNativeStartsWith
    ? // Native
        function startsWith(s, search, position) {
            return s.startsWith(search, position);
        }
    : // For IE11
        function startsWith(s, search, position) {
            return s.slice(position, position + search.length) === search;
        };
const fromCodePoint = hasNativeFromCodePoint
    ? String.fromCodePoint
    : // IE11
        function fromCodePoint(...codePoints) {
            let elements = '';
            let length = codePoints.length;
            let i = 0;
            let code;
            while (length > i) {
                code = codePoints[i++];
                if (code > 0x10ffff)
                    throw RangeError(code + ' is not a valid code point');
                elements +=
                    code < 0x10000
                        ? String.fromCharCode(code)
                        : String.fromCharCode(((code -= 0x10000) >> 10) + 0xd800, (code % 0x400) + 0xdc00);
            }
            return elements;
        };
const fromEntries = 
// native
hasNativeFromEntries
    ? Object.fromEntries
    : // Ponyfill
        function fromEntries(entries) {
            const obj = {};
            for (const [k, v] of entries) {
                obj[k] = v;
            }
            return obj;
        };
const codePointAt = hasNativeCodePointAt
    ? // Native
        function codePointAt(s, index) {
            return s.codePointAt(index);
        }
    : // IE 11
        function codePointAt(s, index) {
            let size = s.length;
            if (index < 0 || index >= size) {
                return undefined;
            }
            let first = s.charCodeAt(index);
            let second;
            return first < 0xd800 ||
                first > 0xdbff ||
                index + 1 === size ||
                (second = s.charCodeAt(index + 1)) < 0xdc00 ||
                second > 0xdfff
                ? first
                : ((first - 0xd800) << 10) + (second - 0xdc00) + 0x10000;
        };
const trimStart = hasTrimStart
    ? // Native
        function trimStart(s) {
            return s.trimStart();
        }
    : // Ponyfill
        function trimStart(s) {
            return s.replace(SPACE_SEPARATOR_START_REGEX, '');
        };
const trimEnd = hasTrimEnd
    ? // Native
        function trimEnd(s) {
            return s.trimEnd();
        }
    : // Ponyfill
        function trimEnd(s) {
            return s.replace(SPACE_SEPARATOR_END_REGEX, '');
        };
// Prevent minifier to translate new RegExp to literal form that might cause syntax error on IE11.
function RE(s, flag) {
    return new RegExp(s, flag);
}
// #endregion
let matchIdentifierAtIndex;
if (REGEX_SUPPORTS_U_AND_Y) {
    // Native
    const IDENTIFIER_PREFIX_RE = RE('([^\\p{White_Space}\\p{Pattern_Syntax}]*)', 'yu');
    matchIdentifierAtIndex = function matchIdentifierAtIndex(s, index) {
        IDENTIFIER_PREFIX_RE.lastIndex = index;
        const match = IDENTIFIER_PREFIX_RE.exec(s);
        return (match && match[1] !== undefined && match[1] !== null)
            ? match[1]
            : '';
    };
}
else {
    // IE11
    matchIdentifierAtIndex = function matchIdentifierAtIndex(s, index) {
        let match = [];
        while (true) {
            const c = codePointAt(s, index);
            if (c === undefined || _isWhiteSpace(c) || _isPatternSyntax(c)) {
                break;
            }
            match.push(c);
            index += c >= 0x10000 ? 2 : 1;
        }
        return fromCodePoint(...match);
    };
}
class Parser {
    constructor(message, options = {}) {
        this.message = message;
        this.position = { offset: 0, line: 1, column: 1 };
        this.ignoreTag = !!options.ignoreTag;
        this.locale = options.locale;
        this.requiresOtherClause = !!options.requiresOtherClause;
        this.shouldParseSkeletons = !!options.shouldParseSkeletons;
    }
    parse() {
        if (this.offset() !== 0) {
            throw Error('parser can only be used once');
        }
        return this.parseMessage(0, '', false);
    }
    parseMessage(nestingLevel, parentArgType, expectingCloseTag) {
        let elements = [];
        while (!this.isEOF()) {
            const char = this.char();
            if (char === 123 /* `{` */) {
                const result = this.parseArgument(nestingLevel, expectingCloseTag);
                if (result.err) {
                    return result;
                }
                elements.push(result.val);
            }
            else if (char === 125 /* `}` */ && nestingLevel > 0) {
                break;
            }
            else if (char === 35 /* `#` */ &&
                (parentArgType === 'plural' || parentArgType === 'selectordinal')) {
                const position = this.clonePosition();
                this.bump();
                elements.push({
                    type: TYPE.pound,
                    location: createLocation(position, this.clonePosition()),
                });
            }
            else if (char === 60 /* `<` */ &&
                !this.ignoreTag &&
                this.peek() === 47 // char code for '/'
            ) {
                if (expectingCloseTag) {
                    break;
                }
                else {
                    return this.error(ErrorKind.UNMATCHED_CLOSING_TAG, createLocation(this.clonePosition(), this.clonePosition()));
                }
            }
            else if (char === 60 /* `<` */ &&
                !this.ignoreTag &&
                _isAlpha(this.peek() || 0)) {
                const result = this.parseTag(nestingLevel, parentArgType);
                if (result.err) {
                    return result;
                }
                elements.push(result.val);
            }
            else {
                const result = this.parseLiteral(nestingLevel, parentArgType);
                if (result.err) {
                    return result;
                }
                elements.push(result.val);
            }
        }
        return { val: elements, err: null };
    }
    /**
     * A tag name must start with an ASCII lower/upper case letter. The grammar is based on the
     * [custom element name][] except that a dash is NOT always mandatory and uppercase letters
     * are accepted:
     *
     * ```
     * tag ::= "<" tagName (whitespace)* "/>" | "<" tagName (whitespace)* ">" message "</" tagName (whitespace)* ">"
     * tagName ::= [a-z] (PENChar)*
     * PENChar ::=
     *     "-" | "." | [0-9] | "_" | [a-z] | [A-Z] | #xB7 | [#xC0-#xD6] | [#xD8-#xF6] | [#xF8-#x37D] |
     *     [#x37F-#x1FFF] | [#x200C-#x200D] | [#x203F-#x2040] | [#x2070-#x218F] | [#x2C00-#x2FEF] |
     *     [#x3001-#xD7FF] | [#xF900-#xFDCF] | [#xFDF0-#xFFFD] | [#x10000-#xEFFFF]
     * ```
     *
     * [custom element name]: https://html.spec.whatwg.org/multipage/custom-elements.html#valid-custom-element-name
     * NOTE: We're a bit more lax here since HTML technically does not allow uppercase HTML element but we do
     * since other tag-based engines like React allow it
     */
    parseTag(nestingLevel, parentArgType) {
        const startPosition = this.clonePosition();
        this.bump(); // `<`
        const tagName = this.parseTagName();
        this.bumpSpace();
        if (this.bumpIf('/>')) {
            // Self closing tag
            return {
                val: {
                    type: TYPE.literal,
                    value: `<${tagName}/>`,
                    location: createLocation(startPosition, this.clonePosition()),
                },
                err: null,
            };
        }
        else if (this.bumpIf('>')) {
            const childrenResult = this.parseMessage(nestingLevel + 1, parentArgType, true);
            if (childrenResult.err) {
                return childrenResult;
            }
            const children = childrenResult.val;
            // Expecting a close tag
            const endTagStartPosition = this.clonePosition();
            if (this.bumpIf('</')) {
                if (this.isEOF() || !_isAlpha(this.char())) {
                    return this.error(ErrorKind.INVALID_TAG, createLocation(endTagStartPosition, this.clonePosition()));
                }
                const closingTagNameStartPosition = this.clonePosition();
                const closingTagName = this.parseTagName();
                if (tagName !== closingTagName) {
                    return this.error(ErrorKind.UNMATCHED_CLOSING_TAG, createLocation(closingTagNameStartPosition, this.clonePosition()));
                }
                this.bumpSpace();
                if (!this.bumpIf('>')) {
                    return this.error(ErrorKind.INVALID_TAG, createLocation(endTagStartPosition, this.clonePosition()));
                }
                return {
                    val: {
                        type: TYPE.tag,
                        value: tagName,
                        children,
                        location: createLocation(startPosition, this.clonePosition()),
                    },
                    err: null,
                };
            }
            else {
                return this.error(ErrorKind.UNCLOSED_TAG, createLocation(startPosition, this.clonePosition()));
            }
        }
        else {
            return this.error(ErrorKind.INVALID_TAG, createLocation(startPosition, this.clonePosition()));
        }
    }
    /**
     * This method assumes that the caller has peeked ahead for the first tag character.
     */
    parseTagName() {
        const startOffset = this.offset();
        this.bump(); // the first tag name character
        while (!this.isEOF() && _isPotentialElementNameChar(this.char())) {
            this.bump();
        }
        return this.message.slice(startOffset, this.offset());
    }
    parseLiteral(nestingLevel, parentArgType) {
        const start = this.clonePosition();
        let value = '';
        while (true) {
            const parseQuoteResult = this.tryParseQuote(parentArgType);
            if (parseQuoteResult) {
                value += parseQuoteResult;
                continue;
            }
            const parseUnquotedResult = this.tryParseUnquoted(nestingLevel, parentArgType);
            if (parseUnquotedResult) {
                value += parseUnquotedResult;
                continue;
            }
            const parseLeftAngleResult = this.tryParseLeftAngleBracket();
            if (parseLeftAngleResult) {
                value += parseLeftAngleResult;
                continue;
            }
            break;
        }
        const location = createLocation(start, this.clonePosition());
        return {
            val: { type: TYPE.literal, value, location },
            err: null,
        };
    }
    tryParseLeftAngleBracket() {
        if (!this.isEOF() &&
            this.char() === 60 /* `<` */ &&
            (this.ignoreTag ||
                // If at the opening tag or closing tag position, bail.
                !_isAlphaOrSlash(this.peek() || 0))) {
            this.bump(); // `<`
            return '<';
        }
        return null;
    }
    /**
     * Starting with ICU 4.8, an ASCII apostrophe only starts quoted text if it immediately precedes
     * a character that requires quoting (that is, "only where needed"), and works the same in
     * nested messages as on the top level of the pattern. The new behavior is otherwise compatible.
     */
    tryParseQuote(parentArgType) {
        if (this.isEOF() || this.char() !== 39 /* `'` */) {
            return null;
        }
        // Parse escaped char following the apostrophe, or early return if there is no escaped char.
        // Check if is valid escaped character
        switch (this.peek()) {
            case 39 /* `'` */:
                // double quote, should return as a single quote.
                this.bump();
                this.bump();
                return "'";
            // '{', '<', '>', '}'
            case 123:
            case 60:
            case 62:
            case 125:
                break;
            case 35: // '#'
                if (parentArgType === 'plural' || parentArgType === 'selectordinal') {
                    break;
                }
                return null;
            default:
                return null;
        }
        this.bump(); // apostrophe
        const codePoints = [this.char()]; // escaped char
        this.bump();
        // read chars until the optional closing apostrophe is found
        while (!this.isEOF()) {
            const ch = this.char();
            if (ch === 39 /* `'` */) {
                if (this.peek() === 39 /* `'` */) {
                    codePoints.push(39);
                    // Bump one more time because we need to skip 2 characters.
                    this.bump();
                }
                else {
                    // Optional closing apostrophe.
                    this.bump();
                    break;
                }
            }
            else {
                codePoints.push(ch);
            }
            this.bump();
        }
        return fromCodePoint(...codePoints);
    }
    tryParseUnquoted(nestingLevel, parentArgType) {
        if (this.isEOF()) {
            return null;
        }
        const ch = this.char();
        if (ch === 60 /* `<` */ ||
            ch === 123 /* `{` */ ||
            (ch === 35 /* `#` */ &&
                (parentArgType === 'plural' || parentArgType === 'selectordinal')) ||
            (ch === 125 /* `}` */ && nestingLevel > 0)) {
            return null;
        }
        else {
            this.bump();
            return fromCodePoint(ch);
        }
    }
    parseArgument(nestingLevel, expectingCloseTag) {
        const openingBracePosition = this.clonePosition();
        this.bump(); // `{`
        this.bumpSpace();
        if (this.isEOF()) {
            return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
        }
        if (this.char() === 125 /* `}` */) {
            this.bump();
            return this.error(ErrorKind.EMPTY_ARGUMENT, createLocation(openingBracePosition, this.clonePosition()));
        }
        // argument name
        let value = this.parseIdentifierIfPossible().value;
        if (!value) {
            return this.error(ErrorKind.MALFORMED_ARGUMENT, createLocation(openingBracePosition, this.clonePosition()));
        }
        this.bumpSpace();
        if (this.isEOF()) {
            return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
        }
        switch (this.char()) {
            // Simple argument: `{name}`
            case 125 /* `}` */: {
                this.bump(); // `}`
                return {
                    val: {
                        type: TYPE.argument,
                        // value does not include the opening and closing braces.
                        value,
                        location: createLocation(openingBracePosition, this.clonePosition()),
                    },
                    err: null,
                };
            }
            // Argument with options: `{name, format, ...}`
            case 44 /* `,` */: {
                this.bump(); // `,`
                this.bumpSpace();
                if (this.isEOF()) {
                    return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
                }
                return this.parseArgumentOptions(nestingLevel, expectingCloseTag, value, openingBracePosition);
            }
            default:
                return this.error(ErrorKind.MALFORMED_ARGUMENT, createLocation(openingBracePosition, this.clonePosition()));
        }
    }
    /**
     * Advance the parser until the end of the identifier, if it is currently on
     * an identifier character. Return an empty string otherwise.
     */
    parseIdentifierIfPossible() {
        const startingPosition = this.clonePosition();
        const startOffset = this.offset();
        const value = matchIdentifierAtIndex(this.message, startOffset);
        const endOffset = startOffset + value.length;
        this.bumpTo(endOffset);
        const endPosition = this.clonePosition();
        const location = createLocation(startingPosition, endPosition);
        return { value, location };
    }
    parseArgumentOptions(nestingLevel, expectingCloseTag, value, openingBracePosition) {
        // Parse this range:
        // {name, type, style}
        //        ^---^
        let typeStartPosition = this.clonePosition();
        let argType = this.parseIdentifierIfPossible().value;
        let typeEndPosition = this.clonePosition();
        switch (argType) {
            case '':
                // Expecting a style string number, date, time, plural, selectordinal, or select.
                return this.error(ErrorKind.EXPECT_ARGUMENT_TYPE, createLocation(typeStartPosition, typeEndPosition));
            case 'number':
            // AMAZON_CHANGE_START
            case 'price':
            // AMAZON_CHANGE_END
            case 'date':
            case 'time': {
                // Parse this range:
                // {name, number, style}
                //              ^-------^
                this.bumpSpace();
                let styleAndLocation = null;
                if (this.bumpIf(',')) {
                    this.bumpSpace();
                    const styleStartPosition = this.clonePosition();
                    const result = this.parseSimpleArgStyleIfPossible();
                    if (result.err) {
                        return result;
                    }
                    const style = trimEnd(result.val);
                    if (style.length === 0) {
                        return this.error(ErrorKind.EXPECT_ARGUMENT_STYLE, createLocation(this.clonePosition(), this.clonePosition()));
                    }
                    const styleLocation = createLocation(styleStartPosition, this.clonePosition());
                    styleAndLocation = { style, styleLocation };
                }
                const argCloseResult = this.tryParseArgumentClose(openingBracePosition);
                if (argCloseResult.err) {
                    return argCloseResult;
                }
                const location = createLocation(openingBracePosition, this.clonePosition());
                // Extract style or skeleton
                if (styleAndLocation && styleAndLocation.style && startsWith(styleAndLocation.style, '::', 0)) {
                    // Skeleton starts with `::`.
                    let skeleton = trimStart(styleAndLocation.style.slice(2));
                    if (argType === 'number') {
                        const result = this.parseNumberSkeletonFromString(skeleton, styleAndLocation.styleLocation);
                        if (result.err) {
                            return result;
                        }
                        return {
                            val: { type: TYPE.number, value, location, style: result.val },
                            err: null,
                        };
                    }
                    else {
                        if (skeleton.length === 0) {
                            return this.error(ErrorKind.EXPECT_DATE_TIME_SKELETON, location);
                        }
                        let dateTimePattern = skeleton;
                        // Get "best match" pattern only if locale is passed, if not, let it
                        // pass as-is where `parseDateTimeSkeleton()` will throw an error
                        // for unsupported patterns.
                        if (this.locale) {
                            dateTimePattern = getBestPattern(skeleton, this.locale);
                        }
                        const style = {
                            type: SKELETON_TYPE.dateTime,
                            pattern: dateTimePattern,
                            location: styleAndLocation.styleLocation,
                            parsedOptions: this.shouldParseSkeletons
                                ? parseDateTimeSkeleton(dateTimePattern)
                                : {},
                        };
                        const type = argType === 'date' ? TYPE.date : TYPE.time;
                        return {
                            val: { type, value, location, style },
                            err: null,
                        };
                    }
                }
                // Regular style or no style.
                return {
                    val: {
                        type: argType === 'number'
                            ? TYPE.number
                            // AMAZON_CHANGE_START
                            : argType === 'price'
                                ? TYPE.price
                                // AMAZON_CHANGE_END
                                : argType === 'date'
                                    ? TYPE.date
                                    : TYPE.time,
                        value,
                        location,
                        style: (styleAndLocation && styleAndLocation.style !== undefined && styleAndLocation.style !== null)
                            ? styleAndLocation.style
                            : null,
                    },
                    err: null,
                };
            }
            case 'plural':
            case 'selectordinal':
            case 'select': {
                // Parse this range:
                // {name, plural, options}
                //              ^---------^
                const typeEndPosition = this.clonePosition();
                this.bumpSpace();
                if (!this.bumpIf(',')) {
                    return this.error(ErrorKind.EXPECT_SELECT_ARGUMENT_OPTIONS, createLocation(typeEndPosition, Object.assign({}, typeEndPosition)));
                }
                this.bumpSpace();
                // Parse offset:
                // {name, plural, offset:1, options}
                //                ^-----^
                //
                // or the first option:
                //
                // {name, plural, one {...} other {...}}
                //                ^--^
                let identifierAndLocation = this.parseIdentifierIfPossible();
                let pluralOffset = 0;
                if (argType !== 'select' && identifierAndLocation.value === 'offset') {
                    if (!this.bumpIf(':')) {
                        return this.error(ErrorKind.EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE, createLocation(this.clonePosition(), this.clonePosition()));
                    }
                    this.bumpSpace();
                    const result = this.tryParseDecimalInteger(ErrorKind.EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE, ErrorKind.INVALID_PLURAL_ARGUMENT_OFFSET_VALUE);
                    if (result.err) {
                        return result;
                    }
                    // Parse another identifier for option parsing
                    this.bumpSpace();
                    identifierAndLocation = this.parseIdentifierIfPossible();
                    pluralOffset = result.val;
                }
                const optionsResult = this.tryParsePluralOrSelectOptions(nestingLevel, argType, expectingCloseTag, identifierAndLocation);
                if (optionsResult.err) {
                    return optionsResult;
                }
                const argCloseResult = this.tryParseArgumentClose(openingBracePosition);
                if (argCloseResult.err) {
                    return argCloseResult;
                }
                const location = createLocation(openingBracePosition, this.clonePosition());
                if (argType === 'select') {
                    return {
                        val: {
                            type: TYPE.select,
                            value,
                            options: fromEntries(optionsResult.val),
                            location,
                        },
                        err: null,
                    };
                }
                else {
                    return {
                        val: {
                            type: TYPE.plural,
                            value,
                            options: fromEntries(optionsResult.val),
                            offset: pluralOffset,
                            pluralType: argType === 'plural' ? 'cardinal' : 'ordinal',
                            location,
                        },
                        err: null,
                    };
                }
            }
            default:
                return this.error(ErrorKind.INVALID_ARGUMENT_TYPE, createLocation(typeStartPosition, typeEndPosition));
        }
    }
    tryParseArgumentClose(openingBracePosition) {
        // Parse: {value, number, ::currency/GBP }
        //
        if (this.isEOF() || this.char() !== 125 /* `}` */) {
            return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
        }
        this.bump(); // `}`
        return { val: true, err: null };
    }
    /**
     * See: https://github.com/unicode-org/icu/blob/af7ed1f6d2298013dc303628438ec4abe1f16479/icu4c/source/common/messagepattern.cpp#L659
     */
    parseSimpleArgStyleIfPossible() {
        let nestedBraces = 0;
        const startPosition = this.clonePosition();
        while (!this.isEOF()) {
            const ch = this.char();
            switch (ch) {
                case 39 /* `'` */: {
                    // Treat apostrophe as quoting but include it in the style part.
                    // Find the end of the quoted literal text.
                    this.bump();
                    let apostrophePosition = this.clonePosition();
                    if (!this.bumpUntil("'")) {
                        return this.error(ErrorKind.UNCLOSED_QUOTE_IN_ARGUMENT_STYLE, createLocation(apostrophePosition, this.clonePosition()));
                    }
                    this.bump();
                    break;
                }
                case 123 /* `{` */: {
                    nestedBraces += 1;
                    this.bump();
                    break;
                }
                case 125 /* `}` */: {
                    if (nestedBraces > 0) {
                        nestedBraces -= 1;
                    }
                    else {
                        return {
                            val: this.message.slice(startPosition.offset, this.offset()),
                            err: null,
                        };
                    }
                    break;
                }
                default:
                    this.bump();
                    break;
            }
        }
        return {
            val: this.message.slice(startPosition.offset, this.offset()),
            err: null,
        };
    }
    parseNumberSkeletonFromString(skeleton, location) {
        let tokens = [];
        try {
            tokens = parseNumberSkeletonFromString(skeleton);
        }
        catch (e) {
            return this.error(ErrorKind.INVALID_NUMBER_SKELETON, location);
        }
        return {
            val: {
                type: SKELETON_TYPE.number,
                tokens,
                location,
                parsedOptions: this.shouldParseSkeletons
                    ? parseNumberSkeleton(tokens)
                    : {},
            },
            err: null,
        };
    }
    /**
     * @param nesting_level The current nesting level of messages.
     *     This can be positive when parsing message fragment in select or plural argument options.
     * @param parent_arg_type The parent argument's type.
     * @param parsed_first_identifier If provided, this is the first identifier-like selector of
     *     the argument. It is a by-product of a previous parsing attempt.
     * @param expecting_close_tag If true, this message is directly or indirectly nested inside
     *     between a pair of opening and closing tags. The nested message will not parse beyond
     *     the closing tag boundary.
     */
    tryParsePluralOrSelectOptions(nestingLevel, parentArgType, expectCloseTag, parsedFirstIdentifier) {
        let hasOtherClause = false;
        const options = [];
        const parsedSelectors = new Set();
        let { value: selector, location: selectorLocation } = parsedFirstIdentifier;
        // Parse:
        // one {one apple}
        // ^--^
        while (true) {
            if (selector.length === 0) {
                const startPosition = this.clonePosition();
                if (parentArgType !== 'select' && this.bumpIf('=')) {
                    // Try parse `={number}` selector
                    const result = this.tryParseDecimalInteger(ErrorKind.EXPECT_PLURAL_ARGUMENT_SELECTOR, ErrorKind.INVALID_PLURAL_ARGUMENT_SELECTOR);
                    if (result.err) {
                        return result;
                    }
                    selectorLocation = createLocation(startPosition, this.clonePosition());
                    selector = this.message.slice(startPosition.offset, this.offset());
                }
                else {
                    break;
                }
            }
            // Duplicate selector clauses
            if (parsedSelectors.has(selector)) {
                return this.error(parentArgType === 'select'
                    ? ErrorKind.DUPLICATE_SELECT_ARGUMENT_SELECTOR
                    : ErrorKind.DUPLICATE_PLURAL_ARGUMENT_SELECTOR, selectorLocation);
            }
            if (selector === 'other') {
                hasOtherClause = true;
            }
            // Parse:
            // one {one apple}
            //     ^----------^
            this.bumpSpace();
            const openingBracePosition = this.clonePosition();
            if (!this.bumpIf('{')) {
                return this.error(parentArgType === 'select'
                    ? ErrorKind.EXPECT_SELECT_ARGUMENT_SELECTOR_FRAGMENT
                    : ErrorKind.EXPECT_PLURAL_ARGUMENT_SELECTOR_FRAGMENT, createLocation(this.clonePosition(), this.clonePosition()));
            }
            const fragmentResult = this.parseMessage(nestingLevel + 1, parentArgType, expectCloseTag);
            if (fragmentResult.err) {
                return fragmentResult;
            }
            const argCloseResult = this.tryParseArgumentClose(openingBracePosition);
            if (argCloseResult.err) {
                return argCloseResult;
            }
            options.push([
                selector,
                {
                    value: fragmentResult.val,
                    location: createLocation(openingBracePosition, this.clonePosition()),
                },
            ]);
            // Keep track of the existing selectors
            parsedSelectors.add(selector);
            // Prep next selector clause.
            this.bumpSpace();
            ({ value: selector, location: selectorLocation } =
                this.parseIdentifierIfPossible());
        }
        if (options.length === 0) {
            return this.error(parentArgType === 'select'
                ? ErrorKind.EXPECT_SELECT_ARGUMENT_SELECTOR
                : ErrorKind.EXPECT_PLURAL_ARGUMENT_SELECTOR, createLocation(this.clonePosition(), this.clonePosition()));
        }
        if (this.requiresOtherClause && !hasOtherClause) {
            return this.error(ErrorKind.MISSING_OTHER_CLAUSE, createLocation(this.clonePosition(), this.clonePosition()));
        }
        return { val: options, err: null };
    }
    tryParseDecimalInteger(expectNumberError, invalidNumberError) {
        let sign = 1;
        const startingPosition = this.clonePosition();
        if (this.bumpIf('+')) ;
        else if (this.bumpIf('-')) {
            sign = -1;
        }
        let hasDigits = false;
        let decimal = 0;
        while (!this.isEOF()) {
            const ch = this.char();
            if (ch >= 48 /* `0` */ && ch <= 57 /* `9` */) {
                hasDigits = true;
                decimal = decimal * 10 + (ch - 48);
                this.bump();
            }
            else {
                break;
            }
        }
        const location = createLocation(startingPosition, this.clonePosition());
        if (!hasDigits) {
            return this.error(expectNumberError, location);
        }
        decimal *= sign;
        if (!isSafeInteger(decimal)) {
            return this.error(invalidNumberError, location);
        }
        return { val: decimal, err: null };
    }
    offset() {
        return this.position.offset;
    }
    isEOF() {
        return this.offset() === this.message.length;
    }
    clonePosition() {
        // This is much faster than `Object.assign` or spread.
        return {
            offset: this.position.offset,
            line: this.position.line,
            column: this.position.column,
        };
    }
    /**
     * Return the code point at the current position of the parser.
     * Throws if the index is out of bound.
     */
    char() {
        const offset = this.position.offset;
        if (offset >= this.message.length) {
            throw Error('out of bound');
        }
        const code = codePointAt(this.message, offset);
        if (code === undefined) {
            throw Error(`Offset ${offset} is at invalid UTF-16 code unit boundary`);
        }
        return code;
    }
    error(kind, location) {
        return {
            val: null,
            err: {
                kind,
                message: this.message,
                location,
            },
        };
    }
    /** Bump the parser to the next UTF-16 code unit. */
    bump() {
        if (this.isEOF()) {
            return;
        }
        const code = this.char();
        if (code === 10 /* '\n' */) {
            this.position.line += 1;
            this.position.column = 1;
            this.position.offset += 1;
        }
        else {
            this.position.column += 1;
            // 0 ~ 0x10000 -> unicode BMP, otherwise skip the surrogate pair.
            this.position.offset += code < 0x10000 ? 1 : 2;
        }
    }
    /**
     * If the substring starting at the current position of the parser has
     * the given prefix, then bump the parser to the character immediately
     * following the prefix and return true. Otherwise, don't bump the parser
     * and return false.
     */
    bumpIf(prefix) {
        if (startsWith(this.message, prefix, this.offset())) {
            for (let i = 0; i < prefix.length; i++) {
                this.bump();
            }
            return true;
        }
        return false;
    }
    /**
     * Bump the parser until the pattern character is found and return `true`.
     * Otherwise bump to the end of the file and return `false`.
     */
    bumpUntil(pattern) {
        const currentOffset = this.offset();
        const index = this.message.indexOf(pattern, currentOffset);
        if (index >= 0) {
            this.bumpTo(index);
            return true;
        }
        else {
            this.bumpTo(this.message.length);
            return false;
        }
    }
    /**
     * Bump the parser to the target offset.
     * If target offset is beyond the end of the input, bump the parser to the end of the input.
     */
    bumpTo(targetOffset) {
        if (this.offset() > targetOffset) {
            throw Error(`targetOffset ${targetOffset} must be greater than or equal to the current offset ${this.offset()}`);
        }
        targetOffset = Math.min(targetOffset, this.message.length);
        while (true) {
            const offset = this.offset();
            if (offset === targetOffset) {
                break;
            }
            if (offset > targetOffset) {
                throw Error(`targetOffset ${targetOffset} is at invalid UTF-16 code unit boundary`);
            }
            this.bump();
            if (this.isEOF()) {
                break;
            }
        }
    }
    /** advance the parser through all whitespace to the next non-whitespace code unit. */
    bumpSpace() {
        while (!this.isEOF() && _isWhiteSpace(this.char())) {
            this.bump();
        }
    }
    /**
     * Peek at the *next* Unicode codepoint in the input without advancing the parser.
     * If the input has been exhausted, then this returns null.
     */
    peek() {
        if (this.isEOF()) {
            return null;
        }
        const code = this.char();
        const offset = this.offset();
        const nextCode = this.message.charCodeAt(offset + (code >= 0x10000 ? 2 : 1));
        return (nextCode !== undefined && nextCode !== null) ? nextCode : null;
    }
}
/**
 * This check if codepoint is alphabet (lower & uppercase)
 * @param codepoint
 * @returns
 */
function _isAlpha(codepoint) {
    return ((codepoint >= 97 && codepoint <= 122) ||
        (codepoint >= 65 && codepoint <= 90));
}
function _isAlphaOrSlash(codepoint) {
    return _isAlpha(codepoint) || codepoint === 47; /* '/' */
}
/** See `parseTag` function docs. */
function _isPotentialElementNameChar(c) {
    return (c === 45 /* '-' */ ||
        c === 46 /* '.' */ ||
        (c >= 48 && c <= 57) /* 0..9 */ ||
        c === 95 /* '_' */ ||
        (c >= 97 && c <= 122) /** a..z */ ||
        (c >= 65 && c <= 90) /* A..Z */ ||
        c == 0xb7 ||
        (c >= 0xc0 && c <= 0xd6) ||
        (c >= 0xd8 && c <= 0xf6) ||
        (c >= 0xf8 && c <= 0x37d) ||
        (c >= 0x37f && c <= 0x1fff) ||
        (c >= 0x200c && c <= 0x200d) ||
        (c >= 0x203f && c <= 0x2040) ||
        (c >= 0x2070 && c <= 0x218f) ||
        (c >= 0x2c00 && c <= 0x2fef) ||
        (c >= 0x3001 && c <= 0xd7ff) ||
        (c >= 0xf900 && c <= 0xfdcf) ||
        (c >= 0xfdf0 && c <= 0xfffd) ||
        (c >= 0x10000 && c <= 0xeffff));
}
/**
 * Code point equivalent of regex `\p{White_Space}`.
 * From: https://www.unicode.org/Public/UCD/latest/ucd/PropList.txt
 */
function _isWhiteSpace(c) {
    return ((c >= 0x0009 && c <= 0x000d) ||
        c === 0x0020 ||
        c === 0x0085 ||
        (c >= 0x200e && c <= 0x200f) ||
        c === 0x2028 ||
        c === 0x2029);
}
/**
 * Code point equivalent of regex `\p{Pattern_Syntax}`.
 * See https://www.unicode.org/Public/UCD/latest/ucd/PropList.txt
 */
function _isPatternSyntax(c) {
    return ((c >= 0x0021 && c <= 0x0023) ||
        c === 0x0024 ||
        (c >= 0x0025 && c <= 0x0027) ||
        c === 0x0028 ||
        c === 0x0029 ||
        c === 0x002a ||
        c === 0x002b ||
        c === 0x002c ||
        c === 0x002d ||
        (c >= 0x002e && c <= 0x002f) ||
        (c >= 0x003a && c <= 0x003b) ||
        (c >= 0x003c && c <= 0x003e) ||
        (c >= 0x003f && c <= 0x0040) ||
        c === 0x005b ||
        c === 0x005c ||
        c === 0x005d ||
        c === 0x005e ||
        c === 0x0060 ||
        c === 0x007b ||
        c === 0x007c ||
        c === 0x007d ||
        c === 0x007e ||
        c === 0x00a1 ||
        (c >= 0x00a2 && c <= 0x00a5) ||
        c === 0x00a6 ||
        c === 0x00a7 ||
        c === 0x00a9 ||
        c === 0x00ab ||
        c === 0x00ac ||
        c === 0x00ae ||
        c === 0x00b0 ||
        c === 0x00b1 ||
        c === 0x00b6 ||
        c === 0x00bb ||
        c === 0x00bf ||
        c === 0x00d7 ||
        c === 0x00f7 ||
        (c >= 0x2010 && c <= 0x2015) ||
        (c >= 0x2016 && c <= 0x2017) ||
        c === 0x2018 ||
        c === 0x2019 ||
        c === 0x201a ||
        (c >= 0x201b && c <= 0x201c) ||
        c === 0x201d ||
        c === 0x201e ||
        c === 0x201f ||
        (c >= 0x2020 && c <= 0x2027) ||
        (c >= 0x2030 && c <= 0x2038) ||
        c === 0x2039 ||
        c === 0x203a ||
        (c >= 0x203b && c <= 0x203e) ||
        (c >= 0x2041 && c <= 0x2043) ||
        c === 0x2044 ||
        c === 0x2045 ||
        c === 0x2046 ||
        (c >= 0x2047 && c <= 0x2051) ||
        c === 0x2052 ||
        c === 0x2053 ||
        (c >= 0x2055 && c <= 0x205e) ||
        (c >= 0x2190 && c <= 0x2194) ||
        (c >= 0x2195 && c <= 0x2199) ||
        (c >= 0x219a && c <= 0x219b) ||
        (c >= 0x219c && c <= 0x219f) ||
        c === 0x21a0 ||
        (c >= 0x21a1 && c <= 0x21a2) ||
        c === 0x21a3 ||
        (c >= 0x21a4 && c <= 0x21a5) ||
        c === 0x21a6 ||
        (c >= 0x21a7 && c <= 0x21ad) ||
        c === 0x21ae ||
        (c >= 0x21af && c <= 0x21cd) ||
        (c >= 0x21ce && c <= 0x21cf) ||
        (c >= 0x21d0 && c <= 0x21d1) ||
        c === 0x21d2 ||
        c === 0x21d3 ||
        c === 0x21d4 ||
        (c >= 0x21d5 && c <= 0x21f3) ||
        (c >= 0x21f4 && c <= 0x22ff) ||
        (c >= 0x2300 && c <= 0x2307) ||
        c === 0x2308 ||
        c === 0x2309 ||
        c === 0x230a ||
        c === 0x230b ||
        (c >= 0x230c && c <= 0x231f) ||
        (c >= 0x2320 && c <= 0x2321) ||
        (c >= 0x2322 && c <= 0x2328) ||
        c === 0x2329 ||
        c === 0x232a ||
        (c >= 0x232b && c <= 0x237b) ||
        c === 0x237c ||
        (c >= 0x237d && c <= 0x239a) ||
        (c >= 0x239b && c <= 0x23b3) ||
        (c >= 0x23b4 && c <= 0x23db) ||
        (c >= 0x23dc && c <= 0x23e1) ||
        (c >= 0x23e2 && c <= 0x2426) ||
        (c >= 0x2427 && c <= 0x243f) ||
        (c >= 0x2440 && c <= 0x244a) ||
        (c >= 0x244b && c <= 0x245f) ||
        (c >= 0x2500 && c <= 0x25b6) ||
        c === 0x25b7 ||
        (c >= 0x25b8 && c <= 0x25c0) ||
        c === 0x25c1 ||
        (c >= 0x25c2 && c <= 0x25f7) ||
        (c >= 0x25f8 && c <= 0x25ff) ||
        (c >= 0x2600 && c <= 0x266e) ||
        c === 0x266f ||
        (c >= 0x2670 && c <= 0x2767) ||
        c === 0x2768 ||
        c === 0x2769 ||
        c === 0x276a ||
        c === 0x276b ||
        c === 0x276c ||
        c === 0x276d ||
        c === 0x276e ||
        c === 0x276f ||
        c === 0x2770 ||
        c === 0x2771 ||
        c === 0x2772 ||
        c === 0x2773 ||
        c === 0x2774 ||
        c === 0x2775 ||
        (c >= 0x2794 && c <= 0x27bf) ||
        (c >= 0x27c0 && c <= 0x27c4) ||
        c === 0x27c5 ||
        c === 0x27c6 ||
        (c >= 0x27c7 && c <= 0x27e5) ||
        c === 0x27e6 ||
        c === 0x27e7 ||
        c === 0x27e8 ||
        c === 0x27e9 ||
        c === 0x27ea ||
        c === 0x27eb ||
        c === 0x27ec ||
        c === 0x27ed ||
        c === 0x27ee ||
        c === 0x27ef ||
        (c >= 0x27f0 && c <= 0x27ff) ||
        (c >= 0x2800 && c <= 0x28ff) ||
        (c >= 0x2900 && c <= 0x2982) ||
        c === 0x2983 ||
        c === 0x2984 ||
        c === 0x2985 ||
        c === 0x2986 ||
        c === 0x2987 ||
        c === 0x2988 ||
        c === 0x2989 ||
        c === 0x298a ||
        c === 0x298b ||
        c === 0x298c ||
        c === 0x298d ||
        c === 0x298e ||
        c === 0x298f ||
        c === 0x2990 ||
        c === 0x2991 ||
        c === 0x2992 ||
        c === 0x2993 ||
        c === 0x2994 ||
        c === 0x2995 ||
        c === 0x2996 ||
        c === 0x2997 ||
        c === 0x2998 ||
        (c >= 0x2999 && c <= 0x29d7) ||
        c === 0x29d8 ||
        c === 0x29d9 ||
        c === 0x29da ||
        c === 0x29db ||
        (c >= 0x29dc && c <= 0x29fb) ||
        c === 0x29fc ||
        c === 0x29fd ||
        (c >= 0x29fe && c <= 0x2aff) ||
        (c >= 0x2b00 && c <= 0x2b2f) ||
        (c >= 0x2b30 && c <= 0x2b44) ||
        (c >= 0x2b45 && c <= 0x2b46) ||
        (c >= 0x2b47 && c <= 0x2b4c) ||
        (c >= 0x2b4d && c <= 0x2b73) ||
        (c >= 0x2b74 && c <= 0x2b75) ||
        (c >= 0x2b76 && c <= 0x2b95) ||
        c === 0x2b96 ||
        (c >= 0x2b97 && c <= 0x2bff) ||
        (c >= 0x2e00 && c <= 0x2e01) ||
        c === 0x2e02 ||
        c === 0x2e03 ||
        c === 0x2e04 ||
        c === 0x2e05 ||
        (c >= 0x2e06 && c <= 0x2e08) ||
        c === 0x2e09 ||
        c === 0x2e0a ||
        c === 0x2e0b ||
        c === 0x2e0c ||
        c === 0x2e0d ||
        (c >= 0x2e0e && c <= 0x2e16) ||
        c === 0x2e17 ||
        (c >= 0x2e18 && c <= 0x2e19) ||
        c === 0x2e1a ||
        c === 0x2e1b ||
        c === 0x2e1c ||
        c === 0x2e1d ||
        (c >= 0x2e1e && c <= 0x2e1f) ||
        c === 0x2e20 ||
        c === 0x2e21 ||
        c === 0x2e22 ||
        c === 0x2e23 ||
        c === 0x2e24 ||
        c === 0x2e25 ||
        c === 0x2e26 ||
        c === 0x2e27 ||
        c === 0x2e28 ||
        c === 0x2e29 ||
        (c >= 0x2e2a && c <= 0x2e2e) ||
        c === 0x2e2f ||
        (c >= 0x2e30 && c <= 0x2e39) ||
        (c >= 0x2e3a && c <= 0x2e3b) ||
        (c >= 0x2e3c && c <= 0x2e3f) ||
        c === 0x2e40 ||
        c === 0x2e41 ||
        c === 0x2e42 ||
        (c >= 0x2e43 && c <= 0x2e4f) ||
        (c >= 0x2e50 && c <= 0x2e51) ||
        c === 0x2e52 ||
        (c >= 0x2e53 && c <= 0x2e7f) ||
        (c >= 0x3001 && c <= 0x3003) ||
        c === 0x3008 ||
        c === 0x3009 ||
        c === 0x300a ||
        c === 0x300b ||
        c === 0x300c ||
        c === 0x300d ||
        c === 0x300e ||
        c === 0x300f ||
        c === 0x3010 ||
        c === 0x3011 ||
        (c >= 0x3012 && c <= 0x3013) ||
        c === 0x3014 ||
        c === 0x3015 ||
        c === 0x3016 ||
        c === 0x3017 ||
        c === 0x3018 ||
        c === 0x3019 ||
        c === 0x301a ||
        c === 0x301b ||
        c === 0x301c ||
        c === 0x301d ||
        (c >= 0x301e && c <= 0x301f) ||
        c === 0x3020 ||
        c === 0x3030 ||
        c === 0xfd3e ||
        c === 0xfd3f ||
        (c >= 0xfe45 && c <= 0xfe46));
}

function pruneLocation(els) {
    els.forEach(el => {
        delete el.location;
        if (isSelectElement(el) || isPluralElement(el)) {
            for (const k in el.options) {
                delete el.options[k].location;
                pruneLocation(el.options[k].value);
            }
        }
        else if (isNumberElement(el) && isNumberSkeleton(el.style)) {
            delete el.style.location;
        }
        else if ((isDateElement(el) || isTimeElement(el)) &&
            isDateTimeSkeleton(el.style)) {
            delete el.style.location;
        }
        else if (isTagElement(el)) {
            pruneLocation(el.children);
        }
    });
}
function parse(message, opts = {}) {
    opts = Object.assign({ shouldParseSkeletons: true, requiresOtherClause: true }, opts);
    const result = new Parser(message, opts).parse();
    if (result.err) {
        const error = SyntaxError(ErrorKind[result.err.kind]);
        // @ts-expect-error Assign to error object
        error.location = result.err.location;
        // @ts-expect-error Assign to error object
        error.originalMessage = result.err.message;
        throw error;
    }
    if (!(opts && opts.captureLocation)) {
        pruneLocation(result.val);
    }
    return result.val;
}

//
// Main
//
function memoize(fn, options) {
    var cache = options && options.cache ? options.cache : cacheDefault;
    var serializer = options && options.serializer ? options.serializer : serializerDefault;
    var strategy = options && options.strategy ? options.strategy : strategyDefault;
    return strategy(fn, {
        cache: cache,
        serializer: serializer,
    });
}
//
// Strategy
//
function isPrimitive(value) {
    return (value == null || typeof value === 'number' || typeof value === 'boolean'); // || typeof value === "string" 'unsafe' primitive for our needs
}
function monadic(fn, cache, serializer, arg) {
    var cacheKey = isPrimitive(arg) ? arg : serializer(arg);
    var computedValue = cache.get(cacheKey);
    if (typeof computedValue === 'undefined') {
        computedValue = fn.call(this, arg);
        cache.set(cacheKey, computedValue);
    }
    return computedValue;
}
function variadic(fn, cache, serializer) {
    var args = Array.prototype.slice.call(arguments, 3);
    var cacheKey = serializer(args);
    var computedValue = cache.get(cacheKey);
    if (typeof computedValue === 'undefined') {
        computedValue = fn.apply(this, args);
        cache.set(cacheKey, computedValue);
    }
    return computedValue;
}
function assemble(fn, context, strategy, cache, serialize) {
    return strategy.bind(context, fn, cache, serialize);
}
function strategyDefault(fn, options) {
    var strategy = fn.length === 1 ? monadic : variadic;
    return assemble(fn, this, strategy, options.cache.create(), options.serializer);
}
function strategyVariadic(fn, options) {
    return assemble(fn, this, variadic, options.cache.create(), options.serializer);
}
function strategyMonadic(fn, options) {
    return assemble(fn, this, monadic, options.cache.create(), options.serializer);
}
//
// Serializer
//
var serializerDefault = function () {
    return JSON.stringify(arguments);
};
//
// Cache
//
function ObjectWithoutPrototypeCache() {
    this.cache = Object.create(null);
}
ObjectWithoutPrototypeCache.prototype.get = function (key) {
    return this.cache[key];
};
ObjectWithoutPrototypeCache.prototype.set = function (key, value) {
    this.cache[key] = value;
};
var cacheDefault = {
    create: function create() {
        // @ts-ignore
        return new ObjectWithoutPrototypeCache();
    },
};
var strategies = {
    variadic: strategyVariadic,
    monadic: strategyMonadic,
};

var ErrorCode;
(function (ErrorCode) {
    // When we have a placeholder but no value to format
    ErrorCode["MISSING_VALUE"] = "MISSING_VALUE";
    // When value supplied is invalid
    ErrorCode["INVALID_VALUE"] = "INVALID_VALUE";
    // When we need specific Intl API but it's not available
    ErrorCode["MISSING_INTL_API"] = "MISSING_INTL_API";
})(ErrorCode || (ErrorCode = {}));
class FormatError extends Error {
    constructor(msg, code, originalMessage) {
        super(msg);
        this.code = code;
        this.originalMessage = originalMessage;
    }
    toString() {
        return `[formatjs Error: ${this.code}] ${this.message}`;
    }
}
class InvalidValueError extends FormatError {
    constructor(variableId, value, options, originalMessage) {
        super(`Invalid values for "${variableId}": "${value}". Options are "${Object.keys(options).join('", "')}"`, ErrorCode.INVALID_VALUE, originalMessage);
    }
}
class InvalidValueTypeError extends FormatError {
    constructor(value, type, originalMessage) {
        super(`Value for "${value}" must be of type ${type}`, ErrorCode.INVALID_VALUE, originalMessage);
    }
}
class MissingValueError extends FormatError {
    constructor(variableId, originalMessage) {
        super(`The intl string context variable "${variableId}" was not provided to the string "${originalMessage}"`, ErrorCode.MISSING_VALUE, originalMessage);
    }
}

/**
 * Verifies if the passed in LocalesOrLocalizationContext is a LocalizationContext.
 *
 * This helps with type safety.
 * @param localesOrLocalizationContext
 */
function isLocalizationContext(localesOrLocalizationContext) {
    return (localesOrLocalizationContext.getLocale !==
        undefined);
}
// AMAZON_CHANGE_END

const UNDEFINED_LOCALE = 'und';
// Map of deprecated/unsupported language tags pointing to their modern/supported counterparts.
const SPECIAL_CASE_LANGUAGES = new Map([
    ['no', 'nb'],
    ['iw', 'he'],
    ['in', 'id'],
    ['ji', 'yi']
]);
const DEFAULT_NUMBERING_SYSTEM = 'latn';
class LocaleUtil {
    /**
     * Get the canonical unicode locale string identifier(s) for the given locale string(s). For more on valid locale
     * string values, check out:
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_argument
     *
     * This method provides some leniency for locale string inputs (e.g., trims any trailing whitespaces, replaces '_' or
     * '-') over the acceptable locale string values documented in:
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_argument
     *
     * This method always sets the locale to use Latin digit shape (unless locale already contains number shape override
     * (e.g., th_TH-u-nu-thai).
     *
     * @param locales A single locale string or a locale string array.
     * @return A single canonical locale string or an array of canonical locale strings.
     */
    static getCanonicalLocaleString(locales) {
        if (Array.isArray(locales)) {
            return locales.map((locale) => LocaleUtil.getCanonicalLocale(LocaleUtil.forLocaleStringInternal(locale)).toLocaleString());
        }
        return locales = LocaleUtil.getCanonicalLocale(LocaleUtil.forLocaleStringInternal(locales)).toLocaleString();
    }
    /**
     * Get the canonicalized locale for the given locale; this does an undefined check and replaces it with the default
     * runtime locale.
     *
     * This method always sets the locale to use Latin digit shape (unless locale already contains number shape override
     * (e.g., th_TH-u-nu-thai).
     *
     * @param locale Intl.Locale object to canonicalize.
     * @return canonicalized Intl.Locale for the given locale.
     */
    // @ts-ignore
    static getCanonicalLocale(locale) {
        if (locale == undefined) {
            // Fetch the default runtime locale
            locale = new Intl.Locale(new Intl.NumberFormat().resolvedOptions().locale);
        }
        locale = LocaleUtil.processLocale(locale);
        if (locale.numberingSystem) {
            // If a numbering system is already present, we do not change the locale.
            return locale;
        }
        // Set default numbering system on the input locale.
        locale = new Intl.Locale(locale, { numberingSystem: DEFAULT_NUMBERING_SYSTEM });
        return locale;
    }
    static processLocale(intlLocale) {
        if (intlLocale.toLocaleString().startsWith(UNDEFINED_LOCALE)) {
            return intlLocale;
        }
        // Check whether the locale is supported.
        const supportedLocales = Intl.NumberFormat.supportedLocalesOf(intlLocale.toLocaleString());
        // If locale is not supported return undefined locale.
        if (supportedLocales.length === 0) {
            // Check for exempted locales.
            const specialLanguage = SPECIAL_CASE_LANGUAGES.get(intlLocale.language);
            if (specialLanguage) {
                return new Intl.Locale(intlLocale.baseName.replace(intlLocale.language, specialLanguage));
            }
            console.log(`Unsupported locale: ${intlLocale.toLocaleString()}`);
            return new Intl.Locale(UNDEFINED_LOCALE);
        }
        return new Intl.Locale(supportedLocales[0]);
    }
    /**
     * Given the locale string identifier, returns the corresponding Intl.Locale instance.
     *
     * Provides some leniency for locale string inputs over the acceptable locale string values documented in:
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_argument:
     *
     * * Trims any trailing whitespaces
     * * replaces all occurrences of '_' with '-'.
     */
    static forLocaleStringInternal(locale) {
        return locale ? new Intl.Locale(locale.trim().replace(/_/g, '-')) : undefined;
    }
}

var src = /*#__PURE__*/Object.freeze({
    __proto__: null,
    LocaleUtil: LocaleUtil
});

var PART_TYPE;
(function (PART_TYPE) {
    PART_TYPE[PART_TYPE["literal"] = 0] = "literal";
    PART_TYPE[PART_TYPE["object"] = 1] = "object";
})(PART_TYPE || (PART_TYPE = {}));
function mergeLiteral(parts) {
    if (parts.length < 2) {
        return parts;
    }
    return parts.reduce((all, part) => {
        const lastPart = all[all.length - 1];
        if (!lastPart ||
            lastPart.type !== PART_TYPE.literal ||
            part.type !== PART_TYPE.literal) {
            all.push(part);
        }
        else {
            lastPart.value += part.value;
        }
        return all;
    }, []);
}
function isFormatXMLElementFn(
// AMAZON_CHANGE_START
el
// AMAZON_CHANGE_END
) {
    return typeof el === 'function';
}
// AMAZON_CHANGE_START
function isFormatArgElementFn(el) {
    return typeof el === 'function';
}
function formatToVdomParts(els, localesOrLocalizationContext, formatters, formats, hyperscriptRuntime, values, currentPluralValue, 
// For debugging
originalMessage, 
// (Optional) Already canonicalized locale(s). Avoids redundant LocaleUtil.getCanonicalizedLocale call overheads.
canonicalLocales) {
    const formattedParts = formatToParts(els, localesOrLocalizationContext, formatters, formats, values, currentPluralValue, originalMessage, hyperscriptRuntime, canonicalLocales);
    return formattedParts.map(part => {
        return part.value;
    });
}
// AMAZON_CHANGE_END
// TODO(skeleton): add skeleton support
function formatToParts(els, 
// AMAZON_CHANGE_START
localesOrLocalizationContext, 
// AMAZON_CHANGE_END
formatters, formats, values, currentPluralValue, 
// For debugging
originalMessage, 
// AMAZON_CHANGE_START
hyperscriptRuntime, 
// (Optional) Already canonicalized locale(s). Avoids redundant getCanonicalizeLocale call overheads.
canonicalLocales
// AMAZON_CHANGE_END
) {
    // Hot path for straight simple msg translations
    if (els.length === 1 && isLiteralElement(els[0])) {
        return [
            {
                type: PART_TYPE.literal,
                value: els[0].value,
            },
        ];
    }
    // AMAZON_CHANGE_START
    let localizationContext;
    let locales;
    let timeZoneID;
    if (isLocalizationContext(localesOrLocalizationContext)) {
        localizationContext = localesOrLocalizationContext;
        locales = localesOrLocalizationContext.getLocale();
        timeZoneID = localesOrLocalizationContext.getTimeZone();
    }
    else {
        locales = localesOrLocalizationContext;
    }
    locales = canonicalLocales ? canonicalLocales : LocaleUtil.getCanonicalLocaleString(locales);
    // AMAZON_CHANGE_END
    const result = [];
    for (const el of els) {
        // Exit early for string parts.
        if (isLiteralElement(el)) {
            result.push({
                type: PART_TYPE.literal,
                value: el.value,
            });
            continue;
        }
        // TODO: should this part be literal type?
        // Replace `#` in plural rules with the actual numeric value.
        if (isPoundElement(el)) {
            if (typeof currentPluralValue === 'number') {
                result.push({
                    type: PART_TYPE.literal,
                    value: formatters.getNumberFormat(locales).format(currentPluralValue),
                });
            }
            continue;
        }
        const { value: varName } = el;
        // AMAZON_CHANGE_START
        if (!values) {
            throw new MissingValueError(varName, originalMessage);
        }
        // AMAZON_CHANGE_END
        if (isTagElement(el)) {
            // tslint:disable-next-line:no-shadowed-variable
            const { children, value } = el;
            let formatFn = values[value];
            // AMAZON_CHANGE_START
            if (hyperscriptRuntime) {
                // Using identity format function in case of external hyperscript function.
                formatFn = (tokens) => tokens;
            }
            // AMAZON_CHANGE_END
            if (!isFormatXMLElementFn(formatFn)) {
                throw new InvalidValueTypeError(value, 'function', originalMessage);
            }
            const parts = formatToParts(children, 
            // AMAZON_CHANGE_START
            localesOrLocalizationContext, 
            // AMAZON_CHANGE_END
            formatters, formats, values, currentPluralValue, originalMessage, hyperscriptRuntime, 
            // AMAZON_CHANGE_START
            locales // Locale already canonicalized
            // AMAZON_CHANGE_END
            );
            let chunks = formatFn(parts.map(p => p.value));
            if (!Array.isArray(chunks)) {
                chunks = [chunks];
            }
            // AMAZON_CHANGE_START
            if (hyperscriptRuntime) {
                // Create VDOM for this tag element.
                result.push({
                    type: PART_TYPE.object,
                    value: hyperscriptRuntime.createElement(value, {}, ...chunks),
                });
                // AMAZON_CHANGE_END
            }
            else {
                result.push(...chunks.map((c) => {
                    return {
                        type: typeof c === 'string' ? PART_TYPE.literal : PART_TYPE.object,
                        value: c,
                    };
                }));
            }
            continue;
        }
        // Enforce that all required values are provided by the caller.
        if (!(values && varName in values)) {
            throw new MissingValueError(varName, originalMessage);
        }
        let value = values[varName];
        if (isArgumentElement(el)) {
            if (!value || typeof value === 'string' || typeof value === 'number') {
                value =
                    typeof value === 'string' || typeof value === 'number'
                        ? String(value)
                        : '';
                // AMAZON_CHANGE_START
                // We have received formatting function to style this argument.
            }
            else if (isFormatArgElementFn(value)) {
                value = value(hyperscriptRuntime, {}, []);
            }
            // AMAZON_CHANGE_END
            result.push({
                type: typeof value === 'string' ? PART_TYPE.literal : PART_TYPE.object,
                value,
            });
            continue;
        }
        // Recursively format plural and select parts' option — which can be a
        // nested pattern structure. The choosing of the option to use is
        // abstracted-by and delegated-to the part helper object.
        if (isDateElement(el)) {
            const style = typeof el.style === 'string'
                ? formats.date[el.style]
                : isDateTimeSkeleton(el.style)
                    ? el.style.parsedOptions
                    : // AMAZON_CHANGE_START
                        {};
            if (timeZoneID !== undefined) {
                style.timeZone = timeZoneID;
            }
            // AMAZON_CHANGE_END
            result.push({
                type: PART_TYPE.literal,
                value: formatters
                    .getDateTimeFormat(locales, style)
                    .format(value),
            });
            continue;
        }
        if (isTimeElement(el)) {
            const style = typeof el.style === 'string'
                ? formats.time[el.style]
                : isDateTimeSkeleton(el.style)
                    ? el.style.parsedOptions
                    : formats.time.medium;
            // AMAZON_CHANGE_START
            if (timeZoneID !== undefined) {
                style.timeZone = timeZoneID;
            }
            // AMAZON_CHANGE_END
            result.push({
                type: PART_TYPE.literal,
                value: formatters
                    .getDateTimeFormat(locales, style)
                    .format(value),
            });
            continue;
        }
        if (isNumberElement(el)) {
            const style = typeof el.style === 'string'
                ? formats.number[el.style]
                : isNumberSkeleton(el.style)
                    ? el.style.parsedOptions
                    : undefined;
            if (style && style.scale) {
                value =
                    value *
                        (style.scale || 1);
            }
            result.push({
                type: PART_TYPE.literal,
                value: formatters
                    .getNumberFormat(locales, style)
                    .format(value),
            });
            continue;
        }
        // AMAZON_CHANGE_START
        if (isPriceElement(el)) {
            const style = typeof el.style === 'string' ? formats.price[el.style] : undefined;
            const formattedPrice = formatters
                .getPriceFormat(localizationContext || locales, style)
                .format(value, style);
            result.push({
                type: typeof formattedPrice === 'string'
                    ? PART_TYPE.literal
                    : PART_TYPE.object,
                value: formattedPrice,
            });
            continue;
        }
        // AMAZON_CHANGE_END
        if (isSelectElement(el)) {
            const opt = el.options[value] || el.options.other;
            if (!opt) {
                throw new InvalidValueError(el.value, value, Object.keys(el.options), originalMessage);
            }
            // AMAZON_CHANGE_START
            result.push(...formatToParts(opt.value, localesOrLocalizationContext, formatters, formats, values, currentPluralValue, originalMessage, hyperscriptRuntime, locales // Locale already canonicalized
            ));
            // AMAZON_CHANGE_END
            continue;
        }
        if (isPluralElement(el)) {
            let opt = el.options[`=${value}`];
            if (!opt) {
                if (!Intl.PluralRules) {
                    throw new FormatError(`Intl.PluralRules is not available in this environment.
Try polyfilling it using "@formatjs/intl-pluralrules"
`, ErrorCode.MISSING_INTL_API, originalMessage);
                }
                const rule = formatters
                    .getPluralRules(locales, { type: el.pluralType })
                    .select(value - (el.offset || 0));
                opt = el.options[rule] || el.options.other;
            }
            if (!opt) {
                throw new InvalidValueError(el.value, value, Object.keys(el.options), originalMessage);
            }
            // AMAZON_CHANGE_START
            result.push(...formatToParts(opt.value, localesOrLocalizationContext, formatters, formats, values, value - (el.offset || 0), originalMessage, hyperscriptRuntime, locales // Locale already canonicalized
            ));
            // AMAZON_CHANGE_END
            continue;
        }
    }
    // AMAZON_CHANGE_START
    // Return individual formatted VDOM parts.
    if (hyperscriptRuntime) {
        return result;
    }
    // AMAZON_CHANGE_END
    return mergeLiteral(result);
}
// AMAZON_CHANGE_END

class Marketplace {
    /**
     * Method for the platform to override to have the obfuscated marketplace id retrieval of the marketplace
     * based on the platform, and return obfuscated marketplace id of the marketplace
     * @return {undefined | string} undefined
     */
    getObfuscatedMarketplaceId() {
        return undefined;
    }
    /**
     * Method for the platform to override to have the default locale retrieval of the marketplace based on
     * the platform, and return default locale of the marketplace
     *
     * @return {undefined | string} undefined
     */
    getDefaultLocale() {
        return Intl.DateTimeFormat().resolvedOptions().locale;
    }
    /**
     * Method for the platform to override to have the default currency retrieval of the marketplace based on
     * the platform, and return default currency of the marketplace
     *
     * @return {undefined | string} undefined
     */
    getDefaultCurrency() {
        return undefined;
    }
}

/**
 * EmptyMarketplace with undefined marketplaceId, locale, and currencyCode
 */
class EmptyMarketplace extends Marketplace {
}
/**
 * This class providing a provider generate EmptyMarketplace,
 * as default marketplace provider in marketplace-creator
 */
// tslint:disable-next-line:max-classes-per-file
class EmptyMarketplaceProvider {
    getMarketplace() {
        return EmptyMarketplaceProvider.emptyMarketplace;
    }
}
EmptyMarketplaceProvider.emptyMarketplace = new EmptyMarketplace();

class MarketplaceCreator {
    /**
     * Set the customMarketplaceProvider as the singleton marketplaceProvider.
     * @param {MarketplaceProvider} customMarketplaceProvider - User specified marketplace marketplaceProvider
     */
    static setProvider(customMarketplaceProvider) {
        MarketplaceCreator.marketplaceProvider = customMarketplaceProvider;
    }
    /**
     * Create singleton Marketplace marketplaceProvider which creates marketplace configuration.
     * Return the singleton marketplaceProvider if it exists.
     * Otherwise, set an EmptyMarketplaceProvider object as singleton marketplaceProvider and return it.
     * @return {MarketplaceProvider} the singleton MarketplaceProvider marketplaceProvider
     */
    static provider() {
        if (!MarketplaceCreator.marketplaceProvider) {
            MarketplaceCreator.marketplaceProvider = new EmptyMarketplaceProvider();
        }
        return MarketplaceCreator.marketplaceProvider;
    }
}
MarketplaceCreator.marketplaceProvider = undefined;

const MARKETPLACE_DEFAULTS = {
    // NA marketplaces
    A2Q3Y263D00KWC: {
        name: 'BR',
        locale: 'pt-BR',
        currency: 'BRL'
    },
    AZXD3QD5B39HD: {
        name: 'BR_DEVO',
        locale: 'pt-BR',
        currency: 'BRL'
    },
    A2EUQ1WTGCTBG2: {
        name: 'CA',
        locale: 'en-CA',
        currency: 'CAD'
    },
    A2WDZJZ5TCRMLH: {
        name: 'CL',
        locale: 'es-CL',
        currency: 'CLP'
    },
    A2IDBVIE7EAGN4: {
        name: 'CL_DEVO',
        locale: 'es-CL',
        currency: 'CLP'
    },
    A1ZXRLSQW2V54D: {
        name: 'CO',
        locale: 'es-CO',
        currency: 'COP'
    },
    A1NOHO22DE3OHQ: {
        name: 'CO_DEVO',
        locale: 'es-CO',
        currency: 'COP'
    },
    A1AM78C64UM0Y8: {
        name: 'MX',
        locale: 'es-MX',
        currency: 'MXN'
    },
    A3P3J5A7D2ZVXI: {
        name: 'MX_DEVO',
        locale: 'es-MX',
        currency: 'MXN'
    },
    ATVPDKIKX0DER: {
        name: 'US',
        locale: 'en-US',
        currency: 'USD'
    },
    AGWSWK15IEJJ7: {
        name: 'PYOP_US_PROD',
        locale: 'en-US',
        currency: 'USD'
    },
    A3BXB0YN3XH17H: {
        name: 'PYOP_US_PROD_SANDBOX',
        locale: 'en-US',
        currency: 'USD'
    },
    A35A4JO734ER04: {
        name: 'PYOP_US_DEVO',
        locale: 'en-US',
        currency: 'USD'
    },
    A341L3VCFKNMIY: {
        name: 'PYOP_US_DEVO_SANDBOX',
        locale: 'en-US',
        currency: 'USD'
    },
    A2CE4MDCUAASDP: {
        name: 'PYOP_CA_PROD',
        locale: 'en-CA',
        currency: 'CAD'
    },
    A2VJI655WT2I0B: {
        name: 'PYOP_CA_PROD_SANDBOX',
        locale: 'en-CA',
        currency: 'CAD'
    },
    A1NWP2E4EKQP94: {
        name: 'PYOP_CA_DEVO',
        locale: 'en-CA',
        currency: 'CAD'
    },
    ASBG27DKM7EX6: {
        name: 'PYOP_CA_DEVO_SANDBOX',
        locale: 'en-CA',
        currency: 'CAD'
    },
    // EU marketplaces
    A2VIGQ35RCS4UG: {
        name: 'AE',
        locale: 'en-AE',
        currency: 'AED'
    },
    A34GYYCZVDBSIK: {
        name: 'AE_DEVO',
        locale: 'en-AE',
        currency: 'AED'
    },
    A1PA6795UKMFR9: {
        name: 'DE',
        locale: 'de-DE',
        currency: 'EUR'
    },
    AMEN7PMS3EDWL: {
        name: 'BE',
        locale: 'fr-FR',
        currency: 'EUR'
    },
    A1YFOMBPFBRY22: {
        name: 'BE_DEVO',
        locale: 'fr-FR',
        currency: 'EUR'
    },
    ARBP9OOSHTCHU: {
        name: 'EG',
        locale: 'en-AE',
        currency: 'EGP'
    },
    AUJPM9XGFJRC7: {
        name: 'EG_DEVO',
        locale: 'en-AE',
        currency: 'EGP'
    },
    A1RKKUPIHCS9HS: {
        name: 'ES',
        locale: 'es-ES',
        currency: 'EUR'
    },
    AJZF8LZ1EJVJN: {
        name: 'ES_DEVO',
        locale: 'es-ES',
        currency: 'EUR'
    },
    A13V1IB3VIYZZH: {
        name: 'FR',
        locale: 'fr-FR',
        currency: 'EUR'
    },
    A1F83G8C2ARO7P: {
        name: 'GB',
        locale: 'en-GB',
        currency: 'GBP'
    },
    APJ6JRA9NG5V4: {
        name: 'IT',
        locale: 'it-IT',
        currency: 'EUR'
    },
    A3HOBANJMCMD83: {
        name: 'IT_DEVO',
        locale: 'it-IT',
        currency: 'EUR'
    },
    A28R8C7NBKEWEA: {
        name: 'IE',
        locale: 'en-IE',
        currency: 'EUR'
    },
    AQGJ466SKA0F0: {
        name: 'IE_DEVO',
        locale: 'en-IE',
        currency: 'EUR'
    },
    A21TJRUUN4KGV: {
        name: 'IN',
        locale: 'en-IN',
        currency: 'INR'
    },
    A2XZLSVIQ0F4JT: {
        name: 'IN_DEVO',
        locale: 'en-IN',
        currency: 'INR'
    },
    A3OCL2LJ6GDH9T: {
        name: 'NG',
        locale: 'en-NG',
        currency: 'NGN'
    },
    A3PJZM0QIA8QDU: {
        name: 'NG_DEVO',
        locale: 'en-NG',
        currency: 'NGN'
    },
    A1805IZSGTT6HS: {
        name: 'NL',
        locale: 'nl-NL',
        currency: 'EUR'
    },
    A1M3WC0SJ3A38T: {
        name: 'NL_DEVO',
        locale: 'nl-NL',
        currency: 'EUR'
    },
    A1C3SOZRARQ6R3: {
        name: 'PL',
        locale: 'pl-PL',
        currency: 'PLN'
    },
    AKY4K4WKH21YQ: {
        name: 'PL_DEVO',
        locale: 'pl-PL',
        currency: 'PLN'
    },
    A17E79C6D8DWNP: {
        name: 'SA',
        locale: 'en-AE',
        currency: 'SAR'
    },
    A1MQPSGJ6U9Q54: {
        name: 'SA_DEVO',
        locale: 'en-AE',
        currency: 'SAR'
    },
    A2NODRKZP88ZB9: {
        name: 'SE',
        locale: 'sv-SE',
        currency: 'SEK'
    },
    AT614YYYNOC1S: {
        name: 'SE_DEVO',
        locale: 'sv-SE',
        currency: 'SEK'
    },
    A33AVAJ2PDY3EV: {
        name: 'TR',
        locale: 'tr-TR',
        currency: 'TRY'
    },
    A3CQBQD3RGPJR8: {
        name: 'TR_DEVO',
        locale: 'tr-TR',
        currency: 'TRY'
    },
    AE08WJ6YKNBMC: {
        name: 'ZA',
        locale: 'en-ZA',
        currency: 'ZAR'
    },
    A3TFQ1G9L8TQ5L: {
        name: 'ZA_DEVO',
        locale: 'en-ZA',
        currency: 'ZAR'
    },
    AZAJMM36N6WQL: {
        name: 'PYOP_UK_PROD',
        locale: 'en-GB',
        currency: 'GBP'
    },
    A3M3RRFO9XDT2G: {
        name: 'PYOP_UK_PROD_SANDBOX',
        locale: 'en-GB',
        currency: 'GBP'
    },
    A2UWQKHEFGNQY3: {
        name: 'PYOP_UK_DEVO',
        locale: 'en-GB',
        currency: 'GBP'
    },
    A2V84NPDZD3ZEH: {
        name: 'PYOP_UK_DEVO_SANDBOX',
        locale: 'en-GB',
        currency: 'GBP'
    },
    A53RDEWN57UU5: {
        name: 'PYOP_DE_PROD',
        locale: 'de-DE',
        currency: 'EUR'
    },
    A1G8446IYHA4MR: {
        name: 'PYOP_DE_PROD_SANDBOX',
        locale: 'de-DE',
        currency: 'EUR'
    },
    A34RX6LWLXD2KA: {
        name: 'PYOP_DE_DEVO',
        locale: 'de-DE',
        currency: 'EUR'
    },
    A225AO9WA6YYOF: {
        name: 'PYOP_DE_DEVO_SANDBOX',
        locale: 'de-DE',
        currency: 'EUR'
    },
    A3FDG49KKM823Y: {
        name: 'PYOP_IN_PROD',
        locale: 'en-IN',
        currency: 'INR'
    },
    A1IAUC0NN979H6: {
        name: 'PYOP_IN_PROD_SANDBOX',
        locale: 'en-IN',
        currency: 'INR'
    },
    A18VPDB9UTK24D: {
        name: 'PYOP_IN_DEVO',
        locale: 'en-IN',
        currency: 'INR'
    },
    A16Z2XWZJTFS70: {
        name: 'PYOP_IN_DEVO_SANDBOX',
        locale: 'en-IN',
        currency: 'INR'
    },
    // FE marketplaces
    A39IBJ37TRP1C6: {
        name: 'AU',
        locale: 'en-AU',
        currency: 'AUD'
    },
    A1RNPCQ4K8U27I: {
        name: 'AU_DEVO',
        locale: 'en-AU',
        currency: 'AUD'
    },
    A1VC38T7YXB528: {
        name: 'JP',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    AD2EMQ3L3PG8S: {
        name: 'RU',
        locale: 'ru-RU',
        currency: 'RUB'
    },
    A38NPJYVS5YHNH: {
        name: 'RU_DEVO',
        locale: 'ru-RU',
        currency: 'RUB'
    },
    A19VAU5U5O7RUS: {
        name: 'SG',
        locale: 'en-SG',
        currency: 'SGD'
    },
    A1DQ6JV7I20JSG: {
        name: 'SG_DEVO',
        locale: 'en-SG',
        currency: 'SGD'
    },
    A75P1MG8VMU51: {
        name: 'PYOP_JP_PROD',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    A31YDYE76E6TCP: {
        name: 'PYOP_JP_PROD_SANDBOX',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    A2EFPHRCZ60KI3: {
        name: 'PYOP_JP_DEVO',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    A1U1OSY46WLA42: {
        name: 'PYOP_JP_DEVO_SANDBOX',
        locale: 'ja-JP',
        currency: 'JPY'
    },
    // CN Marketplaces
    AAHKV2X7AFYLW: {
        name: 'CN',
        locale: 'zh-CN',
        currency: 'CNY'
    }
};

/**
 * This class is based on the Java DefaultMarketplaceImpl,
 * providing default marketplace specific implementation.
 */
class DefaultMarketplaceImpl extends Marketplace {
    /**
     * This constructor enables consumers to override the default locale and default currency for the valid amazon
     * marketplace IDs.
     * If the default locale or default currency is passed as undefined for the valid obfuscatedMarketplaceId as key,
     * then the default locale or default currency will be retrieved from the MARKETPLACE_DEFAULTS mapping.
     * If it is a invalid amazon retail marketplace ID, and will have, the associated values will be assigned as undefined
     *
     * @param {undefined | string} obfuscatedMarketplaceIdParam - obfuscated marketplace id of the marketplace
     * @param {undefined | string} defaultLocaleParam - default locale of the marketplace
     * @param {undefined | string} defaultCurrencyParam - default currency of the marketplace
     */
    constructor(obfuscatedMarketplaceId, defaultLocale, defaultCurrency) {
        super();
        // the obfuscatedMarketplaceId must be presented to look up the default locale and currency
        if (obfuscatedMarketplaceId) {
            this.obfuscatedMarketplaceId = obfuscatedMarketplaceId;
            const defaultValues = MARKETPLACE_DEFAULTS[obfuscatedMarketplaceId];
            this.defaultLocale = defaultLocale
                ? defaultLocale
                : defaultValues
                    ? defaultValues.locale
                    : super.getDefaultLocale();
            this.defaultCurrency = defaultCurrency
                ? defaultCurrency
                : defaultValues
                    ? defaultValues.currency
                    : undefined;
        }
        else {
            this.obfuscatedMarketplaceId = undefined;
            this.defaultLocale = undefined;
            this.defaultCurrency = undefined;
        }
    }
    /**
     * get the default obfuscatedMarketplaceId of the marketplace with default implementation behavior
     * @return {undefined | string} obfuscatedMarketplaceId - default obfuscatedMarketplaceId of the marketplace
     */
    getObfuscatedMarketplaceId() {
        return this.obfuscatedMarketplaceId;
    }
    /**
     * get the default locale of the marketplace with default implementation behavior
     * @return {undefined | string} defaultLocale - default locale of the marketplace
     */
    getDefaultLocale() {
        return this.defaultLocale;
    }
    /**
     * get the default currency of the marketplace with default implementation behavior
     * @return {undefined | string} defaultCurrency - default currency of the marketplace
     */
    getDefaultCurrency() {
        return this.defaultCurrency;
    }
    toString() {
        return `Marketplace[obfuscatedMarketplaceId=${this.getObfuscatedMarketplaceId()},defaultLocale=${this.getDefaultLocale()},defaultCurrency=${this.getDefaultCurrency()}]`;
    }
    /**
     * Returns a map of all Amazon Marketplaces and their corresponding ObfuscatedIds.
     * e.g. GB = A1F83G8C2ARO7P
     *      AE_DEVO = A34GYYCZVDBSIK
     *
     * @return {Map<string, string>} map of marketplaces and their obfuscated ids.
     */
    static getObfuscatedMarketplaceIdMap() {
        const map = new Map();
        for (const marketplaceId of Object.keys(MARKETPLACE_DEFAULTS)) {
            map.set(MARKETPLACE_DEFAULTS[marketplaceId].name, marketplaceId);
        }
        return map;
    }
}

var dist$1 = {};

var configType = {};

Object.defineProperty(configType, "__esModule", { value: true });
configType.ConfigType = void 0;
/**
 * Enum to identify an I18n Configuration.
 * All available configurations will be registered here.
 */
class ConfigType {
    constructor(_name, _parent) {
        this._name = _name;
        this._parent = _parent;
    }
    /**
     * Returns the parent of the {@link ConfigType} enum,
     * or undefined if no parent exists.
     */
    get parent() {
        return this._parent;
    }
    toString() {
        return this._name;
    }
}
configType.ConfigType = ConfigType;
/**
 * Reads the mapping from MarketplaceID to CX Tuple.
 */
ConfigType.MARKETPLACEID_TO_CX_TUPLE = new ConfigType('MARKETPLACEID_TO_CX_TUPLE');
/**
 * Reads Price Format related configurations used in IU libraries.
 * This is the base for all Price Format type configurations.
 */
ConfigType.PRICE_FORMAT = new ConfigType('PRICE_FORMAT');
/**
 * Reads Price Format related configurations used in IU libraries.
 * This setting applies configs specifically to cx-family retail.
 * It is a sub-type of PRICE_FORMAT.
 */
ConfigType.RETAIL_PRICE_FORMAT = new ConfigType('RETAIL_PRICE_FORMAT', ConfigType.PRICE_FORMAT);
/**
* Reads Points Format related configurations used in IU libraries.
*/
ConfigType.POINTS_FORMAT = new ConfigType('POINTS_FORMAT');
/**
 * Reads the mapping from CXTuple to equivalent I18nDomain.
 */
ConfigType.CX_TUPLE_TO_I18N_DOMAIN = new ConfigType('CX_TUPLE_TO_I18N_DOMAIN');
/**
 * Reads the mapping from CXTuple to corresponding default languages and currencies.
 */
ConfigType.CX_TUPLE_TO_DEFAULT_PREFERENCES = new ConfigType('CX_TUPLE_TO_DEFAULT_PREFERENCES');

var configName = {};

Object.defineProperty(configName, "__esModule", { value: true });
configName.ConfigName = void 0;
/**
 * Interface to define a specific configuration to query.
 *
 * Each configuration type will have it's own implementation,
 * providing enum values of available configurations to query.
 */
class ConfigName {
    constructor(_name, _returnType) {
        this._name = _name;
        this._returnType = _returnType;
    }
    /**
     * Defines what type of data will be return from the configuration query.
     * This method can be used as a helper to identify how to read the response data.
     *
     * @return {@link DataType}
     */
    getReturnType() {
        return this._returnType;
    }
    /**
     * Return the printable string of this ConfigName
     */
    toString() {
        return this._name;
    }
}
configName.ConfigName = ConfigName;

var queryOutput = {};

Object.defineProperty(queryOutput, "__esModule", { value: true });
queryOutput.QueryOutput = void 0;
/**
 * Interface to define an output to a configuration query.
 *
 * Each configuration type will have it's own implementation,
 * providing enum values of the available outputs of a query.
 */
class QueryOutput {
    constructor(_name, _returnType) {
        this._name = _name;
        this._returnType = _returnType;
    }
    /**
     * Defines that data type the query output value is stored in.
     * This method can be used as a helper to identify how to read the query output.
     *
     * @return {@link DataType}
     */
    getReturnType() {
        return this._returnType;
    }
    toString() {
        return this._name;
    }
}
queryOutput.QueryOutput = QueryOutput;

var i18nConfiguration = {};

var configurationData = {};

var utils = {};

Object.defineProperty(utils, "__esModule", { value: true });
utils.isNullOrUndefined = void 0;
function isNullOrUndefined(data) {
    return data == null || data == undefined;
}
utils.isNullOrUndefined = isNullOrUndefined;

Object.defineProperty(configurationData, "__esModule", { value: true });
configurationData.ConfigurationData = void 0;
const utils_1 = utils;
/**
 * Wrapper class to wrap around Configuration Data returned from a query.
 */
class ConfigurationData {
    constructor(config) {
        this._configs = !utils_1.isNullOrUndefined(config) ? config : new Map();
    }
    /**
     * Extracts the requested configuration field value in String format.
     * Any configuration that is not stored in String format will be converted to a String.
     *
     * @param configName    name of the config field to extract
     *
     * @return Nullable configuration value,
     *         null if configName doesn't exist
     */
    getStringValue(configName) {
        const val = this._configs.get(configName);
        return !utils_1.isNullOrUndefined(val) ? String(val) : null;
    }
    /**
     * Extracts the requested configuration field value in number format.
     * The configuration can be stored in either number or String format.
     *
     * @param configName    name of the config field to extract
     *
     * @return Nullable configuration value
     *         null if configName doesn't exist
     */
    getNumberValue(configName) {
        let val = this._configs.get(configName);
        if (utils_1.isNullOrUndefined(val)) {
            return null;
        }
        val = Number(val);
        if (Number.isNaN(val)) {
            throw new Error(`The value of ${configName} cannot be converted to a number`);
        }
        return val;
    }
    /**
     * Extracts the requested configuration field value in boolean format.
     * The configuration can be stored in either boolean or String format.
     *
     * @param configName    name of the config field to extract
     *
     * @return Nullable configuration value
     *         null if configName doesn't exist
     */
    getBooleanValue(configName) {
        const val = this._configs.get(configName);
        return !utils_1.isNullOrUndefined(val) ? 'true' === String(val).toLowerCase() : null;
    }
    /**
     * Extracts the requested configuration field value in enum format.
     *
     * @param enumType  the enum type that the config value belongs to
     * @param configName    name of the config field to extract
     *
     * @return Nullable configuration value
     *         null if configName doesn't exist
     */
    getEnumValue(enumType, configName) {
        const val = this._configs.get(configName);
        if (utils_1.isNullOrUndefined(val)) {
            return null;
        }
        let keys = Object.keys(enumType).filter(x => enumType[x] == val);
        return keys.length > 0 ? enumType[keys[0]] : null;
    }
    /**
     * Determines whether the Configuration Data stores a primitive or complex data type.
     * ConfigurationData will be considered as Complex if it contains more than 1 data item.
     *
     * @return true if primitive, false otherwise.
     */
    isPrimitiveData() {
        return this._configs.size <= 1;
    }
    /**
     * Retrieves the Config name of the primitive data of the ConfigurationData object.
     * If the object contains a complex Data Type instead undefined will be returned.
     *
     * @return Underlying Primitive Data name, or null if
     *         Data is complex or non existing.
     */
    getPrimitiveDataConfigName() {
        return this._configs.size == 1 ? this._configs.keys().next().value : null;
    }
    isEmpty() {
        if (this._configs.size === 0) {
            return true;
        }
        for (const val of this._configs.values()) {
            if (!utils_1.isNullOrUndefined(val)) {
                return false;
            }
        }
        return true;
    }
    merge(data) {
        for (const entry of data._configs.entries()) {
            this._configs.set(entry[0], entry[1]);
        }
        return this;
    }
    toString() {
        let res = '{';
        let configCount = 0;
        for (const entry of this._configs.entries()) {
            res += `${entry[0]}:${entry[1]}`;
            configCount++;
            if (configCount != this._configs.size) {
                res += ',';
            }
        }
        res += '}';
        return res;
    }
}
configurationData.ConfigurationData = ConfigurationData;

Object.defineProperty(i18nConfiguration, "__esModule", { value: true });
i18nConfiguration.I18nConfiguration = void 0;
const configuration_data_1 = configurationData;
/**
 * Wrapper class to extract I18n Configuration query QueryOutputs.
 * If the query result is a primitive data value, then the data can be extracted directly,
 * otherwise it can be extracted through the {@link ConfigurationData} object.
 */
class I18nConfiguration {
    constructor(configData) {
        this._configData = configData;
        this._isPrimitiveDataResult = configData.isPrimitiveData();
    }
    /**
     * Extracts the {@link ConfigurationData} returned from the query.
     *
     * If no configuration data was returned from the query then an empty
     * object (object without underlying data) will be returned.
     *
     * @return configuration data
     */
    get configData() {
        return this._configData;
    }
    /**
     * Extracts the resolved configuration in String format. This method is only call-able
     * if the retrieved configuration is a primitive data type. if this method is called on an object
     * then an exception will be thrown.
     *
     * Any primitive data type that is not already of string format will be converted to a String.
     *
     * @return Resolved Configuration in String format.
     *
     * @throws Error    Thrown if called on a non-primitive configuration value
     */
    getStringValue() {
        this.validatePrimitiveResult('getStringValue');
        return this._configData.getStringValue(this._configData.getPrimitiveDataConfigName());
    }
    /**
     * Extracts the resolved configuration in int format. This method is only call-able
     * if the retrieved configuration is a primitive data type. if this method is called on an object
     * then an exception will be thrown.
     *
     * The configuration can be stored in either int or String format.
     *
     * @return Resolved Configuration in int format.
     *
     * @throws Error    Thrown if called on a non-primitive configuration value
     */
    getNumberValue() {
        this.validatePrimitiveResult('getNumberValue');
        return this._configData.getNumberValue(this._configData.getPrimitiveDataConfigName());
    }
    /**
     * Extracts the resolved configuration in Boolean format. This method is only call-able
     * if the retrieved configuration is a primitive data type. if this method is called on an object
     * then an exception will be thrown.
     *
     * The configuration can be stored in either boolean or String format.
     *
     * @return Resolved Configuration in boolean format.
     *
     * @throws Error    Thrown if called on a non-primitive configuration value
     */
    getBooleanValue() {
        this.validatePrimitiveResult('getBooleanValue');
        return this._configData.getBooleanValue(this._configData.getPrimitiveDataConfigName());
    }
    /**
     * Extracts the resolved configuration in Enum format. This method is only call-able
     * if the retrieved configuration is a primitive data type. if this method is called on an object
     * then an exception will be thrown.
     *
     * The configuration can be stored in either number or string format based on the enum type.
     *
     * @param enumType the enum type that the config value belongs to
     * @return Resolved Configuration in enum format.
     *
     * @throws Error    Thrown if called on a non-primitive configuration value
     */
    getEnumValue(enumType) {
        this.validatePrimitiveResult("getEnumValue");
        return this._configData.getEnumValue(enumType, this._configData.getPrimitiveDataConfigName());
    }
    /**
     * Determines whether the {@link I18nConfiguration} object contains any data or not.
     *
     * @return true if empty, false otherwise.
     */
    isEmpty() {
        return this._configData.isEmpty();
    }
    validatePrimitiveResult(methodName) {
        if (!this._isPrimitiveDataResult) {
            throw new Error(`Method ${methodName} cannot be called on a configuration of type object.
                Call getConfigData().${methodName}(configName) instead.`);
        }
    }
    toString() {
        return `I18nConfiguration: ${this._configData}`;
    }
}
i18nConfiguration.I18nConfiguration = I18nConfiguration;
/**
 * Return the {@link Builder} instance used to build an {@link I18nConfiguration} object.
 *
 * @return the builder instance
 */
I18nConfiguration.Builder = class {
    constructor() {
        this._configs = new Map();
    }
    /**
     * Adds a configuration to the {@link I18nConfiguration} container.
     *
     * @param configName name of the config to add.
     * @param value value of the config to add.
     *
     * @return builder instance
     */
    addConfiguration(configName, value) {
        this._configs.set(configName, value);
        return this;
    }
    /**
     * Builds the {@link I18nConfiguration} class using the previously
     * added configurations.
     *
     * @return {@link I18nConfiguration} class instance
     */
    build() {
        return new I18nConfiguration(new configuration_data_1.ConfigurationData(this._configs));
    }
};

var dataType = {};

Object.defineProperty(dataType, "__esModule", { value: true });
dataType.DataType = void 0;
/**
 * Enum to indicate a Data Type.
 */
class DataType {
    // @ts-ignore
    constructor(_isPrimitive) {
        this._isPrimitive = _isPrimitive;
    }
    /**
     * Flag to indicate if the data type is primitive or complex.
     *
     * @return true if primitive, false otherwise
     */
    get isPrimitive() {
        return this._isPrimitive;
    }
}
dataType.DataType = DataType;
DataType.STRING = new DataType(true);
DataType.INT = new DataType(true);
DataType.BOOLEAN = new DataType(true);
DataType.CONFIGURATION_DATA = new DataType(false);
DataType.ENUM = new DataType(true);

var i18nArgs = {};

Object.defineProperty(i18nArgs, "__esModule", { value: true });
i18nArgs.I18nArgs = void 0;
/**
 * Interface to define a specific input into a query that will allow building
 * input data using the Builder pattern.
 *
 * Each configuration type will have its own implementation to provide query
 * specific inputs.
 */
class I18nArgs {
    toString() {
        let res = `${this.constructor.name}:{`;
        let argsCount = 0;
        for (const entry of this.getArgsMap().entries()) {
            res += `${entry[0]}:${entry[1]}`;
            argsCount++;
            if (argsCount != this.getArgsMap().size) {
                res += ',';
            }
        }
        res += '}';
        return res;
    }
}
i18nArgs.I18nArgs = I18nArgs;

var queryResolver = {};

Object.defineProperty(queryResolver, "__esModule", { value: true });
queryResolver.QueryResolver = void 0;
/**
 * Base class to resolve configuration queries.
 */
class QueryResolver {
    constructor() {
        this._isInitialized = false;
    }
    /**
     * Validates that the inputs are of expected configType.
     * If not an Error is thrown
     *
     * @param name Name of the specific configuration to query.
     * @param args Input parameters used to resolve the query.
     *
     * @throws Error Thrown if arguments are not of expected type
     */
    validate(name, args) {
        const targetConfigType = this.getTargetConfigType();
        if (name.getConfigType() !== targetConfigType || args.getConfigType() !== targetConfigType) {
            throw new Error(this.getValidationErrorMessage());
        }
    }
    /**
     * Gets the resolver's initialization state.
     *
     * @return true if sucessfully initialized, false otherwise.
     */
    isInitialized() {
        return this._isInitialized;
    }
    setInitializedState(isInitialized) {
        this._isInitialized = isInitialized;
    }
}
queryResolver.QueryResolver = QueryResolver;

var i18nConfigurationProvider = {};

Object.defineProperty(i18nConfigurationProvider, "__esModule", { value: true });
i18nConfigurationProvider.I18nConfigurationProvider = void 0;
const i18n_configuration_1 = i18nConfiguration;
/**
 * Accessor class to retrieve I18n Configurations.
 */
class I18nConfigurationProvider {
    /**
     * Retrieves the requested configuration.
     *
     * @param name Name of the specific configuration to query.
     * @param args Input parameters used to resolve the query.
     *
     * @return Resolved configuration query QueryOutput. If the configuration query did not return a QueryOutput
     *          an empty I18nConfiguration should be returned.
     *
     * @throwns Error when config and args type doesn't match or the resolver is not registered.
     */
    getConfiguration(name, args) {
        this.validate(name, args);
        let configuration;
        try {
            configuration = this.resolveConfiguration(name, args);
        }
        catch (error) {
            console.warn(`Failed to resolve configuration query with name ${name.toString()} and args ${args.toString()}. Error: ${error}`);
            // return empty configuration in case of an error
            configuration = new i18n_configuration_1.I18nConfiguration.Builder().build();
        }
        return configuration;
    }
    /**
     * Validates that the passed arguments belong to the same or related Config Type.
     * If a miss-match is detected an Error is thrown
     *
     * @param name Name of the specific configuration to query.
     * @param args Input parameters used to resolve the query.
     *
     * @throws Error Thrown if miss-match in argument type is detected.
     */
    validate(name, args) {
        const matchDetected = (name.getConfigType() === args.getConfigType() ||
            (args.getConfigType().parent !== undefined && name.getConfigType() === args.getConfigType().parent));
        if (!matchDetected) {
            throw new Error(`Input query args type need to match the config name type.` +
                `Found ${name.getConfigType()} for config name, but ${args.getConfigType()} for config args.`);
        }
    }
}
i18nConfigurationProvider.I18nConfigurationProvider = I18nConfigurationProvider;

var configurationProviderManager = {};

Object.defineProperty(configurationProviderManager, "__esModule", { value: true });
configurationProviderManager.ConfigurationProviderManager = void 0;
/**
 * Class to manage {@link I18nConfigurationProvider} access.
 *
 * This class is designed so that the I18nConfigurationProvider only has to be
 * registered once within an application, and after that the same
 * I18nConfigurationProvider interface is used to retrieve configurations.
 */
class ConfigurationProviderManager {
    constructor(_provider) {
        this._provider = _provider;
    }
    /**
     * Registers the specified Configuration Provider. If a provider was already
     * registered this request is being ignored.
     *
     * @param provider  {@link I18nConfigurationProvider} implementation to use for
     *                  configuration retrieval.
     */
    static register(provider) {
        if (provider === undefined || provider === null) {
            throw new Error('Configuration provider cannot be undefined or null');
        }
        if (this.instance === undefined) {
            this._instance = new ConfigurationProviderManager(provider);
            console.log(`Registered provider ${provider.constructor.name} for configuration resolution.`);
        }
        else {
            console.warn(`Provider ${this.instance._provider.constructor.name} is already registered. `
                + `Ignoring register request for ${provider.constructor.name}`);
        }
    }
    /**
     * Accessor to the registered {@link I18nConfigurationProvider} instance to be
     * used for configuration retrieval.
     *
     * @return registered {@link I18nConfigurationProvider} instance
     */
    static getProvider() {
        if (this.instance !== undefined) {
            return this.instance._provider;
        }
        else {
            console.warn('No provider has been registered yet. Returning undefined.');
        }
        return undefined;
    }
    /**
     * Check whether a {@link I18nConfigurationProvider} has been registered.
     *
     * @return true if the {@link I18nConfigurationProvider} has been registered. Otherwise, false.
     */
    static hasProvider() {
        return this.instance != undefined;
    }
    static get instance() {
        return this._instance;
    }
    static resetInstance() {
        this._instance = undefined;
    }
}
configurationProviderManager.ConfigurationProviderManager = ConfigurationProviderManager;

var marketplaceIdToCxTuple = {};

Object.defineProperty(marketplaceIdToCxTuple, "__esModule", { value: true });
marketplaceIdToCxTuple.MarketplaceIdToCxTuple = void 0;
const config_name_1$4 = configName;
const config_type_1$9 = configType;
const data_type_1$9 = dataType;
/**
 * Defines the Configurations available under the MarketplaceID to CXID
 * mapping table.
 */
class MarketplaceIdToCxTuple extends config_name_1$4.ConfigName {
    constructor(_name, _returnType) {
        super(_name, _returnType);
    }
    getConfigType() {
        return config_type_1$9.ConfigType.MARKETPLACEID_TO_CX_TUPLE;
    }
}
marketplaceIdToCxTuple.MarketplaceIdToCxTuple = MarketplaceIdToCxTuple;
/**
 * cx-id is stored in String format.
 */
MarketplaceIdToCxTuple.CX_ID = new MarketplaceIdToCxTuple('cx_id', data_type_1$9.DataType.STRING);
/**
 * cx-family is stored in String format.
 */
MarketplaceIdToCxTuple.CX_FAMILY = new MarketplaceIdToCxTuple('cx_family', data_type_1$9.DataType.STRING);

var marketplaceIdToCxTupleArgs = {};

Object.defineProperty(marketplaceIdToCxTupleArgs, "__esModule", { value: true });
marketplaceIdToCxTupleArgs.MarketplaceIdToCxTupleArgs = void 0;
const i18n_args_1$4 = i18nArgs;
const config_type_1$8 = configType;
/**
 * Input args to be used for a MarketplaceId to CXID data query.
 */
class MarketplaceIdToCxTupleArgs extends i18n_args_1$4.I18nArgs {
    constructor(_obfuscatedMarketplaceId) {
        super();
        this._obfuscatedMarketplaceId = _obfuscatedMarketplaceId;
    }
    get obfuscatedMarketplaceId() {
        return this._obfuscatedMarketplaceId;
    }
    getConfigType() {
        return config_type_1$8.ConfigType.MARKETPLACEID_TO_CX_TUPLE;
    }
    getArgsMap() {
        return new Map([
            ['obfuscated_marketplace_id', this._obfuscatedMarketplaceId]
        ]);
    }
}
marketplaceIdToCxTupleArgs.MarketplaceIdToCxTupleArgs = MarketplaceIdToCxTupleArgs;
/**
 * Builder class that build the {@link MarketplaceIdToCxIdArgs} instance.
 */
MarketplaceIdToCxTupleArgs.Builder = class {
    /**
     * Sets the ObfuscatedMarketplaceId within the {@link MarketplaceIdToCxIdArgs}
     * container.
     *
     * @param id    Obfuscated Marketplace ID to be used for the config query.
     *
     * @return builder instance
     */
    withObfuscatedMarketplaceId(id) {
        this.obfuscatedMarketplaceId = id;
        return this;
    }
    /**
     * Builds the {@link MarketplaceIdToCxIdArgs} class using the previously set
     * properties.
     *
     * @return {@link MarketplaceIdToCxIdArgs} class instance
     */
    build() {
        return new MarketplaceIdToCxTupleArgs(this.obfuscatedMarketplaceId);
    }
};

var marketplaceIdToCxTupleResult = {};

Object.defineProperty(marketplaceIdToCxTupleResult, "__esModule", { value: true });
marketplaceIdToCxTupleResult.MarketplaceIdToCxTupleResult = void 0;
const query_output_1$4 = queryOutput;
const data_type_1$8 = dataType;
/**
 * Data available as part of a MarketplaceID to CXID query result.
 */
class MarketplaceIdToCxTupleResult extends query_output_1$4.QueryOutput {
}
marketplaceIdToCxTupleResult.MarketplaceIdToCxTupleResult = MarketplaceIdToCxTupleResult;
/**
 * cx-id is stored in String format.
 */
MarketplaceIdToCxTupleResult.CX_ID = new MarketplaceIdToCxTupleResult('cx_id', data_type_1$8.DataType.STRING);
/**
 * cx-family is stored in String format.
 */
MarketplaceIdToCxTupleResult.CX_FAMILY = new MarketplaceIdToCxTupleResult('cx_family', data_type_1$8.DataType.STRING);

var priceFormat = {};

Object.defineProperty(priceFormat, "__esModule", { value: true });
priceFormat.PriceFormat = void 0;
const data_type_1$7 = dataType;
const config_name_1$3 = configName;
const config_type_1$7 = configType;
/**
 * Defines the Configurations available under the PriceFormat table.
 */
class PriceFormat$1 extends config_name_1$3.ConfigName {
    constructor(_name, _returnType) {
        super(_name, _returnType);
    }
    getConfigType() {
        return config_type_1$7.ConfigType.PRICE_FORMAT;
    }
    getReturnType() {
        return this._returnType;
    }
}
priceFormat.PriceFormat = PriceFormat$1;
/**
 * Customizes how the currency symbol and pattern is being displayed.
 */
PriceFormat$1.CURRENCY_FORMAT = new PriceFormat$1('currency_format', data_type_1$7.DataType.CONFIGURATION_DATA);
/**
 * Customizes the currency symbol prefix.
 */
PriceFormat$1.SYMBOL_PREFIX = new PriceFormat$1('symbol_prefix', data_type_1$7.DataType.STRING);
/**
 * Override the currency symbol position
 */
PriceFormat$1.CURRENCY_SYMBOL_POSITION = new PriceFormat$1('currency_symbol_position', data_type_1$7.DataType.ENUM);
/**
 * Customizes the trailing zero display configuration.
 */
PriceFormat$1.TRAILING_ZERO_DISPLAY = new PriceFormat$1('trailing_zero_display', data_type_1$7.DataType.ENUM);

var priceFormatArgs = {};

Object.defineProperty(priceFormatArgs, "__esModule", { value: true });
priceFormatArgs.PriceFormatArgs = void 0;
const i18n_args_1$3 = i18nArgs;
class PriceFormatArgs extends i18n_args_1$3.I18nArgs {
    constructor(_locale, _currency, _cxFamily, _cxId, _cxProgram, _priceFormatType) {
        super();
        this._locale = _locale;
        this._currency = _currency;
        this._cxFamily = _cxFamily;
        this._cxId = _cxId;
        this._cxProgram = _cxProgram;
        this._priceFormatType = _priceFormatType;
    }
    get locale() {
        return this._locale;
    }
    get currency() {
        return this._currency;
    }
    get cxFamily() {
        return this._cxFamily;
    }
    get cxId() {
        return this._cxId;
    }
    get cxProgram() {
        return this._cxProgram;
    }
    get priceFormatType() {
        return this._priceFormatType;
    }
}
priceFormatArgs.PriceFormatArgs = PriceFormatArgs;

var priceFormatArgsBuilder = {};

var retailPriceFormatArgs = {};

Object.defineProperty(retailPriceFormatArgs, "__esModule", { value: true });
retailPriceFormatArgs.RetailPriceFormatArgs = void 0;
const config_type_1$6 = configType;
const price_format_args_1 = priceFormatArgs;
class RetailPriceFormatArgs extends price_format_args_1.PriceFormatArgs {
    constructor(_locale, _currency, _cxFamily, _cxId, _cxProgram, _priceFormatType) {
        super(_locale, _currency, _cxFamily, _cxId, _cxProgram, _priceFormatType);
    }
    getConfigType() {
        return config_type_1$6.ConfigType.RETAIL_PRICE_FORMAT;
    }
    getArgsMap() {
        return new Map([
            ['locale', this._locale],
            ['currency', this._currency],
            ['cx_family', RetailPriceFormatArgs.RETAIL_CX_FAMILY],
            ['cx_id', this._cxId],
            ['cx_program', this._cxProgram],
            ['price_format_type', this._priceFormatType]
        ]);
    }
}
retailPriceFormatArgs.RetailPriceFormatArgs = RetailPriceFormatArgs;
RetailPriceFormatArgs.RETAIL_CX_FAMILY = 'retail';

Object.defineProperty(priceFormatArgsBuilder, "__esModule", { value: true });
priceFormatArgsBuilder.PriceFormatArgsBuilder = void 0;
const retail_price_format_args_1 = retailPriceFormatArgs;
const __1 = dist$1;
class PriceFormatArgsBuilder {
    constructor() {
        this._cxProgram = PriceFormatArgsBuilder.EMPTY_CX_PROGRAM;
    }
    /**
     * Sets the Locale within the {@link PriceFormatArgs} container.
     *
     * @param locale    Locale to be used for the config query.
     *
     * @return builder instance
     */
    withLocale(locale) {
        this._locale = locale;
        return this;
    }
    /**
     * Sets the Currency within the {@link PriceFormatArgs} container.
     *
     * @param currency  Currency to be used for the config query.
     *
     * @return builder instance
     */
    withCurrency(currency) {
        this._currency = currency;
        return this;
    }
    /**
     * Sets the CX-ID within the {@link PriceFormatArgs} container.
     *
     * @param cxId  CX-ID to be used for the config query.
     *
     * @return builder instance
     */
    withCxId(cxId) {
        this._cxId = cxId;
        return this;
    }
    /**
     * Sets the CX-Program within the {@link PriceFormatArgs} container.
     *
     * @param cxProgram  CX-Program to be used for the config query.
     *
     * @return builder instance
     */
    withCxProgram(cxProgram) {
        this._cxProgram = cxProgram;
        return this;
    }
    /**
     * Sets the CX-Family within the {@link PriceFormatArgs} container.
     *
     * @param cxFamily  CX-Family to be used for the config query.
     *
     * @return builder instance
     */
    withCxFamily(cxFamily) {
        this._cxFamily = cxFamily;
        return this;
    }
    /**
     * Sets the Price Format Type within the {@link PriceFormatArgs} container.
     *
     * @param priceFormatType   {@link PriceFormatType} to be used for the config query.
     *
     * @return builder instance
     */
    withPriceFormatType(priceFormatType) {
        this._priceFormatType = priceFormatType;
        return this;
    }
    /**
     * Copies the Arguments from an existing {@link PriceFormatArgs} instance.
     *
     * @param args  {@link PriceFormatArgs} to copy
     *
     * @return builder instance
     */
    fromPriceFormatArgs(arg) {
        return this.withCurrency(arg.currency)
            .withLocale(arg.locale)
            .withCxFamily(arg.cxFamily)
            .withCxId(arg.cxId)
            .withCxProgram(arg.cxProgram)
            .withPriceFormatType(arg.priceFormatType);
    }
    /**
     * Builds a {@link PriceFormatArgs} class type using the previously set
     * properties. The type of the PriceFormatArgs class is defined by the cx family
     *
     * @return {@link PriceFormatArgs} subclass instance by cx-family. If no cx-family is set
     * {@link RetailPriceFormatArgs} will be returned
     */
    build() {
        //if no cx family is passed use retail by default
        if (__1.isNullOrUndefined(this._cxFamily) || retail_price_format_args_1.RetailPriceFormatArgs.RETAIL_CX_FAMILY === this._cxFamily) {
            return new retail_price_format_args_1.RetailPriceFormatArgs(this._locale, this._currency, this._cxFamily, this._cxId, this._cxProgram, this._priceFormatType);
        }
        else {
            throw new Error(`CX family ${this._cxFamily} is currently not supported.`);
        }
    }
}
priceFormatArgsBuilder.PriceFormatArgsBuilder = PriceFormatArgsBuilder;
PriceFormatArgsBuilder.EMPTY_CX_PROGRAM = '[]';

var priceFormatType_enum = {};

(function (exports) {
Object.defineProperty(exports, "__esModule", { value: true });
exports.PriceFormatType = void 0;
(function (PriceFormatType) {
    /**
     * Displays price with 3 letter ISO 4217 currency code.
     *
     * Example: USD1,234.56
     */
    PriceFormatType["ISO"] = "iso";
    /**
     * Display price using currency long name.
     *
     * Example: 1,234.56 US Dollars
     */
    PriceFormatType["PLURAL"] = "plural";
    /**
     * Display price using Amazon specific custom symbol(wherever applicable).
     * If no custom symbol is defined then CLDR default formatting will be applied.
     *
     * Example: CDN$1,234.56
     */
    PriceFormatType["AMAZON_SYMBOL"] = "amazonSymbol";
    /**
     * Display price without decimal places and spaces.
     *
     * Example: $1234
     */
    PriceFormatType["COMPACT"] = "compact";
    /**
     * Display price used within sentences.
     *
     * Example: $1,000,000
     */
    PriceFormatType["IN_SENTENCE"] = "inSentence";
})(exports.PriceFormatType || (exports.PriceFormatType = {}));

}(priceFormatType_enum));

var priceFormatResult = {};

Object.defineProperty(priceFormatResult, "__esModule", { value: true });
priceFormatResult.PriceFormatResult = void 0;
const data_type_1$6 = dataType;
const query_output_1$3 = queryOutput;
/**
 * Data available as part of a Price Format query result.
 */
class PriceFormatResult extends query_output_1$3.QueryOutput {
}
priceFormatResult.PriceFormatResult = PriceFormatResult;
/**
 * Specifies the custom symbol to be used as the symbol override for the currency.
 */
PriceFormatResult.SYMBOL = new PriceFormatResult('symbol', data_type_1$6.DataType.STRING);
/**
 * Specify whether a SPACE is required between the symbol and price in price format pattern.
 */
PriceFormatResult.INSERT_SPACE_IN_PATTERN = new PriceFormatResult('insert_space_in_pattern', data_type_1$6.DataType.BOOLEAN);
/**
 * Custom pattern to be used for formatting the price.
 */
PriceFormatResult.FORMAT_PATTERN = new PriceFormatResult('format_pattern', data_type_1$6.DataType.STRING);
/**
 * Override the currency symbol prefix.
 */
PriceFormatResult.SYMBOL_PREFIX = new PriceFormatResult('symbol_prefix', data_type_1$6.DataType.STRING);
/**
 * Override the currency symbol position
 */
PriceFormatResult.CURRENCY_SYMBOL_POSITION = new PriceFormatResult('currency_symbol_position', data_type_1$6.DataType.ENUM);
/**
 * Override the trailing zero display configuration.
 */
PriceFormatResult.TRAILING_ZERO_DISPLAY = new PriceFormatResult('trailing_zero_display', data_type_1$6.DataType.ENUM);

var trailingZeroDisplay_enum = {};

(function (exports) {
Object.defineProperty(exports, "__esModule", { value: true });
exports.TrailingZeroDisplay = void 0;
(function (TrailingZeroDisplay) {
    /**
      * Display according to the settings for minimum fraction and significant digits.
      */
    TrailingZeroDisplay["AUTO"] = "AUTO";
    /**
     * Same as AUTO, but hide trailing zeros after the decimal separator if they are all zero.
     */
    TrailingZeroDisplay["HIDE_IF_WHOLE"] = "HIDE_IF_WHOLE";
})(exports.TrailingZeroDisplay || (exports.TrailingZeroDisplay = {}));

}(trailingZeroDisplay_enum));

var currencySymbolPosition_enum = {};

(function (exports) {
Object.defineProperty(exports, "__esModule", { value: true });
exports.CurrencySymbolPosition = void 0;
(function (CurrencySymbolPosition) {
    /**
     * This override is default for all locales, the way it is defined in CLDR.
     */
    CurrencySymbolPosition["DEFAULT"] = "default";
    /**
     * A formatted value with this override added will have currency symbol at the beginning
     * in the in-memory sequence (logical sequence) in currency pattern.
     *
     * Visual display position when rendered on the screen will depend on whether the display direction
     * is left to right or right to left.
     *
     * E.g., for left to right locales like English, French, etc:
     *
     * "$1,234.56"
     *
     * E.g., for right to left locales like Arabic, Hebrew, etc:
     *
     * "1,234.56$"
     */
    CurrencySymbolPosition["START"] = "start";
    /**
     * A formatted value with this override added will have currency symbol at the end
     * in the in-memory sequence (logical sequence) in currency pattern.
     *
     * Visual display position when rendered on the screen will depend on whether the display direction
     * is left to right or right to left.
     *
     * E.g., for left to right locales like English, French, etc:
     *
     * "1,234.56$"
     *
     * E.g., for right to left locales like Arabic, Hebrew, etc:
     *
     * "$1,234.56"
     */
    CurrencySymbolPosition["END"] = "end";
})(exports.CurrencySymbolPosition || (exports.CurrencySymbolPosition = {}));

}(currencySymbolPosition_enum));

var pointsFormat = {};

Object.defineProperty(pointsFormat, "__esModule", { value: true });
pointsFormat.PointsFormat = void 0;
const data_type_1$5 = dataType;
const config_name_1$2 = configName;
const config_type_1$5 = configType;
/**
 * Defines the Configurations available under the PointFormat table.
 */
class PointsFormat extends config_name_1$2.ConfigName {
    constructor(_name, _returnType) {
        super(_name, _returnType);
    }
    getConfigType() {
        return config_type_1$5.ConfigType.POINTS_FORMAT;
    }
}
pointsFormat.PointsFormat = PointsFormat;
/**
 * Pattern Strings to be used for formatting points amounts.
 */
PointsFormat.PATTERN = new PointsFormat('pattern', data_type_1$5.DataType.STRING);

var pointsFormatArgs = {};

Object.defineProperty(pointsFormatArgs, "__esModule", { value: true });
pointsFormatArgs.PointsFormatArgs = void 0;
const config_type_1$4 = configType;
const i18n_args_1$2 = i18nArgs;
/**
 * Input args to be used for a Points Format data queries.
 */
class PointsFormatArgs extends i18n_args_1$2.I18nArgs {
    constructor(_locale, _programName, _style) {
        super();
        this._locale = _locale;
        this._programName = _programName;
        this._style = _style;
    }
    getConfigType() {
        return config_type_1$4.ConfigType.POINTS_FORMAT;
    }
    getArgsMap() {
        return new Map([
            ['locale', this._locale],
            ['program_name', this._programName],
            ['style', this._style]
        ]);
    }
    get locale() {
        return this._locale;
    }
    get programName() {
        return this._programName;
    }
    get style() {
        return this._style;
    }
}
pointsFormatArgs.PointsFormatArgs = PointsFormatArgs;
/**
 * Return the {@link Builder} instance used to build an {@link PointsFormatArgs} object.
 *
 * @return the builder instance
 */
PointsFormatArgs.Builder = class {
    /**
     * Sets the Locale within the {@link PointsFormatArgs} container.
     *
     * @param locale    Locale to be used for the config query.
     *
     * @return builder instance
     */
    withLocale(locale) {
        this.locale = locale;
        return this;
    }
    /**
     * Sets the Program Name within the {@link PointsFormatArgs} container.
     *
     * @param programName  Program Name to be used for the config query.
     *
     * @return builder instance
     */
    withProgramName(programName) {
        this.programName = programName;
        return this;
    }
    /**
     * Sets the style within the {@link PointsFormatArgs} container.
     *
     * @param style  Style to be used for the config query.
     *
     * @return builder instance
     */
    withStyle(style) {
        this.style = style;
        return this;
    }
    /**
     * Builds a {@link PointsFormatArgs} class type using the previously set
     * properties.
     *
     * @return {@link PointsFormatArgs} class instance
     */
    build() {
        return new PointsFormatArgs(this.locale, this.programName, this.style);
    }
};

var pointsFormatResult = {};

Object.defineProperty(pointsFormatResult, "__esModule", { value: true });
pointsFormatResult.PointsFormatResult = void 0;
const data_type_1$4 = dataType;
const query_output_1$2 = queryOutput;
/**
 * Data available as part of a Points Format query result.
 */
class PointsFormatResult extends query_output_1$2.QueryOutput {
}
pointsFormatResult.PointsFormatResult = PointsFormatResult;
/**
 * Pattern is stored in String format.
 */
PointsFormatResult.PATTERN = new PointsFormatResult('pattern', data_type_1$4.DataType.STRING);

var pointsFormatStyle_enum = {};

(function (exports) {
Object.defineProperty(exports, "__esModule", { value: true });
exports.PointsFormatStyle = void 0;
(function (PointsFormatStyle) {
    /**
     * Displaying the amount and making it clear that it is an amount of points.
     */
    PointsFormatStyle["DEFAULT"] = "default";
    /**
     * For situations where the number is implicitly a point amount, so a symbol is not required.
     */
    PointsFormatStyle["IMPLICIT"] = "implicit";
})(exports.PointsFormatStyle || (exports.PointsFormatStyle = {}));

}(pointsFormatStyle_enum));

var cxTupleToI18nDomain = {};

Object.defineProperty(cxTupleToI18nDomain, "__esModule", { value: true });
cxTupleToI18nDomain.CXTupleToI18nDomain = void 0;
const data_type_1$3 = dataType;
const config_name_1$1 = configName;
const config_type_1$3 = configType;
/**
 * CXFamily and CXID form a combined unique identifier (CXTuple) that is an abstraction over the existing concept of
 * marketplace, which determines the ingress point of a customer. CXTuple is meant to encompass a broader set of site
 * experiences that are not restricted to just retail/marketplaces.
 * Reference: https://w.amazon.com/bin/view/INTech/CXFamilyCXId/
 *
 * I18nDomain is a remnant from a previous attempt at replacing marketplaceId, but was never fully fleshed out. In the
 * Panther space, it is used to offer variant support in PuffJ files.
 *
 * Defines the Configurations available under the CXTuple To I18nDomain mapping table.
 */
class CXTupleToI18nDomain extends config_name_1$1.ConfigName {
    constructor(_name, _returnType) {
        super(_name, _returnType);
    }
    getConfigType() {
        return config_type_1$3.ConfigType.CX_TUPLE_TO_I18N_DOMAIN;
    }
}
cxTupleToI18nDomain.CXTupleToI18nDomain = CXTupleToI18nDomain;
/**
 * I18nDomain is stored in string format
 */
CXTupleToI18nDomain.I18N_DOMAIN = new CXTupleToI18nDomain('i18n_domain', data_type_1$3.DataType.STRING);

var cxTupleToI18nDomainArgs = {};

var _a$1;
Object.defineProperty(cxTupleToI18nDomainArgs, "__esModule", { value: true });
cxTupleToI18nDomainArgs.CXTupleToI18nDomainArgs = void 0;
const config_type_1$2 = configType;
const i18n_args_1$1 = i18nArgs;
/**
 * Input args to be used to query CXTuple to I18nDomain mappings.
 */
class CXTupleToI18nDomainArgs extends i18n_args_1$1.I18nArgs {
    constructor(_cxFamily, _cxId, _cxProgram) {
        super();
        this._cxFamily = _cxFamily;
        this._cxId = _cxId;
        this._cxProgram = _cxProgram;
    }
    get cxFamily() {
        return this._cxFamily;
    }
    get cxId() {
        return this._cxId;
    }
    get cxProgram() {
        return this._cxProgram;
    }
    getConfigType() {
        return config_type_1$2.ConfigType.CX_TUPLE_TO_I18N_DOMAIN;
    }
    getArgsMap() {
        return new Map([
            ['cx_family', this._cxFamily],
            ['cx_id', this._cxId],
            ['cx_program', this._cxProgram]
        ]);
    }
}
cxTupleToI18nDomainArgs.CXTupleToI18nDomainArgs = CXTupleToI18nDomainArgs;
/**
 * Builder class that build the {@link CXTupleToI18nDomainArgs} instance.
 */
CXTupleToI18nDomainArgs.Builder = (_a$1 = class {
        constructor() {
            this.cxProgram = CXTupleToI18nDomainArgs.Builder.EMPTY_CX_PROGRAM;
        }
        /**
         * Sets the CXFamily within the {@link CXTupleToI18nDomainArgs}
         * container.
         *
         * @param cxFamily CXFamily to be used for the config query.
         *
         * @return builder instance
         */
        withCxFamily(cxFamily) {
            this.cxFamily = cxFamily;
            return this;
        }
        /**
         * Sets the CXId within the {@link CXTupleToI18nDomainArgs}
         * container.
         *
         * @param cxId CXId to be used for the config query.
         *
         * @return builder instance
         */
        withCxId(cxId) {
            this.cxId = cxId;
            return this;
        }
        /**
         * Sets the cxProgram within the {@link CXTupleToI18nDomainArgs}
         * container.
         *
         * @param cxProgram cxProgram to be used for the config query.
         *
         * @return builder instance
         */
        withCxProgram(cxProgram) {
            this.cxProgram = cxProgram;
            return this;
        }
        /**
         * Builds the {@link CXTupleToI18nDomainArgs} class using the previously set
         * properties.
         *
         * @return {@link CXTupleToI18nDomainArgs} class instance
         */
        build() {
            return new CXTupleToI18nDomainArgs(this.cxFamily, this.cxId, this.cxProgram);
        }
    },
    _a$1.EMPTY_CX_PROGRAM = '[]',
    _a$1);

var cxTupleToI18nDomainResult = {};

Object.defineProperty(cxTupleToI18nDomainResult, "__esModule", { value: true });
cxTupleToI18nDomainResult.CXTupleToI18nDomainResult = void 0;
const data_type_1$2 = dataType;
const query_output_1$1 = queryOutput;
/**
 * Data available as part of a CXTuple to I18nDomain query result.
 */
class CXTupleToI18nDomainResult extends query_output_1$1.QueryOutput {
}
cxTupleToI18nDomainResult.CXTupleToI18nDomainResult = CXTupleToI18nDomainResult;
/**
 * I18nDomain is stored in string format
 */
CXTupleToI18nDomainResult.I18N_DOMAIN = new CXTupleToI18nDomainResult('i18n_domain', data_type_1$2.DataType.STRING);

var cxTupleToDefaultPreferences = {};

Object.defineProperty(cxTupleToDefaultPreferences, "__esModule", { value: true });
cxTupleToDefaultPreferences.CXTupleToDefaultPreferences = void 0;
const data_type_1$1 = dataType;
const config_name_1 = configName;
const config_type_1$1 = configType;
/**
 * CXFamily and CXID form a combined unique identifier (CXTuple) that is an abstraction over the existing concept of
 * marketplace, which determines the ingress point of a customer. CXTuple is meant to encompass a broader set of site
 * experiences that are not restricted to just retail/marketplaces.
 * Reference: https://w.amazon.com/bin/view/INTech/CXFamilyCXId/
 *
 * Defines the Configurations available under the CXTuple To Default Preferences mapping table.
 */
class CXTupleToDefaultPreferences extends config_name_1.ConfigName {
    constructor(_name, _returnType) {
        super(_name, _returnType);
    }
    getConfigType() {
        return config_type_1$1.ConfigType.CX_TUPLE_TO_DEFAULT_PREFERENCES;
    }
}
cxTupleToDefaultPreferences.CXTupleToDefaultPreferences = CXTupleToDefaultPreferences;
/**
 * Default currency is stored in String format.
 */
CXTupleToDefaultPreferences.DEFAULT_CURRENCY = new CXTupleToDefaultPreferences('default_currency', data_type_1$1.DataType.STRING);
/**
 * Default language is stored in String format.
 */
CXTupleToDefaultPreferences.DEFAULT_LANGUAGE = new CXTupleToDefaultPreferences('default_language', data_type_1$1.DataType.STRING);

var cxTupleToDefaultPreferencesArgs = {};

Object.defineProperty(cxTupleToDefaultPreferencesArgs, "__esModule", { value: true });
cxTupleToDefaultPreferencesArgs.CXTupleToDefaultPreferencesArgs = void 0;
const config_type_1 = configType;
const i18n_args_1 = i18nArgs;
/**
 * Input args to be used to query CXTuple defaults.
 */
class CXTupleToDefaultPreferencesArgs extends i18n_args_1.I18nArgs {
    constructor(_cxFamily, _cxId, _cxProgram) {
        super();
        this._cxFamily = _cxFamily;
        this._cxId = _cxId;
        this._cxProgram = _cxProgram;
    }
    getConfigType() {
        return config_type_1.ConfigType.CX_TUPLE_TO_DEFAULT_PREFERENCES;
    }
    getArgsMap() {
        return new Map([
            ['cx_family', this._cxFamily],
            ['cx_id', this._cxId],
            ['cx_program', this._cxProgram]
        ]);
    }
    get cxFamily() {
        return this._cxFamily;
    }
    get cxId() {
        return this._cxId;
    }
    get cxProgram() {
        return this._cxProgram;
    }
}
cxTupleToDefaultPreferencesArgs.CXTupleToDefaultPreferencesArgs = CXTupleToDefaultPreferencesArgs;
/**
 * Builder class that build the {@link CXTupleToDefaultPreferencesArgs} instance.
 */
CXTupleToDefaultPreferencesArgs.Builder = class {
    /**
     * Sets the CXFamily within the {@link CXTupleToDefaultPreferencesArgs}
     * container.
     *
     * @param cxFamily CXFamily to be used for the config query.
     *
     * @return builder instance
     */
    withCxFamily(cxFamily) {
        this.cxFamily = cxFamily;
        return this;
    }
    /**
     * Sets the CXId within the {@link CXTupleToDefaultPreferencesArgs}
     * container.
     *
     * @param cxId CXId to be used for the config query.
     *
     * @return builder instance
     */
    withCxId(cxId) {
        this.cxId = cxId;
        return this;
    }
    /**
     * Sets the CXProgram within the {@link CXTupleToDefaultPreferencesArgs}
     * container.
     *
     * @param CXProgram CXProgram to be used for the config query.
     *
     * @return builder instance
     */
    withCxProgram(cxProgram) {
        this.cxProgram = cxProgram;
        return this;
    }
    /**
     * Builds the {@link CXTupleToDefaultPreferencesArgs} class using the previously set
     * properties.
     *
     * @return {@link CXTupleToDefaultPreferences} class instance
     */
    build() {
        return new CXTupleToDefaultPreferencesArgs(this.cxFamily, this.cxId, this.cxProgram);
    }
};

var cxTupleToDefaultPreferencesResult = {};

Object.defineProperty(cxTupleToDefaultPreferencesResult, "__esModule", { value: true });
cxTupleToDefaultPreferencesResult.CXTupleToDefaultPreferencesResult = void 0;
const data_type_1 = dataType;
const query_output_1 = queryOutput;
/**
 * Data available as part of a CXTuple Defaults query result.
 */
class CXTupleToDefaultPreferencesResult extends query_output_1.QueryOutput {
}
cxTupleToDefaultPreferencesResult.CXTupleToDefaultPreferencesResult = CXTupleToDefaultPreferencesResult;
/**
 * Default currency is stored in String format.
 */
CXTupleToDefaultPreferencesResult.DEFAULT_CURRENCY = new CXTupleToDefaultPreferencesResult('default_currency', data_type_1.DataType.STRING);
/**
 * Default language is stored in String format.
 */
CXTupleToDefaultPreferencesResult.DEFAULT_LANGUAGE = new CXTupleToDefaultPreferencesResult('default_language', data_type_1.DataType.STRING);

(function (exports) {
Object.defineProperty(exports, "__esModule", { value: true });
var config_type_1 = configType;
Object.defineProperty(exports, "ConfigType", { enumerable: true, get: function () { return config_type_1.ConfigType; } });
var config_name_1 = configName;
Object.defineProperty(exports, "ConfigName", { enumerable: true, get: function () { return config_name_1.ConfigName; } });
var query_output_1 = queryOutput;
Object.defineProperty(exports, "QueryOutput", { enumerable: true, get: function () { return query_output_1.QueryOutput; } });
var i18n_configuration_1 = i18nConfiguration;
Object.defineProperty(exports, "I18nConfiguration", { enumerable: true, get: function () { return i18n_configuration_1.I18nConfiguration; } });
var configuration_data_1 = configurationData;
Object.defineProperty(exports, "ConfigurationData", { enumerable: true, get: function () { return configuration_data_1.ConfigurationData; } });
var data_type_1 = dataType;
Object.defineProperty(exports, "DataType", { enumerable: true, get: function () { return data_type_1.DataType; } });
var i18n_args_1 = i18nArgs;
Object.defineProperty(exports, "I18nArgs", { enumerable: true, get: function () { return i18n_args_1.I18nArgs; } });
var query_resolver_1 = queryResolver;
Object.defineProperty(exports, "QueryResolver", { enumerable: true, get: function () { return query_resolver_1.QueryResolver; } });
var i18n_configuration_provider_1 = i18nConfigurationProvider;
Object.defineProperty(exports, "I18nConfigurationProvider", { enumerable: true, get: function () { return i18n_configuration_provider_1.I18nConfigurationProvider; } });
var configuration_provider_manager_1 = configurationProviderManager;
Object.defineProperty(exports, "ConfigurationProviderManager", { enumerable: true, get: function () { return configuration_provider_manager_1.ConfigurationProviderManager; } });
var utils_1 = utils;
Object.defineProperty(exports, "isNullOrUndefined", { enumerable: true, get: function () { return utils_1.isNullOrUndefined; } });
// Export for MarkeptlaceIdToCXId feature
var marketplaceId_to_cx_tuple_1 = marketplaceIdToCxTuple;
Object.defineProperty(exports, "MarketplaceIdToCxTuple", { enumerable: true, get: function () { return marketplaceId_to_cx_tuple_1.MarketplaceIdToCxTuple; } });
var marketplaceId_to_cx_tuple_args_1 = marketplaceIdToCxTupleArgs;
Object.defineProperty(exports, "MarketplaceIdToCxTupleArgs", { enumerable: true, get: function () { return marketplaceId_to_cx_tuple_args_1.MarketplaceIdToCxTupleArgs; } });
var marketplaceId_to_cx_tuple_result_1 = marketplaceIdToCxTupleResult;
Object.defineProperty(exports, "MarketplaceIdToCxTupleResult", { enumerable: true, get: function () { return marketplaceId_to_cx_tuple_result_1.MarketplaceIdToCxTupleResult; } });
// Export for the PriceFormat feature
var price_format_1 = priceFormat;
Object.defineProperty(exports, "PriceFormat", { enumerable: true, get: function () { return price_format_1.PriceFormat; } });
var price_format_args_1 = priceFormatArgs;
Object.defineProperty(exports, "PriceFormatArgs", { enumerable: true, get: function () { return price_format_args_1.PriceFormatArgs; } });
var price_format_args_builder_1 = priceFormatArgsBuilder;
Object.defineProperty(exports, "PriceFormatArgsBuilder", { enumerable: true, get: function () { return price_format_args_builder_1.PriceFormatArgsBuilder; } });
var retail_price_format_args_1 = retailPriceFormatArgs;
Object.defineProperty(exports, "RetailPriceFormatArgs", { enumerable: true, get: function () { return retail_price_format_args_1.RetailPriceFormatArgs; } });
var price_format_type_enum_1 = priceFormatType_enum;
Object.defineProperty(exports, "PriceFormatType", { enumerable: true, get: function () { return price_format_type_enum_1.PriceFormatType; } });
var price_format_result_1 = priceFormatResult;
Object.defineProperty(exports, "PriceFormatResult", { enumerable: true, get: function () { return price_format_result_1.PriceFormatResult; } });
var trailing_zero_display_enum_1 = trailingZeroDisplay_enum;
Object.defineProperty(exports, "TrailingZeroDisplay", { enumerable: true, get: function () { return trailing_zero_display_enum_1.TrailingZeroDisplay; } });
var currency_symbol_position_enum_1 = currencySymbolPosition_enum;
Object.defineProperty(exports, "CurrencySymbolPosition", { enumerable: true, get: function () { return currency_symbol_position_enum_1.CurrencySymbolPosition; } });
// Export for the I18nPointsFormat feature
var points_format_1 = pointsFormat;
Object.defineProperty(exports, "PointsFormat", { enumerable: true, get: function () { return points_format_1.PointsFormat; } });
var points_format_args_1 = pointsFormatArgs;
Object.defineProperty(exports, "PointsFormatArgs", { enumerable: true, get: function () { return points_format_args_1.PointsFormatArgs; } });
var points_format_result_1 = pointsFormatResult;
Object.defineProperty(exports, "PointsFormatResult", { enumerable: true, get: function () { return points_format_result_1.PointsFormatResult; } });
var points_format_style_enum_1 = pointsFormatStyle_enum;
Object.defineProperty(exports, "PointsFormatStyle", { enumerable: true, get: function () { return points_format_style_enum_1.PointsFormatStyle; } });
// Export for the CXTupleToI18nDomain feature
var cx_tuple_to_i18n_domain_1 = cxTupleToI18nDomain;
Object.defineProperty(exports, "CXTupleToI18nDomain", { enumerable: true, get: function () { return cx_tuple_to_i18n_domain_1.CXTupleToI18nDomain; } });
var cx_tuple_to_i18n_domain_args_1 = cxTupleToI18nDomainArgs;
Object.defineProperty(exports, "CXTupleToI18nDomainArgs", { enumerable: true, get: function () { return cx_tuple_to_i18n_domain_args_1.CXTupleToI18nDomainArgs; } });
var cx_tuple_to_i18n_domain_result_1 = cxTupleToI18nDomainResult;
Object.defineProperty(exports, "CXTupleToI18nDomainResult", { enumerable: true, get: function () { return cx_tuple_to_i18n_domain_result_1.CXTupleToI18nDomainResult; } });
// Export for the I18nPreferencesACSConfig feature
var cx_tuple_to_default_preferences_1 = cxTupleToDefaultPreferences;
Object.defineProperty(exports, "CXTupleToDefaultPreferences", { enumerable: true, get: function () { return cx_tuple_to_default_preferences_1.CXTupleToDefaultPreferences; } });
var cx_tuple_to_default_preferences_args_1 = cxTupleToDefaultPreferencesArgs;
Object.defineProperty(exports, "CXTupleToDefaultPreferencesArgs", { enumerable: true, get: function () { return cx_tuple_to_default_preferences_args_1.CXTupleToDefaultPreferencesArgs; } });
var cx_tuple_to_default_preferences_result_1 = cxTupleToDefaultPreferencesResult;
Object.defineProperty(exports, "CXTupleToDefaultPreferencesResult", { enumerable: true, get: function () { return cx_tuple_to_default_preferences_result_1.CXTupleToDefaultPreferencesResult; } });

}(dist$1));

var dist = {};

var resourceBundleI18nConfigurationProvider = {};

var resolverFactory = {};

var cxTupleToDefaultPreferencesQueryResolver = {};

var cxTupleToDefaultPreferencesMapping_resource = {};

Object.defineProperty(cxTupleToDefaultPreferencesMapping_resource, "__esModule", { value: true });
/**
 * Utils class with Static Map containing mappings of CXTuple to it's default language and currency preferences.
 */
class CxTupleToDefaultPreferencesMapping {
    getContent() {
        return CxTupleToDefaultPreferencesMapping.CX_TUPLE_TO_DEFAULT_PREFERENCES_MAPPING;
    }
}
cxTupleToDefaultPreferencesMapping_resource.default = CxTupleToDefaultPreferencesMapping;
CxTupleToDefaultPreferencesMapping.CX_TUPLE_TO_DEFAULT_PREFERENCES_MAPPING = new Map([
    // Retail CXTuple Defaults derived from the I18nPreferencesACSConfig ACS feature table.
    // https://console.harmony.a2z.com/acs-web-editor/features/I18nPreferencesACSConfig/records
    [/* AE */ "retail/amazon.ae|default_language", "en-AE"],
    [/* AE */ "retail/amazon.ae|default_currency", "AED"],
    [/* AU */ "retail/amazon.au|default_language", "en-AU"],
    [/* AU */ "retail/amazon.au|default_currency", "AUD"],
    [/* BE */ "retail/amazon.com.be|default_language", "fr-BE"],
    [/* BE */ "retail/amazon.com.be|default_currency", "EUR"],
    [/* BR */ "retail/amazon.com.br|default_language", "pt-BR"],
    [/* BR */ "retail/amazon.com.br|default_currency", "BRL"],
    [/* CA */ "retail/amazon.ca|default_language", "en-CA"],
    [/* CA */ "retail/amazon.ca|default_currency", "CAD"],
    [/* CL */ "retail/amazon.cl|default_language", "es-CL"],
    [/* CL */ "retail/amazon.cl|default_currency", "CLP"],
    [/* CO */ "retail/amazon.com.co|default_language", "es-CO"],
    [/* CO */ "retail/amazon.com.co|default_currency", "COP"],
    [/* CN */ "retail/amazon.cn|default_language", "zh-CN"],
    [/* CN */ "retail/amazon.cn|default_currency", "CNY"],
    [/* DE */ "retail/amazon.de|default_language", "de-DE"],
    [/* DE */ "retail/amazon.de|default_currency", "EUR"],
    [/* EG */ "retail/amazon.eg|default_language", "ar-AE"],
    [/* EG */ "retail/amazon.eg|default_currency", "EGP"],
    [/* ES */ "retail/amazon.es|default_language", "es-ES"],
    [/* ES */ "retail/amazon.es|default_currency", "EUR"],
    [/* FR */ "retail/amazon.fr|default_language", "fr-FR"],
    [/* FR */ "retail/amazon.fr|default_currency", "EUR"],
    [/* IN */ "retail/amazon.in|default_language", "en-IN"],
    [/* IN */ "retail/amazon.in|default_currency", "INR"],
    [/* IT */ "retail/amazon.it|default_language", "it-IT"],
    [/* IT */ "retail/amazon.it|default_currency", "EUR"],
    [/* JP */ "retail/amazon.co.jp|default_language", "ja-JP"],
    [/* JP */ "retail/amazon.co.jp|default_currency", "JPY"],
    [/* MX */ "retail/amazon.com.mx|default_language", "es-MX"],
    [/* MX */ "retail/amazon.com.mx|default_currency", "MXN"],
    [/* NG */ "retail/amazon.com.ng|default_language", "en-NG"],
    [/* NG */ "retail/amazon.com.ng|default_currency", "NGN"],
    [/* NL */ "retail/amazon.nl|default_language", "nl-NL"],
    [/* NL */ "retail/amazon.nl|default_currency", "EUR"],
    [/* PL */ "retail/amazon.pl|default_language", "pl-PL"],
    [/* PL */ "retail/amazon.pl|default_currency", "PLN"],
    [/* RU */ "retail/amazon.ru|default_language", "ru-RU"],
    [/* RU */ "retail/amazon.ru|default_currency", "RUB"],
    [/* SA */ "retail/amazon.sa|default_language", "ar-AE"],
    [/* SA */ "retail/amazon.sa|default_currency", "SAR"],
    [/* SE */ "retail/amazon.se|default_language", "sv-SE"],
    [/* SE */ "retail/amazon.se|default_currency", "SEK"],
    [/* SG */ "retail/amazon.sg|default_language", "en-SG"],
    [/* SG */ "retail/amazon.sg|default_currency", "SGD"],
    [/* TR */ "retail/amazon.com.tr|default_language", "tr-TR"],
    [/* TR */ "retail/amazon.com.tr|default_currency", "TRY"],
    [/* UK */ "retail/amazon.co.uk|default_language", "en-GB"],
    [/* UK */ "retail/amazon.co.uk|default_currency", "GBP"],
    [/* US */ "retail/amazon.com|default_language", "en-US"],
    [/* US */ "retail/amazon.com|default_currency", "USD"],
    [/* ZA */ "retail/amazon.co.za|default_language", "en-ZA"],
    [/* ZA */ "retail/amazon.co.za|default_currency", "ZAR"],
    // PYOP/AmazonPay CXTuple to default preference mappings derived from the I18nMarketplaceCommons pkg:
    // https://tiny.amazon.com/40anwt56
    [/* PYOP_CA */ "pyop/apa.amazon.ca|default_language", "en-CA"],
    [/* PYOP_CA */ "pyop/apa.amazon.ca|default_currency", "CAD"],
    [/* PYOP_DE */ "pyop/apa.amazon.de|default_language", "de-DE"],
    [/* PYOP_DE */ "pyop/apa.amazon.de|default_currency", "EUR"],
    [/* PYOP_IN */ "pyop/apa.amazon.in|default_language", "en-IN"],
    [/* PYOP_IN */ "pyop/apa.amazon.in|default_currency", "INR"],
    [/* PYOP_JP */ "pyop/apa.amazon.co.jp|default_language", "ja-JP"],
    [/* PYOP_JP */ "pyop/apa.amazon.co.jp|default_currency", "JPY"],
    [/* PYOP_US */ "pyop/apa.amazon.com|default_language", "en-US"],
    [/* PYOP_US */ "pyop/apa.amazon.com|default_currency", "USD"],
    [/* PYOP_UK */ "pyop/apa.amazon.co.uk|default_language", "en-GB"],
    [/* PYOP_UK */ "pyop/apa.amazon.co.uk|default_currency", "GBP"]
]);

var mapResourceQueryResolver = {};

Object.defineProperty(mapResourceQueryResolver, "__esModule", { value: true });
const i18n_configuration_provider_1$h = dist$1;
/**
 * Base class to resolve Resource configuration queries
 */
class MapResourceQueryResolver extends i18n_configuration_provider_1$h.QueryResolver {
    constructor() {
        super();
        this.initialize();
    }
    initialize() {
        if (!this.isInitialized()) {
            try {
                this._resourceConfigRetriever = this.createMapResourceConfigRetriever();
                this.setInitializedState(true);
            }
            catch (e) {
                console.error(`Failed to initialize query resolver ${this.constructor.name}`);
            }
        }
        return this.isInitialized();
    }
}
mapResourceQueryResolver.default = MapResourceQueryResolver;
MapResourceQueryResolver.MAP_ATTRIBUTE_KEY_SEPARATOR = '|';

var __importDefault$6 = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(cxTupleToDefaultPreferencesQueryResolver, "__esModule", { value: true });
const i18n_configuration_provider_1$g = dist$1;
const cx_tuple_to_default_preferences_mapping_resource_1 = __importDefault$6(cxTupleToDefaultPreferencesMapping_resource);
const map_resource_query_resolver_1$2 = __importDefault$6(mapResourceQueryResolver);
/**
 * Resolves queries for CXTuple to Default Preferences Mappings.
 */
class CXTupleToDefaultPreferencesQueryResolver extends map_resource_query_resolver_1$2.default {
    constructor() {
        super();
    }
    resolve(attributeName, args) {
        this.validate(attributeName, args);
        return this.doResolve(attributeName, args);
    }
    doResolve(attributeName, args) {
        const configBuilder = new i18n_configuration_provider_1$g.I18nConfiguration.Builder();
        switch (attributeName) {
            case i18n_configuration_provider_1$g.CXTupleToDefaultPreferences.DEFAULT_CURRENCY:
                const currency = this.getResource(attributeName, args);
                configBuilder.addConfiguration(i18n_configuration_provider_1$g.CXTupleToDefaultPreferencesResult.DEFAULT_CURRENCY, currency);
                break;
            case i18n_configuration_provider_1$g.CXTupleToDefaultPreferences.DEFAULT_LANGUAGE:
                const language = this.getResource(attributeName, args);
                configBuilder.addConfiguration(i18n_configuration_provider_1$g.CXTupleToDefaultPreferencesResult.DEFAULT_LANGUAGE, language);
                break;
            default:
                throw new Error(`Attribute name ${attributeName} is not defined for feature ${attributeName.getConfigType()}`);
        }
        return configBuilder.build();
    }
    /**
     * Method to build a resource key and fetch the particular resource from a static map. The key is comprised of
     * the args and a config component in the format:
     * - cx_family/cx_id|default_currency
     * - cx_family/cx_id|default_language
     *
     * @param attributeName The CXTupleToDefaultPreferences ConfigName for which the value needs to be fetched.
     * @param args CXTupleToDefaultPreferencesArgs used to build the resource key.
     * @return string resource from map.
     */
    getResource(attributeName, args) {
        let resourceKey = args.cxFamily + CXTupleToDefaultPreferencesQueryResolver.CX_TUPLE_SEPARATOR + args.cxId;
        resourceKey += map_resource_query_resolver_1$2.default.MAP_ATTRIBUTE_KEY_SEPARATOR;
        resourceKey += attributeName;
        return this._resourceConfigRetriever.getContent().get(resourceKey);
    }
    createMapResourceConfigRetriever() {
        return new cx_tuple_to_default_preferences_mapping_resource_1.default();
    }
    getTargetConfigType() {
        return CXTupleToDefaultPreferencesQueryResolver.CONFIG_TYPE;
    }
    getValidationErrorMessage() {
        return `CXTuple to default preferences configs can only be resolved for configType ${CXTupleToDefaultPreferencesQueryResolver.CONFIG_TYPE}`;
    }
}
cxTupleToDefaultPreferencesQueryResolver.default = CXTupleToDefaultPreferencesQueryResolver;
CXTupleToDefaultPreferencesQueryResolver.CX_TUPLE_SEPARATOR = "/";
CXTupleToDefaultPreferencesQueryResolver.CONFIG_TYPE = i18n_configuration_provider_1$g.ConfigType.CX_TUPLE_TO_DEFAULT_PREFERENCES;

var cxTupleToI18nDomainQueryResolver = {};

var cxTupleToI18nDomainMapping_resource = {};

Object.defineProperty(cxTupleToI18nDomainMapping_resource, "__esModule", { value: true });
/**
 * Utils class with Static Map containing mappings of CXTuple to it's equivalent retail I18nDomain.
 */
class CxTupleToI18nDomainMapping {
    getContent() {
        return CxTupleToI18nDomainMapping.CX_TUPLE_TO_I18N_DOMAIN_MAPPINGS;
    }
}
cxTupleToI18nDomainMapping_resource.default = CxTupleToI18nDomainMapping;
CxTupleToI18nDomainMapping.CX_TUPLE_TO_I18N_DOMAIN_MAPPINGS = new Map([
    // Retail CXTuple Defaults derived from the I18nPreferencesACSConfig ACS feature table.
    // https://console.harmony.a2z.com/acs-web-editor/features/CXTupleI18nDomainMapping/records
    ["retail/amazon.ae|i18n_domain", "retail-ae"],
    ["retail/amazon.com.au|i18n_domain", "retail-au"],
    ["retail/amazon.com.be|i18n_domain", "retail-be"],
    ["retail/amazon.com.br|i18n_domain", "retail-br"],
    ["retail/amazon.ca|i18n_domain", "retail-ca"],
    ["retail/amazon.cl|i18n_domain", "retail-cl"],
    ["retail/amazon.com.co|i18n_domain", "retail-co"],
    ["retail/amazon.cn|i18n_domain", "retail-cn"],
    ["retail/amazon.de|i18n_domain", "retail-de"],
    ["retail/amazon.eg|i18n_domain", "retail-eg"],
    ["retail/amazon.es|i18n_domain", "retail-es"],
    ["retail/amazon.fr|i18n_domain", "retail-fr"],
    ["retail/amazon.in|i18n_domain", "retail-in"],
    ["retail/amazon.it|i18n_domain", "retail-it"],
    ["retail/amazon.co.jp|i18n_domain", "retail-jp"],
    ["retail/amazon.com.mx|i18n_domain", "retail-mx"],
    ["retail/amazon.com.ng|i18n_domain", "retail-ng"],
    ["retail/amazon.nl|i18n_domain", "retail-nl"],
    ["retail/amazon.pl|i18n_domain", "retail-pl"],
    ["retail/amazon.ru|i18n_domain", "retail-ru"],
    ["retail/amazon.sa|i18n_domain", "retail-sa"],
    ["retail/amazon.se|i18n_domain", "retail-se"],
    ["retail/amazon.sg|i18n_domain", "retail-sg"],
    ["retail/amazon.com.tr|i18n_domain", "retail-tr"],
    ["retail/amazon.co.uk|i18n_domain", "retail-uk"],
    ["retail/amazon.com|i18n_domain", "retail-us"],
    ["retail/amazon.co.za|i18n_domain", "retail-za"]
]);

var __importDefault$5 = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(cxTupleToI18nDomainQueryResolver, "__esModule", { value: true });
const i18n_configuration_provider_1$f = dist$1;
const cx_tuple_to_i18n_domain_mapping_resource_1 = __importDefault$5(cxTupleToI18nDomainMapping_resource);
const map_resource_query_resolver_1$1 = __importDefault$5(mapResourceQueryResolver);
/**
 * Resolves queries for CXTuple to I18nDomain Mappings.
 */
class CXTupleToI18nDomainQueryResolver extends map_resource_query_resolver_1$1.default {
    constructor() {
        super();
    }
    resolve(attributeName, args) {
        this.validate(attributeName, args);
        return this.doResolve(attributeName, args);
    }
    doResolve(attributeName, args) {
        const configBuilder = new i18n_configuration_provider_1$f.I18nConfiguration.Builder();
        switch (attributeName) {
            case i18n_configuration_provider_1$f.CXTupleToI18nDomain.I18N_DOMAIN:
                const i18nDomain = this.getResource(attributeName, args);
                configBuilder.addConfiguration(i18n_configuration_provider_1$f.CXTupleToI18nDomainResult.I18N_DOMAIN, i18nDomain);
                break;
            default:
                throw new Error(`Attribute name ${attributeName} is not defined for feature ${attributeName.getConfigType()}`);
        }
        return configBuilder.build();
    }
    /**
     * Method to build a resource key and fetch the particular resource from a static map. The key is comprised of
     * the args and a config component in the format:
     * cx_family/cx_id|i18n_domain
     *
     * @param attributeName The CXTupleToI18nDomain ConfigName for which the value needs to be fetched.
     * @param args CXTupleToI18nDomainArgs used to build the resource key.
     * @return I18nDomain string resource from map.
     */
    getResource(attributeName, args) {
        let resourceKey = args.cxFamily + CXTupleToI18nDomainQueryResolver.CX_TUPLE_SEPARATOR + args.cxId;
        resourceKey += map_resource_query_resolver_1$1.default.MAP_ATTRIBUTE_KEY_SEPARATOR;
        resourceKey += attributeName;
        return this._resourceConfigRetriever.getContent().get(resourceKey);
    }
    createMapResourceConfigRetriever() {
        return new cx_tuple_to_i18n_domain_mapping_resource_1.default();
    }
    getTargetConfigType() {
        return CXTupleToI18nDomainQueryResolver.CONFIG_TYPE;
    }
    getValidationErrorMessage() {
        return `CXTuple to I18nDomain configs can only be resolved for configType ${CXTupleToI18nDomainQueryResolver.CONFIG_TYPE}`;
    }
}
cxTupleToI18nDomainQueryResolver.default = CXTupleToI18nDomainQueryResolver;
CXTupleToI18nDomainQueryResolver.CX_TUPLE_SEPARATOR = "/";
CXTupleToI18nDomainQueryResolver.CONFIG_TYPE = i18n_configuration_provider_1$f.ConfigType.CX_TUPLE_TO_I18N_DOMAIN;

var marketplaceIdToCxTupleQueryResolver = {};

var marketplaceIdToCxTupleMapping_resource = {};

Object.defineProperty(marketplaceIdToCxTupleMapping_resource, "__esModule", { value: true });
class MarketplaceIdToCxTupleMapping {
    getContent() {
        return MarketplaceIdToCxTupleMapping.MARKETPLACE_TO_CXTUPLE_MAPPINGS;
    }
}
marketplaceIdToCxTupleMapping_resource.default = MarketplaceIdToCxTupleMapping;
MarketplaceIdToCxTupleMapping.MARKETPLACE_TO_CXTUPLE_MAPPINGS = new Map([
    [/* AE */ "A2VIGQ35RCS4UG|cx_id", "amazon.ae"],
    [/* AE */ "A2VIGQ35RCS4UG|cx_family", "retail"],
    [/* AE_DEVO */ "A34GYYCZVDBSIK|cx_id", "amazon.ae"],
    [/* AE_DEVO */ "A34GYYCZVDBSIK|cx_family", "retail"],
    [/* AU */ "A39IBJ37TRP1C6|cx_id", "amazon.com.au"],
    [/* AU */ "A39IBJ37TRP1C6|cx_family", "retail"],
    [/* AU_DEVO */ "A1RNPCQ4K8U27I|cx_id", "amazon.com.au"],
    [/* AU_DEVO */ "A1RNPCQ4K8U27I|cx_family", "retail"],
    [/* BE */ "AMEN7PMS3EDWL|cx_id", "amazon.com.be"],
    [/* BE */ "AMEN7PMS3EDWL|cx_family", "retail"],
    [/* BE_DEVO */ "A1YFOMBPFBRY22|cx_id", "amazon.com.be"],
    [/* BE_DEVO */ "A1YFOMBPFBRY22|cx_family", "retail"],
    [/* BR */ "A2Q3Y263D00KWC|cx_id", "amazon.com.br"],
    [/* BR */ "A2Q3Y263D00KWC|cx_family", "retail"],
    [/* BR_DEVO */ "AZXD3QD5B39HD|cx_id", "amazon.com.br"],
    [/* BR_DEVO */ "AZXD3QD5B39HD|cx_family", "retail"],
    [/* CA */ "A2EUQ1WTGCTBG2|cx_id", "amazon.ca"],
    [/* CA */ "A2EUQ1WTGCTBG2|cx_family", "retail"],
    [/* CL */ "A2WDZJZ5TCRMLH|cx_id", "amazon.cl"],
    [/* CL */ "A2WDZJZ5TCRMLH|cx_family", "retail"],
    [/* CL_DEVO */ "A2IDBVIE7EAGN4|cx_id", "amazon.cl"],
    [/* CL_DEVO */ "A2IDBVIE7EAGN4|cx_family", "retail"],
    [/* CO */ "A1NOHO22DE3OHQ|cx_id", "amazon.com.co"],
    [/* CO */ "A1NOHO22DE3OHQ|cx_family", "retail"],
    [/* CN */ "AAHKV2X7AFYLW|cx_id", "amazon.cn"],
    [/* CN */ "AAHKV2X7AFYLW|cx_family", "retail"],
    [/* DE */ "A1PA6795UKMFR9|cx_id", "amazon.de"],
    [/* DE */ "A1PA6795UKMFR9|cx_family", "retail"],
    [/* EG */ "ARBP9OOSHTCHU|cx_id", "amazon.eg"],
    [/* EG */ "ARBP9OOSHTCHU|cx_family", "retail"],
    [/* EG_DEVO */ "AUJPM9XGFJRC7|cx_id", "amazon.eg"],
    [/* EG_DEVO */ "AUJPM9XGFJRC7|cx_family", "retail"],
    [/* ES */ "A1RKKUPIHCS9HS|cx_id", "amazon.es"],
    [/* ES */ "A1RKKUPIHCS9HS|cx_family", "retail"],
    [/* ES_DEVO */ "AJZF8LZ1EJVJN|cx_id", "amazon.es"],
    [/* ES_DEVO */ "AJZF8LZ1EJVJN|cx_family", "retail"],
    [/* FR */ "A13V1IB3VIYZZH|cx_id", "amazon.fr"],
    [/* FR */ "A13V1IB3VIYZZH|cx_family", "retail"],
    [/* IN */ "A21TJRUUN4KGV|cx_id", "amazon.in"],
    [/* IN */ "A21TJRUUN4KGV|cx_family", "retail"],
    [/* IN_DEVO */ "A2XZLSVIQ0F4JT|cx_family", "retail"],
    [/* IN_DEVO */ "A2XZLSVIQ0F4JT|cx_id", "amazon.in"],
    [/* IT */ "APJ6JRA9NG5V4|cx_id", "amazon.it"],
    [/* IT */ "APJ6JRA9NG5V4|cx_family", "retail"],
    [/* IT_DEVO */ "A3HOBANJMCMD83|cx_id", "amazon.it"],
    [/* IT_DEVO */ "A3HOBANJMCMD83|cx_family", "retail"],
    [/* JP */ "A1VC38T7YXB528|cx_id", "amazon.co.jp"],
    [/* JP */ "A1VC38T7YXB528|cx_family", "retail"],
    [/* MX */ "A1AM78C64UM0Y8|cx_id", "amazon.com.mx"],
    [/* MX */ "A1AM78C64UM0Y8|cx_family", "retail"],
    [/* MX_DEVO */ "A3P3J5A7D2ZVXI|cx_id", "amazon.com.mx"],
    [/* MX_DEVO */ "A3P3J5A7D2ZVXI|cx_family", "retail"],
    [/* NG */ "A3OCL2LJ6GDH9T|cx_id", "amazon.com.ng"],
    [/* NG */ "A3OCL2LJ6GDH9T|cx_family", "retail"],
    [/* NG_DEVO */ "A3PJZM0QIA8QDU|cx_id", "amazon.com.ng"],
    [/* NG_DEVO */ "A3PJZM0QIA8QDU|cx_family", "retail"],
    [/* NL */ "A1805IZSGTT6HS|cx_id", "amazon.nl"],
    [/* NL */ "A1805IZSGTT6HS|cx_family", "retail"],
    [/* NL_DEVO */ "A1M3WC0SJ3A38T|cx_id", "amazon.nl"],
    [/* NL_DEVO */ "A1M3WC0SJ3A38T|cx_family", "retail"],
    [/* PL */ "A1C3SOZRARQ6R3|cx_id", "amazon.pl"],
    [/* PL */ "A1C3SOZRARQ6R3|cx_family", "retail"],
    [/* PL_DEVO */ "AKY4K4WKH21YQ|cx_id", "amazon.pl"],
    [/* PL_DEVO */ "AKY4K4WKH21YQ|cx_family", "retail"],
    [/* RU */ "AD2EMQ3L3PG8S|cx_id", "amazon.ru"],
    [/* RU */ "AD2EMQ3L3PG8S|cx_family", "retail"],
    [/* RU_DEVO */ "A38NPJYVS5YHNH|cx_id", "amazon.ru"],
    [/* RU_DEVO */ "A38NPJYVS5YHNH|cx_family", "retail"],
    [/* SA */ "A17E79C6D8DWNP|cx_id", "amazon.sa"],
    [/* SA */ "A17E79C6D8DWNP|cx_family", "retail"],
    [/* SA_DEVO */ "A1MQPSGJ6U9Q54|cx_id", "amazon.sa"],
    [/* SA_DEVO */ "A1MQPSGJ6U9Q54|cx_family", "retail"],
    [/* SE */ "A2NODRKZP88ZB9|cx_id", "amazon.se"],
    [/* SE */ "A2NODRKZP88ZB9|cx_family", "retail"],
    [/* SE_DEVO */ "AT614YYYNOC1S|cx_id", "amazon.se"],
    [/* SE_DEVO */ "AT614YYYNOC1S|cx_family", "retail"],
    [/* SG */ "A19VAU5U5O7RUS|cx_id", "amazon.sg"],
    [/* SG */ "A19VAU5U5O7RUS|cx_family", "retail"],
    [/* SG_DEVO */ "A1DQ6JV7I20JSG|cx_id", "amazon.sg"],
    [/* SG_DEVO */ "A1DQ6JV7I20JSG|cx_family", "retail"],
    [/* TR */ "A33AVAJ2PDY3EV|cx_id", "amazon.com.tr"],
    [/* TR */ "A33AVAJ2PDY3EV|cx_family", "retail"],
    [/* TR_DEVO */ "A3CQBQD3RGPJR8|cx_id", "amazon.com.tr"],
    [/* TR_DEVO */ "A3CQBQD3RGPJR8|cx_family", "retail"],
    [/* UK */ "A1F83G8C2ARO7P|cx_id", "amazon.co.uk"],
    [/* UK */ "A1F83G8C2ARO7P|cx_family", "retail"],
    [/* US */ "ATVPDKIKX0DER|cx_id", "amazon.com"],
    [/* US */ "ATVPDKIKX0DER|cx_family", "retail"],
    [/* ZA */ "AE08WJ6YKNBMC|cx_id", "amazon.co.za"],
    [/* ZA */ "AE08WJ6YKNBMC|cx_family", "retail"],
    [/* ZA_DEVO */ "A3TFQ1G9L8TQ5L|cx_id", "amazon.co.za"],
    [/* ZA_DEVO */ "A3TFQ1G9L8TQ5L|cx_family", "retail"],
    // PYOP/AmazonPay MarketplaceId to CXTuple mappings created using AmazonPay site urls:
    // https://w.amazon.com/bin/view/AmazonPay/MarketplaceIds/
    [/* PYOP_CA_PROD */ "A2CE4MDCUAASDP|cx_id", "apa.amazon.ca"],
    [/* PYOP_CA_PROD */ "A2CE4MDCUAASDP|cx_family", "pyop"],
    [/* PYOP_CA_PROD_SANDBOX */ "A2VJI655WT2I0B|cx_id", "apa.amazon.ca"],
    [/* PYOP_CA_PROD_SANDBOX */ "A2VJI655WT2I0B|cx_family", "pyop"],
    [/* PYOP_CA_DEVO */ "A1NWP2E4EKQP94|cx_id", "apa.amazon.ca"],
    [/* PYOP_CA_DEVO */ "A1NWP2E4EKQP94|cx_family", "pyop"],
    [/* PYOP_CA_DEVO_SANDBOX */ "ASBG27DKM7EX6|cx_id", "apa.amazon.ca"],
    [/* PYOP_CA_DEVO_SANDBOX */ "ASBG27DKM7EX6|cx_family", "pyop"],
    [/* PYOP_DE_PROD */ "A53RDEWN57UU5|cx_id", "apa.amazon.de"],
    [/* PYOP_DE_PROD */ "A53RDEWN57UU5|cx_family", "pyop"],
    [/* PYOP_DE_PROD_SANDBOX */ "A1G8446IYHA4MR|cx_id", "apa.amazon.de"],
    [/* PYOP_DE_PROD_SANDBOX */ "A1G8446IYHA4MR|cx_family", "pyop"],
    [/* PYOP_DE_DEVO */ "A34RX6LWLXD2KA|cx_id", "apa.amazon.de"],
    [/* PYOP_DE_DEVO */ "A34RX6LWLXD2KA|cx_family", "pyop"],
    [/* PYOP_DE_DEVO_SANDBOX */ "A225AO9WA6YYOF|cx_id", "apa.amazon.de"],
    [/* PYOP_DE_DEVO_SANDBOX */ "A225AO9WA6YYOF|cx_family", "pyop"],
    [/* PYOP_IN_PROD */ "A3FDG49KKM823Y|cx_id", "apa.amazon.in"],
    [/* PYOP_IN_PROD */ "A3FDG49KKM823Y|cx_family", "pyop"],
    [/* PYOP_IN_PROD_SANDBOX */ "A1IAUC0NN979H6|cx_id", "apa.amazon.in"],
    [/* PYOP_IN_PROD_SANDBOX */ "A1IAUC0NN979H6|cx_family", "pyop"],
    [/* PYOP_IN_DEVO */ "A18VPDB9UTK24D|cx_id", "apa.amazon.in"],
    [/* PYOP_IN_DEVO */ "A18VPDB9UTK24D|cx_family", "pyop"],
    [/* PYOP_IN_DEVO_SANDBOX */ "A16Z2XWZJTFS70|cx_id", "apa.amazon.in"],
    [/* PYOP_IN_DEVO_SANDBOX */ "A16Z2XWZJTFS70|cx_family", "pyop"],
    [/* PYOP_JP_PROD */ "A75P1MG8VMU51|cx_id", "apa.amazon.co.jp"],
    [/* PYOP_JP_PROD */ "A75P1MG8VMU51|cx_family", "pyop"],
    [/* PYOP_JP_PROD_SANDBOX */ "A31YDYE76E6TCP|cx_id", "apa.amazon.co.jp"],
    [/* PYOP_JP_PROD_SANDBOX */ "A31YDYE76E6TCP|cx_family", "pyop"],
    [/* PYOP_JP_DEVO */ "A2EFPHRCZ60KI3|cx_id", "apa.amazon.co.jp"],
    [/* PYOP_JP_DEVO */ "A2EFPHRCZ60KI3|cx_family", "pyop"],
    [/* PYOP_JP_DEVO_SANDBOX */ "A1U1OSY46WLA42|cx_id", "apa.amazon.co.jp"],
    [/* PYOP_JP_DEVO_SANDBOX */ "A1U1OSY46WLA42|cx_family", "pyop"],
    [/* PYOP_US_PROD */ "AGWSWK15IEJJ7|cx_id", "apa.amazon.com"],
    [/* PYOP_US_PROD */ "AGWSWK15IEJJ7|cx_family", "pyop"],
    [/* PYOP_US_PROD_SANDBOX */ "A3BXB0YN3XH17H|cx_id", "apa.amazon.com"],
    [/* PYOP_US_PROD_SANDBOX */ "A3BXB0YN3XH17H|cx_family", "pyop"],
    [/* PYOP_US_DEVO */ "A35A4JO734ER04|cx_id", "apa.amazon.com"],
    [/* PYOP_US_DEVO */ "A35A4JO734ER04|cx_family", "pyop"],
    [/* PYOP_US_DEVO_SANDBOX */ "A341L3VCFKNMIY|cx_id", "apa.amazon.com"],
    [/* PYOP_US_DEVO_SANDBOX */ "A341L3VCFKNMIY|cx_family", "pyop"],
    [/* PYOP_UK_PROD */ "AZAJMM36N6WQL|cx_id", "apa.amazon.co.uk"],
    [/* PYOP_UK_PROD */ "AZAJMM36N6WQL|cx_family", "pyop"],
    [/* PYOP_UK_PROD_SANDBOX */ "A3M3RRFO9XDT2G|cx_id", "apa.amazon.co.uk"],
    [/* PYOP_UK_PROD_SANDBOX */ "A3M3RRFO9XDT2G|cx_family", "pyop"],
    [/* PYOP_UK_DEVO */ "A2UWQKHEFGNQY3|cx_id", "apa.amazon.co.uk"],
    [/* PYOP_UK_DEVO */ "A2UWQKHEFGNQY3|cx_family", "pyop"],
    [/* PYOP_UK_DEVO_SANDBOX */ "A2V84NPDZD3ZEH|cx_id", "apa.amazon.co.uk"],
    [/* PYOP_UK_DEVO_SANDBOX */ "A2V84NPDZD3ZEH|cx_family", "pyop"],
]);

var __importDefault$4 = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(marketplaceIdToCxTupleQueryResolver, "__esModule", { value: true });
const i18n_configuration_provider_1$e = dist$1;
const marketplace_id_to_cx_tuple_mapping_resource_1 = __importDefault$4(marketplaceIdToCxTupleMapping_resource);
const map_resource_query_resolver_1 = __importDefault$4(mapResourceQueryResolver);
/**
 * Resolves queries for Marketplace to CXTuple Mappings.
 */
class MarketplaceIdToCxTupleQueryResolver extends map_resource_query_resolver_1.default {
    constructor() {
        super();
    }
    resolve(attributeName, args) {
        this.validate(attributeName, args);
        return this.doResolve(attributeName, args);
    }
    doResolve(attributeName, args) {
        const configBuilder = new i18n_configuration_provider_1$e.I18nConfiguration.Builder();
        switch (attributeName) {
            case i18n_configuration_provider_1$e.MarketplaceIdToCxTuple.CX_ID:
                const cxId = this.getResource(attributeName, args);
                configBuilder.addConfiguration(i18n_configuration_provider_1$e.MarketplaceIdToCxTupleResult.CX_ID, cxId);
                break;
            case i18n_configuration_provider_1$e.MarketplaceIdToCxTuple.CX_FAMILY:
                const cxFamily = this.getResource(attributeName, args);
                configBuilder.addConfiguration(i18n_configuration_provider_1$e.MarketplaceIdToCxTupleResult.CX_FAMILY, cxFamily);
                break;
            default:
                throw new Error(`Attribute name ${attributeName} is not defined for feature ${attributeName.getConfigType()}`);
        }
        return configBuilder.build();
    }
    /**
     * Method to build a resource key and fetch the particular resource from a static map. The key is comprised of
     * the args and a config component in the format:
     * - obfuscatedMarketplaceId.cx_id
     * - obfuscatedMarketplaceId.cx_family
     *
     * @param attributeName The MarketplaceToCxId ConfigName for which the value needs to be fetched.
     * @param args MarketplaceIdToCxIdArgs used to build the resource key.
     * @return string resource from map.
     */
    getResource(attributeName, args) {
        let resourceKey = args.obfuscatedMarketplaceId;
        resourceKey += map_resource_query_resolver_1.default.MAP_ATTRIBUTE_KEY_SEPARATOR;
        resourceKey += attributeName;
        return this._resourceConfigRetriever.getContent().get(resourceKey);
    }
    createMapResourceConfigRetriever() {
        return new marketplace_id_to_cx_tuple_mapping_resource_1.default();
    }
    getTargetConfigType() {
        return MarketplaceIdToCxTupleQueryResolver.CONFIG_TYPE;
    }
    getValidationErrorMessage() {
        return `Marketplace to CXTuple configs can only be resolved for configType ${MarketplaceIdToCxTupleQueryResolver.CONFIG_TYPE}`;
    }
}
marketplaceIdToCxTupleQueryResolver.default = MarketplaceIdToCxTupleQueryResolver;
MarketplaceIdToCxTupleQueryResolver.CONFIG_TYPE = i18n_configuration_provider_1$e.ConfigType.MARKETPLACEID_TO_CX_TUPLE;

var priceFormatQueryResolver = {};

var priceFormatConfigRetriever = {};

var customSymbolOverrides = {};

var customSymbolOverridesDefault = {};

Object.defineProperty(customSymbolOverridesDefault, "__esModule", { value: true });
customSymbolOverridesDefault.DEFAULT_PRICE_FORMAT_OVERRIDES = void 0;
const i18n_configuration_provider_1$d = dist$1;
/**
 * Custom Symbol Overrides that are not locale specific.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$k = {
    'sek_amazon.se': {
        customSymbol: 'kr',
        insertSpaceInPattern: false
    },
    'pln_amazon.pl': {
        customSymbol: '\u007a\u0142',
        insertSpaceInPattern: false
    },
    'sgd': {
        customSymbol: 'S$',
        insertSpaceInPattern: false
    },
    'aed_amazon.ae': {
        customSymbol: 'AED',
        insertSpaceInPattern: true
    },
    'sar_amazon.sa': {
        customSymbol: 'SAR',
        insertSpaceInPattern: true
    },
    'egp_amazon.eg': {
        customSymbol: 'EGP',
        insertSpaceInPattern: true,
    },
    'try_amazon.com.tr': {
        customSymbol: 'TL',
        insertSpaceInPattern: true
    }
};
const SYMBOL_POSITION_OVERRIDES$k = {};
const TRAILING_ZERO_DISPLAY_OVERRIDES$1 = {
    // https://issues.amazon.com/P54777732
    'cop': i18n_configuration_provider_1$d.TrailingZeroDisplay.HIDE_IF_WHOLE,
    'ngn': i18n_configuration_provider_1$d.TrailingZeroDisplay.HIDE_IF_WHOLE,
};
customSymbolOverridesDefault.DEFAULT_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$k,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$k,
    trailingZeroDisplayOverrides: TRAILING_ZERO_DISPLAY_OVERRIDES$1
};

var customSymbolOverridesAr = {};

Object.defineProperty(customSymbolOverridesAr, "__esModule", { value: true });
customSymbolOverridesAr.AR_PRICE_FORMAT_OVERRIDES = void 0;
const i18n_configuration_provider_1$c = dist$1;
/**
 * Custom Symbol Overrides for the 'ar' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$j = {
    'aed_amazon.ae': {
        customSymbol: '\u062f\u0631\u0647\u0645',
        insertSpaceInPattern: true
    },
    'egp_amazon.eg': {
        customSymbol: '\u062c\u0646\u064a\u0647',
        insertSpaceInPattern: true
    },
    'sar_amazon.sa': {
        customSymbol: '\u0631\u064a\u0627\u0644',
        insertSpaceInPattern: true
    },
    /*
      This override addresses the issue that appears when rendering the USD currency symbol in the ar
      locale (US$) for negative numbers in US marketplace(https://issues.amazon.com/I18NFND-55).

      Safari browsers do not yet support bidi isolates such as FSI (U+2068) and PDI (U+2069) so an ALM
      (U+061C) is placed at the beginning of the currency symbol as a workaround for the time-being.
    */
    'usd_amazon.com': {
        customSymbol: '\u061CUS$',
        insertSpaceInPattern: false
    }
};
const SYMBOL_POSITION_OVERRIDES$j = {
    symbol_position: i18n_configuration_provider_1$c.CurrencySymbolPosition.END
};
customSymbolOverridesAr.AR_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$j,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$j,
    /*
        Add ALM as the prefix for non-marketplace default currencies. This ensures that
        we apply right-to-left ordering for the currency symbol (ISO code) relative to the
        rest of the formatted price string. This in particular becomes relevant for negative
        values, when the minus sign is prefixed with LRM from CLDR (https://issues.amazon.com/I18NFND-55).
     */
    symbolPrefix: '\u061C'
};

var customSymbolOverridesBn = {};

Object.defineProperty(customSymbolOverridesBn, "__esModule", { value: true });
customSymbolOverridesBn.BN_PRICE_FORMAT_OVERRIDES = void 0;
const i18n_configuration_provider_1$b = dist$1;
/**
 * Custom Symbol Overrides for the 'bn' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$i = {};
const SYMBOL_POSITION_OVERRIDES$i = {
    symbol_position: i18n_configuration_provider_1$b.CurrencySymbolPosition.START
};
customSymbolOverridesBn.BN_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$i,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$i
};

var customSymbolOverridesDa = {};

Object.defineProperty(customSymbolOverridesDa, "__esModule", { value: true });
customSymbolOverridesDa.DA_PRICE_FORMAT_OVERRIDES = void 0;
/**
 /**
 * Custom Symbol Overrides for the 'da' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$h = {
    'sek_amazon.se': {
        customSymbol: 'Skr',
        insertSpaceInPattern: false
    }
};
const SYMBOL_POSITION_OVERRIDES$h = {};
customSymbolOverridesDa.DA_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$h,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$h
};

var customSymbolOverridesEt = {};

Object.defineProperty(customSymbolOverridesEt, "__esModule", { value: true });
customSymbolOverridesEt.ET_PRICE_FORMAT_OVERRIDES = void 0;
/**
 * Custom Symbol Overrides for the 'et' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$g = {
    'sek_amazon.se': {
        customSymbol: 'Skr',
        insertSpaceInPattern: false
    }
};
const SYMBOL_POSITION_OVERRIDES$g = {};
customSymbolOverridesEt.ET_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$g,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$g
};

var customSymbolOverridesEnXD = {};

Object.defineProperty(customSymbolOverridesEnXD, "__esModule", { value: true });
customSymbolOverridesEnXD.EN_XD_PRICE_FORMAT_OVERRIDES = void 0;
/**
 * Custom Symbol Overrides for the pseudo locale 'en-XD' used for testing purposes.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$f = {};
const SYMBOL_POSITION_OVERRIDES$f = {};
customSymbolOverridesEnXD.EN_XD_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$f,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$f
};

var customSymbolOverridesEnPhnxXDUnifon = {};

Object.defineProperty(customSymbolOverridesEnPhnxXDUnifon, "__esModule", { value: true });
customSymbolOverridesEnPhnxXDUnifon.EN_PHNX_XD_UNIFON_PRICE_FORMAT_OVERRIDES = void 0;
/**
 * Custom Symbol Overrides for the pseudo locale 'en-Phnx-XD-unifon' used for testing purposes.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$e = {};
const SYMBOL_POSITION_OVERRIDES$e = {};
customSymbolOverridesEnPhnxXDUnifon.EN_PHNX_XD_UNIFON_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$e,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$e
};

var customSymbolOverridesEs = {};

Object.defineProperty(customSymbolOverridesEs, "__esModule", { value: true });
customSymbolOverridesEs.ES_PRICE_FORMAT_OVERRIDES = void 0;
/**
 * Custom Symbol Overrides for the 'es' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$d = {
    'usd_amazon.com_compact': {
        customSymbol: '$',
        insertSpaceInPattern: false
    },
    'usd_amazon.com': {
        customSymbol: 'US$',
        insertSpaceInPattern: false
    }
};
const SYMBOL_POSITION_OVERRIDES$d = {};
customSymbolOverridesEs.ES_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$d,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$d
};

var customSymbolOverridesFrCA = {};

Object.defineProperty(customSymbolOverridesFrCA, "__esModule", { value: true });
customSymbolOverridesFrCA.FR_CA_PRICE_FORMAT_OVERRIDES = void 0;
/**
 * Custom Symbol Overrides for the 'fr-CA' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$c = {
    'cad_amazon.ca': {
        customSymbol: '$',
        insertSpaceInPattern: false
    }
};
const SYMBOL_POSITION_OVERRIDES$c = {};
customSymbolOverridesFrCA.FR_CA_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$c,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$c
};

var customSymbolOverridesFrXD = {};

Object.defineProperty(customSymbolOverridesFrXD, "__esModule", { value: true });
customSymbolOverridesFrXD.FR_XD_PRICE_FORMAT_OVERRIDES = void 0;
const i18n_configuration_provider_1$a = dist$1;
/**
 * Custom Symbol Overrides for the pseudo locale 'fr-XD' used for testing purposes.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$b = {};
const SYMBOL_POSITION_OVERRIDES$b = {
    symbol_position: i18n_configuration_provider_1$a.CurrencySymbolPosition.END
};
customSymbolOverridesFrXD.FR_XD_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$b,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$b
};

var customSymbolOverridesFrXDUnifon = {};

Object.defineProperty(customSymbolOverridesFrXDUnifon, "__esModule", { value: true });
customSymbolOverridesFrXDUnifon.FR_XD_UNIFON_PRICE_FORMAT_OVERRIDES = void 0;
/**
 * Custom Symbol Overrides for the pseudo locale 'fr-XD-unifon' used for testing purposes.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$a = {};
const SYMBOL_POSITION_OVERRIDES$a = {};
customSymbolOverridesFrXDUnifon.FR_XD_UNIFON_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$a,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$a,
};

var customSymbolOverridesFrPhnxXDUnifon = {};

Object.defineProperty(customSymbolOverridesFrPhnxXDUnifon, "__esModule", { value: true });
customSymbolOverridesFrPhnxXDUnifon.FR_PHNX_XD_UNIFON_PRICE_FORMAT_OVERRIDES = void 0;
const i18n_configuration_provider_1$9 = dist$1;
/**
 * Custom Symbol Overrides for the pseudo locale 'fr-Phnx-XD-unifon' used for testing purposes.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$9 = {};
const SYMBOL_POSITION_OVERRIDES$9 = {
    symbol_position: i18n_configuration_provider_1$9.CurrencySymbolPosition.START
};
customSymbolOverridesFrPhnxXDUnifon.FR_PHNX_XD_UNIFON_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$9,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$9
};

var customSymbolOverridesHe = {};

Object.defineProperty(customSymbolOverridesHe, "__esModule", { value: true });
customSymbolOverridesHe.HE_PRICE_FORMAT_OVERRIDES = void 0;
/**
 * Custom Symbol Overrides for 'he' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$8 = {};
const SYMBOL_POSITION_OVERRIDES$8 = {};
customSymbolOverridesHe.HE_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$8,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$8,
    /*
        Add RLM as the prefix for non-marketplace default currencies. This ensures that
        we apply right-to-left ordering for the currency symbol (ISO code) relative to the
        rest of the formatted price string. This in particular becomes relevant for negative
        values, when the minus sign is prefixed with LRM from CLDR (https://issues.amazon.com/I18NFND-55).
     */
    symbolPrefix: '\u200F'
};

var customSymbolOverridesIs = {};

Object.defineProperty(customSymbolOverridesIs, "__esModule", { value: true });
customSymbolOverridesIs.IS_PRICE_FORMAT_OVERRIDES = void 0;
/**
 * Custom Symbol Overrides for the 'is' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$7 = {
    'sek_amazon.se': {
        customSymbol: 'Skr',
        insertSpaceInPattern: false
    }
};
const SYMBOL_POSITION_OVERRIDES$7 = {};
customSymbolOverridesIs.IS_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$7,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$7,
};

var customSymbolOverridesJa = {};

Object.defineProperty(customSymbolOverridesJa, "__esModule", { value: true });
customSymbolOverridesJa.JA_PRICE_FORMAT_OVERRIDES = void 0;
const i18n_configuration_provider_1$8 = dist$1;
/**
 * Custom Symbol Overrides for the 'ja' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$6 = {
    'jpy_insentence': {
        customSymbol: '\u5186',
        insertSpaceInPattern: false
    },
    'jpy_amazon.co.jp_compact': {
        customSymbol: '\u00A5',
        insertSpaceInPattern: false
    }
};
const SYMBOL_POSITION_OVERRIDES$6 = {
    'jpy_insentence': i18n_configuration_provider_1$8.CurrencySymbolPosition.END
};
customSymbolOverridesJa.JA_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$6,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$6
};

var customSymbolOverridesNb = {};

Object.defineProperty(customSymbolOverridesNb, "__esModule", { value: true });
customSymbolOverridesNb.NB_PRICE_FORMAT_OVERRIDES = void 0;
/**
 * Custom Symbol Overrides for the 'nb' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$5 = {
    'sek_amazon.se': {
        customSymbol: 'Skr',
        insertSpaceInPattern: false
    }
};
const SYMBOL_POSITION_OVERRIDES$5 = {};
customSymbolOverridesNb.NB_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$5,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$5
};

var customSymbolOverridesNn = {};

Object.defineProperty(customSymbolOverridesNn, "__esModule", { value: true });
customSymbolOverridesNn.NN_PRICE_FORMAT_OVERRIDES = void 0;
/**
 * Custom Symbol Overrides for the 'nn' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$4 = {
    'sek_amazon.se': {
        customSymbol: 'Skr',
        insertSpaceInPattern: false
    }
};
const SYMBOL_POSITION_OVERRIDES$4 = {};
customSymbolOverridesNn.NN_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$4,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$4
};

var customSymbolOverridesNo = {};

Object.defineProperty(customSymbolOverridesNo, "__esModule", { value: true });
customSymbolOverridesNo.NO_PRICE_FORMAT_OVERRIDES = void 0;
/**
 * Custom Symbol Overrides for the 'no' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$3 = {
    'sek_amazon.se': {
        customSymbol: 'Skr',
        insertSpaceInPattern: false
    }
};
const SYMBOL_POSITION_OVERRIDES$3 = {};
customSymbolOverridesNo.NO_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$3,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$3
};

var customSymbolOverridesTr = {};

Object.defineProperty(customSymbolOverridesTr, "__esModule", { value: true });
customSymbolOverridesTr.TR_PRICE_FORMAT_OVERRIDES = void 0;
const i18n_configuration_provider_1$7 = dist$1;
/**
 * Custom Symbol Overrides for the 'tr' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$2 = {};
// Remove this override when the symbol placement on end has been made default in CLDR:
// https://unicode-org.atlassian.net/browse/CLDR-14070
const SYMBOL_POSITION_OVERRIDES$2 = {
    symbol_position: i18n_configuration_provider_1$7.CurrencySymbolPosition.END
};
customSymbolOverridesTr.TR_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$2,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$2
};

var customSymbolOverridesZh = {};

Object.defineProperty(customSymbolOverridesZh, "__esModule", { value: true });
customSymbolOverridesZh.ZH_PRICE_FORMAT_OVERRIDES = void 0;
const i18n_configuration_provider_1$6 = dist$1;
/**
 * Custom Symbol Overrides for the 'zh' locale.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES$1 = {
    'cny_insentence': {
        customSymbol: '\u5143',
        insertSpaceInPattern: false
    },
    'jpy_insentence': {
        customSymbol: '\u65E5\u5143',
        insertSpaceInPattern: false
    }
};
const SYMBOL_POSITION_OVERRIDES$1 = {
    'jpy_insentence': i18n_configuration_provider_1$6.CurrencySymbolPosition.END,
    'cny_insentence': i18n_configuration_provider_1$6.CurrencySymbolPosition.END
};
customSymbolOverridesZh.ZH_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES$1,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES$1
};

var customSymbolOverridesHiXD = {};

Object.defineProperty(customSymbolOverridesHiXD, "__esModule", { value: true });
customSymbolOverridesHiXD.HI_XD_PRICE_FORMAT_OVERRIDES = void 0;
const i18n_configuration_provider_1$5 = dist$1;
/**
 * Custom Symbol Overrides for the pseudo locale 'hi-XD' used for testing purposes.
 * Refer to the i18n.custom-symbol-overrides package's README.md document to understand how to use this file.
 */
const CUSTOM_SYMBOL_OVERRIDES = {};
const SYMBOL_POSITION_OVERRIDES = {};
const TRAILING_ZERO_DISPLAY_OVERRIDES = {
    'inr_amazon.in_insentence': i18n_configuration_provider_1$5.TrailingZeroDisplay.HIDE_IF_WHOLE,
    'inr_amazon.in_amazonsymbol': i18n_configuration_provider_1$5.TrailingZeroDisplay.HIDE_IF_WHOLE,
};
customSymbolOverridesHiXD.HI_XD_PRICE_FORMAT_OVERRIDES = {
    symbolPosition: SYMBOL_POSITION_OVERRIDES,
    customSymbolData: CUSTOM_SYMBOL_OVERRIDES,
    trailingZeroDisplayOverrides: TRAILING_ZERO_DISPLAY_OVERRIDES
};

var require$$22 = /*@__PURE__*/getAugmentedNamespace(src);

Object.defineProperty(customSymbolOverrides, "__esModule", { value: true });
customSymbolOverrides.CustomSymbolOverrides = void 0;
const custom_symbol_overrides_default_1 = customSymbolOverridesDefault;
const custom_symbol_overrides_ar_1 = customSymbolOverridesAr;
const custom_symbol_overrides_bn_1 = customSymbolOverridesBn;
const custom_symbol_overrides_da_1 = customSymbolOverridesDa;
const custom_symbol_overrides_et_1 = customSymbolOverridesEt;
const custom_symbol_overrides_en_XD_1 = customSymbolOverridesEnXD;
const custom_symbol_overrides_en_Phnx_XD_unifon_1 = customSymbolOverridesEnPhnxXDUnifon;
const custom_symbol_overrides_es_1 = customSymbolOverridesEs;
const custom_symbol_overrides_fr_CA_1 = customSymbolOverridesFrCA;
const custom_symbol_overrides_fr_XD_1 = customSymbolOverridesFrXD;
const custom_symbol_overrides_fr_XD_unifon_1 = customSymbolOverridesFrXDUnifon;
const custom_symbol_overrides_fr_Phnx_XD_unifon_1 = customSymbolOverridesFrPhnxXDUnifon;
const custom_symbol_overrides_he_1 = customSymbolOverridesHe;
const custom_symbol_overrides_is_1 = customSymbolOverridesIs;
const custom_symbol_overrides_ja_1 = customSymbolOverridesJa;
const custom_symbol_overrides_nb_1 = customSymbolOverridesNb;
const custom_symbol_overrides_nn_1 = customSymbolOverridesNn;
const custom_symbol_overrides_no_1 = customSymbolOverridesNo;
const custom_symbol_overrides_tr_1 = customSymbolOverridesTr;
const custom_symbol_overrides_zh_1 = customSymbolOverridesZh;
const custom_symbol_overrides_hi_XD_1 = customSymbolOverridesHiXD;
const i18n_configuration_provider_1$4 = dist$1;
const i18n_locale_utilities_1 = require$$22;
const DEFAULT_KEY = 'default';
const FIELD_ORDER_SEPARATOR = '_';
const SYMBOL_PREFIX_KEY = 'symbolPrefix';
const LOCALE_SEPARATOR = '-';
const LOCALE_SEPARATOR_REGEX = new RegExp(LOCALE_SEPARATOR, 'g');
const SYMBOL_POSITION = 'symbol_position';
// Map containing the number shapes and custom symbol overrides by language, when a new
// custom-symbol-overrides-*.ts file is added it should be added to this map.
const PRICE_FORMAT_OVERRIDES_MAP = {
    ar: custom_symbol_overrides_ar_1.AR_PRICE_FORMAT_OVERRIDES,
    bn: custom_symbol_overrides_bn_1.BN_PRICE_FORMAT_OVERRIDES,
    da: custom_symbol_overrides_da_1.DA_PRICE_FORMAT_OVERRIDES,
    et: custom_symbol_overrides_et_1.ET_PRICE_FORMAT_OVERRIDES,
    es: custom_symbol_overrides_es_1.ES_PRICE_FORMAT_OVERRIDES,
    fr_ca: custom_symbol_overrides_fr_CA_1.FR_CA_PRICE_FORMAT_OVERRIDES,
    he: custom_symbol_overrides_he_1.HE_PRICE_FORMAT_OVERRIDES,
    is: custom_symbol_overrides_is_1.IS_PRICE_FORMAT_OVERRIDES,
    ja: custom_symbol_overrides_ja_1.JA_PRICE_FORMAT_OVERRIDES,
    nb: custom_symbol_overrides_nb_1.NB_PRICE_FORMAT_OVERRIDES,
    nn: custom_symbol_overrides_nn_1.NN_PRICE_FORMAT_OVERRIDES,
    no: custom_symbol_overrides_no_1.NO_PRICE_FORMAT_OVERRIDES,
    tr: custom_symbol_overrides_tr_1.TR_PRICE_FORMAT_OVERRIDES,
    zh: custom_symbol_overrides_zh_1.ZH_PRICE_FORMAT_OVERRIDES,
    // Test locales
    en_xd: custom_symbol_overrides_en_XD_1.EN_XD_PRICE_FORMAT_OVERRIDES,
    en_phnx_xd_unifon: custom_symbol_overrides_en_Phnx_XD_unifon_1.EN_PHNX_XD_UNIFON_PRICE_FORMAT_OVERRIDES,
    fr_xd: custom_symbol_overrides_fr_XD_1.FR_XD_PRICE_FORMAT_OVERRIDES,
    fr_xd_unifon: custom_symbol_overrides_fr_XD_unifon_1.FR_XD_UNIFON_PRICE_FORMAT_OVERRIDES,
    fr_phnx_xd_unifon: custom_symbol_overrides_fr_Phnx_XD_unifon_1.FR_PHNX_XD_UNIFON_PRICE_FORMAT_OVERRIDES,
    hi_xd: custom_symbol_overrides_hi_XD_1.HI_XD_PRICE_FORMAT_OVERRIDES,
    [DEFAULT_KEY]: custom_symbol_overrides_default_1.DEFAULT_PRICE_FORMAT_OVERRIDES
};
/**
 * This class is based on the Java ResourceBundle which allows for language
 * specific overrides. It implements methods to get {@link CustomSymbolData} and
 * a LocaleCustomNumberFormat.
 */
class CustomSymbolOverrides {
    /**
     * Given a locale create a list of available ResourceBundles which have locale specific
     * overrides or the default overrides.
     * @param {string} locale
     */
    constructor(locale) {
        this.overrides = [];
        this.intlLocale = i18n_locale_utilities_1.LocaleUtil.getCanonicalLocale(new Intl.Locale(locale));
        // Generate fallback chain of override configs similar to Java ResourceBundle:
        // https://docs.oracle.com/javase/8/docs/api/java/util/ResourceBundle.html#getBundle-java.lang.String-java.util.Locale-java.lang.ClassLoader-
        const overrideKeys = [];
        // loop the complete base name
        let loc = this.intlLocale.baseName;
        loc = loc.replace(LOCALE_SEPARATOR_REGEX, FIELD_ORDER_SEPARATOR);
        let pos = loc.lastIndexOf(FIELD_ORDER_SEPARATOR);
        while (pos > 0) {
            overrideKeys.push(loc.toLocaleLowerCase('und'));
            loc = loc.substring(0, pos);
            pos = loc.lastIndexOf(FIELD_ORDER_SEPARATOR);
        }
        // if script exists, remove it and re-loop
        if (this.intlLocale.script) {
            // @ts-ignore
            const tmp = new Intl.Locale(this.intlLocale.baseName.replace(LOCALE_SEPARATOR + this.intlLocale.script, ''));
            loc = tmp.baseName;
            loc = loc.replace(LOCALE_SEPARATOR_REGEX, FIELD_ORDER_SEPARATOR);
            pos = loc.lastIndexOf(FIELD_ORDER_SEPARATOR);
            while (pos > 0) {
                overrideKeys.push(loc.toLocaleLowerCase('und'));
                loc = loc.substring(0, pos);
                pos = loc.lastIndexOf(FIELD_ORDER_SEPARATOR);
            }
        }
        // capture the primary language subtag
        overrideKeys.push(loc.toLocaleLowerCase('und'));
        for (const key of overrideKeys) {
            const priceFormatOverride = PRICE_FORMAT_OVERRIDES_MAP[key];
            if (priceFormatOverride) {
                this.overrides.push(priceFormatOverride);
            }
        }
        this.overrides.push(PRICE_FORMAT_OVERRIDES_MAP[DEFAULT_KEY]);
    }
    /**
     * Get the custom symbol data for currency code. Checks the overrides in the following manner:
     * Custom Symbol Overrides takes precedence over locale specific overrides and contains key formats and
     * hierarchy for symbol override:
     *                           [currencyCode]_[obfuscatedMarketplaceId]_[type]
     *                           [currencyCode]_[obfuscatedMarketplaceId]
     *                           [currencyCode]_[type]
     *                           [currencyCode]
     * value - CustomSymbolData Object
     *         arg1: customSymbol
     *                - custom symbol to be used as override
     *         arg2: insertSpaceInPattern
     *                - optional argument, set to false by default. Boolean to specify whether a SPACE is required between
     *                  the symbol and price in case the required format is not provided by the default marketplace format.
     *
     * @param {FallbackKeyParams} fallbackKeyParams - contains the params to generate the resoruce fallback keys
     * @return {Nullable<CustomSymbolData>} The CustomSymbolData object if present, else null.
     */
    getCustomSymbolData(fallbackKeyParams) {
        const fallbackKeys = this.getResourceFallbackKeys(fallbackKeyParams);
        for (const override of this.overrides) {
            const resourceValue = this.getResourceValue(override.customSymbolData, fallbackKeys);
            if (resourceValue) {
                return resourceValue;
            }
        }
        return null;
    }
    /**
     * Get the resource value from overrides.
     * @type T the type of value in the override record.
     * @param overrides the records that contains the overrides
     * @param fallbackKeys the list of keys to fetch override in order
     * @return the first override value with type T
     */
    getResourceValue(overrides, fallbackKeys) {
        for (const fallbackKey of fallbackKeys) {
            if (this.containsKey(fallbackKey, overrides)) {
                return overrides[fallbackKey];
            }
        }
        return null;
    }
    /**
     * Generate the resource fallback keys in the hierarchy orders
     *    [currencyCode]_[cxId]_[type]
     *    [currencyCode]_[cxId]
     *    [currencyCode]_[type]
     *    [currencyCode]
     * @param fallbackKeyParams contains all params to generate the resource fallback keys.
     * @returns the list of resource fallback keys
     */
    getResourceFallbackKeys(fallbackKeyParams) {
        const fallbackKeys = [];
        if (!fallbackKeyParams.currencyCode) {
            // return empty list when no currency code is specify
            // the default attribute name will be used.
            return [];
        }
        let typeName = fallbackKeyParams.typeName;
        if (typeName) {
            typeName = typeName.toLowerCase();
        }
        const currency = fallbackKeyParams.currencyCode.toLowerCase();
        let cxId = fallbackKeyParams.cxId;
        if (cxId) {
            cxId = cxId.toLowerCase();
            fallbackKeys.push(getResourceKey(currency, cxId, typeName), getResourceKey(currency, cxId));
        }
        fallbackKeys.push(getResourceKey(currency, typeName), getResourceKey(currency));
        return fallbackKeys;
    }
    /**
     * Get overrides applicable for non-marketplace default currencies.
     *
     * @return {string|undefined} Symbol prefix if present, null otherwise.
     */
    getSymbolPrefix() {
        for (const override of this.overrides) {
            if (this.containsKey(SYMBOL_PREFIX_KEY, override)) {
                return override.symbolPrefix;
            }
        }
        return null;
    }
    /**
     * Get custom symbol position for overriding the position for symbol.
     * If no overrides are present, then returns Default.
     * @return {CurrencySymbolPosition} symbol position for overriding currency symbol position
     */
    getSymbolPositionOverride(fallbackKeyParams) {
        const fallbackKeys = [];
        if (fallbackKeyParams) {
            fallbackKeys.push(...this.getResourceFallbackKeys(fallbackKeyParams));
        }
        fallbackKeys.push(SYMBOL_POSITION);
        for (const override of this.overrides) {
            const resourceValue = this.getResourceValue(override.symbolPosition, fallbackKeys);
            if (resourceValue) {
                return resourceValue;
            }
        }
        return i18n_configuration_provider_1$4.CurrencySymbolPosition.DEFAULT;
    }
    /**
     * Get the trailing zero display mode to control the display of trailing zero.
     * If no overrides are present, then return null.
     *
     * @param fallbackKeyParams fallback key params that used to generate fallback keys
     * @returns the trailing zero display mode. Null if not present.
     */
    getTrailingZeroDisplay(fallbackKeyParams) {
        const fallbackKeys = this.getResourceFallbackKeys(fallbackKeyParams);
        for (const override of this.overrides) {
            if (!override.trailingZeroDisplayOverrides) {
                continue;
            }
            const resourceValue = this.getResourceValue(override.trailingZeroDisplayOverrides, fallbackKeys);
            if (resourceValue) {
                return resourceValue;
            }
        }
        return null;
    }
    /**
     * Check whether the overrides contain a particular key.
     * @param {string} key Overrides key
     * @param {Object.<string, any>} overrides Overrides
     */
    containsKey(key, overrides) {
        if (!overrides) {
            return false;
        }
        return overrides.hasOwnProperty(key);
    }
}
customSymbolOverrides.CustomSymbolOverrides = CustomSymbolOverrides;
/**
 * Constructs key for resource retrieval from the CustomSymbolOverrides based on order of key field inputs.
 *
 * @param {ReadonlyArray<string>} keyFields - The fields used to construct the resource key.
 * @return {string} The key string used to fetch the relevant resource from the CustomSymbolOverrides.
 */
function getResourceKey(...keyfields) {
    if (!keyfields || keyfields.length === 0) {
        throw new Error('The key field(s) must not be null or empty');
    }
    return keyfields.join(FIELD_ORDER_SEPARATOR);
}

Object.defineProperty(priceFormatConfigRetriever, "__esModule", { value: true });
const custom_symbol_overrides_1 = customSymbolOverrides;
class PriceFormatConfigRetriever {
    constructor(_locale) {
        this._locale = _locale;
    }
    getContent() {
        return new custom_symbol_overrides_1.CustomSymbolOverrides(this._locale);
    }
}
priceFormatConfigRetriever.default = PriceFormatConfigRetriever;

var resourceQueryResolver = {};

Object.defineProperty(resourceQueryResolver, "__esModule", { value: true });
const i18n_configuration_provider_1$3 = dist$1;
/**
 * Base class to resolve Resource configuration queries
 * @type T the type of the configuration resource.
 */
class ResourceQueryResolver extends i18n_configuration_provider_1$3.QueryResolver {
    constructor(_locale) {
        super();
        this._locale = _locale;
        this.initialize();
    }
    initialize() {
        if (!this.isInitialized()) {
            try {
                this._resourceConfigRetriever = this.createResourceConfigRetriever();
                this.setInitializedState(true);
            }
            catch (e) {
                console.error(`Failed to initialize query resolver ${this.constructor.name}`);
            }
        }
        return this.isInitialized();
    }
}
resourceQueryResolver.default = ResourceQueryResolver;

var __importDefault$3 = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(priceFormatQueryResolver, "__esModule", { value: true });
const i18n_configuration_provider_1$2 = dist$1;
const price_format_config_retriever_1 = __importDefault$3(priceFormatConfigRetriever);
const resource_query_resolver_1 = __importDefault$3(resourceQueryResolver);
/**
 * Resolves queries for PriceFormat overrides.
 */
class PriceFormatQueryResolver extends resource_query_resolver_1.default {
    static create(args) {
        if (args instanceof i18n_configuration_provider_1$2.PriceFormatArgs) {
            const locale = args.locale;
            return new PriceFormatQueryResolver(!(0, i18n_configuration_provider_1$2.isNullOrUndefined)(locale) ? locale : 'und');
        }
        throw new Error(`Cannot create PriceFormatResourceBundleQueryResolver using args of type ${typeof args}.`
            + `Use RetailPriceFormatArgs to query Price Format data.`);
    }
    resolve(attributeName, args) {
        this.validate(attributeName, args);
        return this.doResolve(attributeName, args);
    }
    doResolve(attributeName, args) {
        const configBuilder = new i18n_configuration_provider_1$2.I18nConfiguration.Builder();
        const customCurrency = this._resourceConfigRetriever.getContent();
        const fallbackKeyParams = {
            currencyCode: args.currency,
            typeName: args.priceFormatType,
            cxId: args.cxId
        };
        switch (attributeName) {
            case (i18n_configuration_provider_1$2.PriceFormat.CURRENCY_FORMAT):
                const customSymbol = customCurrency.getCustomSymbolData(fallbackKeyParams);
                if (!(0, i18n_configuration_provider_1$2.isNullOrUndefined)(customSymbol)) {
                    configBuilder.addConfiguration(i18n_configuration_provider_1$2.PriceFormatResult.SYMBOL, customSymbol.customSymbol);
                    configBuilder.addConfiguration(i18n_configuration_provider_1$2.PriceFormatResult.INSERT_SPACE_IN_PATTERN, customSymbol.insertSpaceInPattern);
                    configBuilder.addConfiguration(i18n_configuration_provider_1$2.PriceFormatResult.FORMAT_PATTERN, customSymbol.formatPattern);
                }
                break;
            case (i18n_configuration_provider_1$2.PriceFormat.CURRENCY_SYMBOL_POSITION):
                configBuilder.addConfiguration(i18n_configuration_provider_1$2.PriceFormatResult.CURRENCY_SYMBOL_POSITION, customCurrency.getSymbolPositionOverride(fallbackKeyParams) || i18n_configuration_provider_1$2.CurrencySymbolPosition.DEFAULT);
                break;
            case (i18n_configuration_provider_1$2.PriceFormat.SYMBOL_PREFIX):
                configBuilder.addConfiguration(i18n_configuration_provider_1$2.PriceFormatResult.SYMBOL_PREFIX, customCurrency.getSymbolPrefix());
                break;
            case (i18n_configuration_provider_1$2.PriceFormat.TRAILING_ZERO_DISPLAY):
                configBuilder.addConfiguration(i18n_configuration_provider_1$2.PriceFormatResult.TRAILING_ZERO_DISPLAY, customCurrency.getTrailingZeroDisplay(fallbackKeyParams) || i18n_configuration_provider_1$2.TrailingZeroDisplay.AUTO);
                break;
            default:
                throw new Error(`Attribute name ${attributeName} is not defined for feature ${attributeName.getConfigType()}`);
        }
        return configBuilder.build();
    }
    validate(name, args) {
        if (name.getConfigType() !== this.getTargetConfigType()
            || args.getConfigType() !== PriceFormatQueryResolver.SPECIFIC_CONFIG_TYPE) {
            throw new Error(this.getValidationErrorMessage());
        }
    }
    createResourceConfigRetriever() {
        return new price_format_config_retriever_1.default(this._locale);
    }
    getTargetConfigType() {
        return PriceFormatQueryResolver.CONFIG_TYPE;
    }
    getValidationErrorMessage() {
        return `Retail Price Format configs can only be resolved for` +
            `configType ${PriceFormatQueryResolver.CONFIG_TYPE} and args type ${PriceFormatQueryResolver.SPECIFIC_CONFIG_TYPE}`;
    }
}
priceFormatQueryResolver.default = PriceFormatQueryResolver;
PriceFormatQueryResolver.CONFIG_TYPE = i18n_configuration_provider_1$2.ConfigType.PRICE_FORMAT;
PriceFormatQueryResolver.SPECIFIC_CONFIG_TYPE = i18n_configuration_provider_1$2.ConfigType.RETAIL_PRICE_FORMAT;

var __importDefault$2 = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(resolverFactory, "__esModule", { value: true });
const i18n_configuration_provider_1$1 = dist$1;
const cx_tuple_to_default_preferences_query_resolver_1 = __importDefault$2(cxTupleToDefaultPreferencesQueryResolver);
const cx_tuple_to_i18n_domain_query_resolver_1 = __importDefault$2(cxTupleToI18nDomainQueryResolver);
const marketplace_id_to_cx_tuple_query_resolver_1 = __importDefault$2(marketplaceIdToCxTupleQueryResolver);
const price_format_query_resolver_1 = __importDefault$2(priceFormatQueryResolver);
/**
 * Factory to manage all {@link QueryResolver} instances.
 */
class ResolverFactory {
    /**
     * Gets the QueryResolver instance that resolves the given {@link I18nArgs}.
     *
     * @param args    the args that used to fetch configuration
     *
     * @return Mapped {@link QueryResolver}
     *
     * @throws Error thrown if a Resolver was found, but the resolver has
     *             not been initialized
     */
    static getResolver(args) {
        let resolver;
        switch (args.getConfigType()) {
            case i18n_configuration_provider_1$1.ConfigType.RETAIL_PRICE_FORMAT:
                resolver = price_format_query_resolver_1.default.create(args);
                break;
            case i18n_configuration_provider_1$1.ConfigType.MARKETPLACEID_TO_CX_TUPLE:
                resolver = new marketplace_id_to_cx_tuple_query_resolver_1.default();
                break;
            case i18n_configuration_provider_1$1.ConfigType.CX_TUPLE_TO_I18N_DOMAIN:
                resolver = new cx_tuple_to_i18n_domain_query_resolver_1.default();
                break;
            case i18n_configuration_provider_1$1.ConfigType.CX_TUPLE_TO_DEFAULT_PREFERENCES:
                resolver = new cx_tuple_to_default_preferences_query_resolver_1.default();
                break;
            default:
                throw new Error(`No resolver has been registered for config type ${args.getConfigType()}`);
        }
        return resolver;
    }
}
resolverFactory.default = ResolverFactory;

var __importDefault$1 = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(resourceBundleI18nConfigurationProvider, "__esModule", { value: true });
const i18n_configuration_provider_1 = dist$1;
const resolver_factory_1 = __importDefault$1(resolverFactory);
/**
 * {@link I18nConfigurationProvider} implementation that reads configuration
 * using the ACS offline client.
 */
class ResourceBundleI18nConfigurationProvider$1 extends i18n_configuration_provider_1.I18nConfigurationProvider {
    resolveConfiguration(name, args) {
        // I18nArgs can be more specific (i.e in case of Price Format).
        // Use the ConfigType as QueryResolvers are registered by specific ConfigTypes.
        const resolver = resolver_factory_1.default.getResolver(args);
        return resolver.resolve(name, args);
    }
}
resourceBundleI18nConfigurationProvider.default = ResourceBundleI18nConfigurationProvider$1;

var __importDefault = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(dist, "__esModule", { value: true });
var ResourceBundleI18nConfigurationProvider = dist.ResourceBundleI18nConfigurationProvider = void 0;
const resource_bundle_i18n_configuration_provider_1 = __importDefault(resourceBundleI18nConfigurationProvider);
ResourceBundleI18nConfigurationProvider = dist.ResourceBundleI18nConfigurationProvider = resource_bundle_i18n_configuration_provider_1.default;

class MarketplaceToCXTupleResolver {
    constructor(_configProvider) {
        this._configProvider = _configProvider;
    }
    resolve(marketplace) {
        if (dist$1.isNullOrUndefined(marketplace) || dist$1.isNullOrUndefined(marketplace.getObfuscatedMarketplaceId())) {
            return CXTuple.UNKNOWN;
        }
        const args = new dist$1.MarketplaceIdToCxTupleArgs.Builder()
            .withObfuscatedMarketplaceId(marketplace.getObfuscatedMarketplaceId())
            .build();
        const cxFamily = this._configProvider.getConfiguration(dist$1.MarketplaceIdToCxTuple.CX_FAMILY, args).getStringValue();
        const cxId = this._configProvider.getConfiguration(dist$1.MarketplaceIdToCxTuple.CX_ID, args).getStringValue();
        return dist$1.isNullOrUndefined(cxFamily) || dist$1.isNullOrUndefined(cxId) ? CXTuple.UNKNOWN : CXTuple.of(cxFamily, cxId);
    }
}

class CXTupleToDefaultPreferencesResolver {
    constructor(_configProvider) {
        this._configProvider = _configProvider;
    }
    resolveDefaultCurrency(cxTuple) {
        const args = new dist$1.CXTupleToDefaultPreferencesArgs.Builder()
            .withCxFamily(cxTuple.cxFamily)
            .withCxId(cxTuple.cxFamily)
            .build();
        const configValue = this._configProvider
            .getConfiguration(dist$1.CXTupleToDefaultPreferences.DEFAULT_CURRENCY, args)
            .getStringValue();
        return configValue || '';
    }
    resolveDefaultLanguage(cxTuple) {
        const args = new dist$1.CXTupleToDefaultPreferencesArgs.Builder()
            .withCxFamily(cxTuple.cxFamily)
            .withCxId(cxTuple.cxFamily)
            .build();
        const configValue = this._configProvider
            .getConfiguration(dist$1.CXTupleToDefaultPreferences.DEFAULT_LANGUAGE, args)
            .getStringValue();
        return configValue || '';
    }
}

/**
 * The I18nConfigurationProvider and PriceFormat interface defines couple of duplicate enums.
 * For example, Type, TrailingZeroDisplay, CurrencySymbolPosition.
 *
 * This method loads the mappings and tries to get the corresponding enum instance defined in I18nConfigurationProvider.
 *
 * @param e the enum instance defined in PriceFormat interface.
 * @param mappings the map contains the mapping from PriceFormat enum to I18nConfigurationProvider enum.
 * @return the corresponding enum instance defined in I18nConfigurationProvider.
 */
function convertToConfig(e, mappings) {
    if (e == null) {
        return null;
    }
    if (mappings.has(e)) {
        return mappings.get(e);
    }
    throw new Error(`Failed to convert to config enum with ${e}`);
}
/**
 * The I18nConfigurationProvider and PriceFormat interface defines couple of duplicate enums.
 * For example, Type, TrailingZeroDisplay, CurrencySymbolPosition.
 *
 * This method loads the mappings and tries to get the corresponding enum instance defined in PriceFormat interface.
 *
 * @param e the enum instance defined in I18nConfigurationProvider.
 * @param mappings the map contains the mapping from PriceFormat enum to I18nConfigurationProvider enum.
 * @return the corresponding enum instance defined in PriceFormat interface.
 */
function convertFromConfig(t, mappings) {
    if (t == null) {
        return null;
    }
    for (const [key, val] of mappings.entries()) {
        if (val == t) {
            return key;
        }
    }
    throw new Error(`Failed to convert from config enum with ${t}`);
}
/**
 * Remove the target object from array if present. Otherwise, no action is taken.
 */
function removeIfPresent(arr, target) {
    if (arr.includes(target)) {
        arr.splice(arr.indexOf(target), 1);
    }
}

var _a;
const CURRENCY_STYLE = 'currency';
const CURRENCY_TYPE = 'currency';
const LITERAL_TYPE = 'literal';
const MINUS_SIGN_TYPE = 'minusSign';
const FRACTION_TYPE = 'fraction';
const DECIMAL_TYPE = 'decimal';
const INTEGER_TYPE = 'integer';
const GROUP_TYPE = 'group';
const NO_BREAK_SPACE = '\u00A0';
const SPECIAL_CHARS_REGEX = new RegExp('[\u200e\u200f\u202a-\u202e\u2066-\u2069\u061c]+');
const NUMBER_FORMAT_LRU_CACHE_SIZE = typeof process !== 'undefined' && process.env.I18N_PRICE_FORMAT_NUMBER_FORMAT_CACHE_SIZE
    ? parseInt(process.env.I18N_PRICE_FORMAT_NUMBER_FORMAT_CACHE_SIZE, 10)
    : 1000;
const OVERRIDE_SUPPORTED_CX_FAMILIES = new Set(['retail']);
const CURRENCY_SYMBOL_POSITION_MAPPINGS = new Map([
    ["end" /* CurrencySymbolPosition.END */, dist$1.CurrencySymbolPosition.END],
    ["start" /* CurrencySymbolPosition.START */, dist$1.CurrencySymbolPosition.START],
    ["default" /* CurrencySymbolPosition.DEFAULT */, dist$1.CurrencySymbolPosition.DEFAULT]
]);
const TRAILING_ZERO_DISPLAY_MAPPINGS = new Map([
    ["HIDE_IF_WHOLE" /* TrailingZeroDisplay.HIDE_IF_WHOLE */, dist$1.TrailingZeroDisplay.HIDE_IF_WHOLE],
    ["AUTO" /* TrailingZeroDisplay.AUTO */, dist$1.TrailingZeroDisplay.AUTO]
]);
const PRICE_FORMAT_TYPE_MAPPINGS = new Map([
    ["inSentence" /* Type.InSentence */, dist$1.PriceFormatType.IN_SENTENCE],
    ["amazonSymbol" /* Type.AmazonSymbol */, dist$1.PriceFormatType.AMAZON_SYMBOL],
    ["iso" /* Type.Iso */, dist$1.PriceFormatType.ISO],
    ["compact" /* Type.Compact */, dist$1.PriceFormatType.COMPACT],
    ["plural" /* Type.Plural */, dist$1.PriceFormatType.PLURAL]
]);
/**
 * {@link PriceFormat} is a class for formatting price.
 *
 * This file provides a set of factory methods for obtaining the formatters based on the given locale,
 * a formatting type, currency, .... The formatting types are Iso, Compact, Plural, InSentence and AmazonSymbol.
 * More details and sample examples of using these formatting types are provided in the {@link Type}.
 *
 * If you are formatting multiple prices, it is more efficient to get the PriceFormat and use it multiple times
 * so that the system doesn't have to fetch the information about the local language and country conventions multiple
 * times.
 *
 * Create global marketplace settings by 'MarketplaceCreator.setProvider(<MarketplaceProvider object>)'
 * If {@link MarketplaceCreator} set with custom provider, marketplace is generated by the specified provider.
 * Otherwise, {@link EmptyMarketplaceProvider} with undefined marketplaceId, locale and currencyCode is used by default.
 *
 * In application, passing marketplace setting through getInstance methods is supported.
 * The marketplaceId or marketplace object overrides the marketplace provider's specified settings.
 */
class PriceFormat {
    /**
     * Creates a PriceFormat class.
     * @param {string} locale - The requested locale
     * @param {Type} type - The type for formatting price
     * @param {Marketplace} marketplace - The marketplace object
     * @param {LocalizationContext} localizationContext - The LocalizationContext object
     * @param {TrailingZeroDisplay} trailingZeroDisplay - The Configuration that controls how the trailing zero display.
     */
    constructor(locale, type, marketplace, localizationContext, trailingZeroDisplay) {
        this.numberFormatCache = new QuickLRU$1({ maxSize: NUMBER_FORMAT_LRU_CACHE_SIZE });
        // @ts-ignore
        this.type = type;
        this.trailingZeroDisplay = trailingZeroDisplay;
        this.localizationContext = localizationContext;
        // Load configuration from I18nConfigurationProvider
        this.configurationProvider = this.resolveConfigProvider();
        const marketplaceToCXTupleResolver = new MarketplaceToCXTupleResolver(this.configurationProvider);
        const cxTupleToDefaultPreferenceResolver = new CXTupleToDefaultPreferencesResolver(this.configurationProvider);
        this.cxTuple =
            this.localizationContext.getAttribute(CoreAttributes.CX_TUPLE_KEY) ||
                marketplaceToCXTupleResolver.resolve(marketplace);
        this.locale = LocaleUtil.getCanonicalLocaleString(this.resolveLocale(locale, this.cxTuple, marketplace, cxTupleToDefaultPreferenceResolver));
        this.marketplaceDefaultCurrencyCode = this.resolveDefaultCurrency(this.cxTuple, marketplace, cxTupleToDefaultPreferenceResolver);
        const queryArgs = new dist$1.PriceFormatArgsBuilder()
            .withLocale(this.locale)
            .withPriceFormatType(convertToConfig(type, PRICE_FORMAT_TYPE_MAPPINGS))
            .build();
        const configData = this.getConfigurationDataWithArgs(queryArgs, [
            dist$1.PriceFormat.SYMBOL_PREFIX,
            dist$1.PriceFormat.CURRENCY_SYMBOL_POSITION
        ]);
        this.symbolPosition = this.getCurrencySymbolPositionFromConfig(configData);
        this.symbolPrefix = configData.getStringValue(dist$1.PriceFormatResult.SYMBOL_PREFIX) || '';
    }
    /**
     * Get number format result of price
     * @param {Price} price - The price to be formatted
     * @return {string} number format result in string format
     */
    format(price) {
        return getDisplayString(this.getNumberFormatParts(price));
    }
    /**
     * Get number format result of price
     * @param {Price} price - The price to be formatted
     * @return {I18NPriceFormatParts} number format result in the form of I18NPriceFormatParts
     */
    formatToParts(price) {
        const numberFormatParts = this.getNumberFormatParts(price);
        const fieldOrder = determineFieldOrder(numberFormatParts, this.type);
        const integerValue = getNumberFormatPartValue(numberFormatParts, INTEGER_TYPE);
        const currencySymbol = getNumberFormatPartValue(numberFormatParts, CURRENCY_TYPE);
        const decimalSeparator = getNumberFormatPartValue(numberFormatParts, DECIMAL_TYPE);
        const fractionalValue = getNumberFormatPartValue(numberFormatParts, FRACTION_TYPE);
        if (!integerValue || !currencySymbol) {
            throw new Error('Currency symbol or integer value are not present');
        }
        const displayFields = {
            fieldOrder,
            integerValue,
            currencySymbol,
            decimalSeparator,
            fractionalValue
        };
        return {
            amount: price.amount,
            currency: price.currency,
            displayString: getDisplayString(numberFormatParts),
            displayFields
        };
    }
    getConfigurationData(currencyCode, configNames) {
        if (dist$1.isNullOrUndefined(currencyCode)) {
            throw new Error('The currency code must not be null or undefined');
        }
        const preCalculatedData = new dist$1.ConfigurationData();
        this.checkCachedConfig(currencyCode, configNames, preCalculatedData);
        if (configNames.length == 0) {
            return preCalculatedData;
        }
        const args = this.buildQueryArgs(currencyCode);
        const configData = this.getConfigurationDataWithArgs(args, configNames);
        this.cacheConfig(currencyCode, configNames, configData);
        configData.merge(preCalculatedData);
        return configData;
    }
    resolveSymbolPositionOverride(currencyCode) {
        let config;
        if (currencyCode) {
            config = this.getConfigurationData(currencyCode, [dist$1.PriceFormat.CURRENCY_SYMBOL_POSITION]);
        }
        else {
            const args = new dist$1.PriceFormatArgsBuilder()
                .withLocale(this.locale)
                .withPriceFormatType(convertToConfig(this.type, PRICE_FORMAT_TYPE_MAPPINGS))
                .build();
            config = this.getConfigurationDataWithArgs(args, [dist$1.PriceFormat.CURRENCY_SYMBOL_POSITION]);
        }
        return this.getCurrencySymbolPositionFromConfig(config);
    }
    resolveTrailingZeroDisplay(currencyCode) {
        // use the trailingZeroDisplay defined in construction if specified
        if (this.trailingZeroDisplay !== undefined) {
            return this.trailingZeroDisplay;
        }
        const configData = this.getConfigurationData(currencyCode, [dist$1.PriceFormat.TRAILING_ZERO_DISPLAY]);
        const trailingZeroDisplay = configData.getEnumValue(dist$1.TrailingZeroDisplay, dist$1.PriceFormatResult.TRAILING_ZERO_DISPLAY);
        return convertFromConfig(trailingZeroDisplay, TRAILING_ZERO_DISPLAY_MAPPINGS) || "AUTO" /* TrailingZeroDisplay.AUTO */;
    }
    resolveConfigProvider() {
        if (!dist$1.ConfigurationProviderManager.hasProvider()) {
            // No ConfigurationProvider has been set yet. Make a default selection.
            // Currently, the default will be Resource Bundle based as that matches the current
            // implementation. This will be replaced with an ACS based provider once
            // those have undergone brave-heart client testing (controlled roll-out).
            dist$1.ConfigurationProviderManager.register(new ResourceBundleI18nConfigurationProvider());
        }
        return dist$1.ConfigurationProviderManager.getProvider();
    }
    resolveLocale(locale, cxTuple, marketplace, cxTupleToDefaultPreferenceResolver) {
        if (!dist$1.isNullOrUndefined(locale)) {
            return locale !== ''
                ? locale
                : (() => {
                    console.warn('An empty locale string was passed in; this will be interpreted as the Root locale. ' +
                        'This is generally an I18n anti-pattern. Consider passing in a valid BCP 47 language tag.');
                    return 'und';
                })();
        }
        const localeData = cxTupleToDefaultPreferenceResolver.resolveDefaultLanguage(cxTuple);
        if (localeData !== '') {
            return localeData;
        }
        return marketplace.getDefaultLocale() || 'und';
    }
    resolveDefaultCurrency(cxTuple, marketplace, cxTupleToDefaultPreferenceResolver) {
        const currencyData = cxTupleToDefaultPreferenceResolver.resolveDefaultCurrency(cxTuple);
        return currencyData !== '' ? currencyData : marketplace.getDefaultCurrency();
    }
    getConfigurationDataWithArgs(args, configNames) {
        const configuration = new dist$1.ConfigurationData();
        if (dist$1.isNullOrUndefined(args)) {
            console.log(`Return empty configuration data with args undefined.`);
            return configuration;
        }
        for (const configName of configNames) {
            const config = this.configurationProvider.getConfiguration(configName, args);
            configuration.merge(config.configData);
        }
        return configuration;
    }
    /**
     * Check the cached configuration if the specific config has been loaded before with the specific currencyCode.
     * If so, the {@link ConfigName} will be removed from the queryConfigs list and merge the cached value to given configData.
     * <br/>
     * Notes: the arguments queryConfigs and configData are updated inside the method.
     */
    checkCachedConfig(currencyCode, configNames, preCalculatedData) {
        // check if values are pre-calculated locally already. If they are, remove them from the query
        // and merge the cached config to the preCalculatedData
        if (this.currencyFormatCache && this.currencyFormatCache.key === currencyCode) {
            removeIfPresent(configNames, dist$1.PriceFormat.CURRENCY_FORMAT);
            preCalculatedData.merge(this.currencyFormatCache.value);
        }
        // check if the trailingZeroDisplay is specified in constructor or the config has been cached with the same currency code
        if (this.trailingZeroDisplay ||
            (this.trailingZeroDisplayCache && this.trailingZeroDisplayCache.key === currencyCode)) {
            removeIfPresent(configNames, dist$1.PriceFormat.TRAILING_ZERO_DISPLAY);
            const preCalculated = this.trailingZeroDisplay
                ? convertToConfig(this.trailingZeroDisplay, TRAILING_ZERO_DISPLAY_MAPPINGS)
                : this.trailingZeroDisplayCache.value;
            const trailingZeroDisplayConfig = new dist$1.I18nConfiguration.Builder()
                .addConfiguration(dist$1.PriceFormatResult.TRAILING_ZERO_DISPLAY, preCalculated)
                .build();
            preCalculatedData.merge(trailingZeroDisplayConfig.configData);
        }
        // Check if the currencySymbolPosition config has cached with the same currency code
        if (this.symbolPositionCache && this.symbolPositionCache.key === currencyCode) {
            removeIfPresent(configNames, dist$1.PriceFormat.CURRENCY_SYMBOL_POSITION);
            const symbolPositionConfig = new dist$1.I18nConfiguration.Builder()
                .addConfiguration(dist$1.PriceFormatResult.CURRENCY_SYMBOL_POSITION, this.symbolPositionCache.value)
                .build().configData;
            preCalculatedData.merge(symbolPositionConfig);
        }
        // Check if the symbolPrefix config has cached with the same currency code
        if (this.symbolPrefixCache && this.symbolPrefixCache.key === currencyCode) {
            removeIfPresent(configNames, dist$1.PriceFormat.SYMBOL_PREFIX);
            const symbolPrefixConfig = new dist$1.I18nConfiguration.Builder()
                .addConfiguration(dist$1.PriceFormatResult.SYMBOL_PREFIX, this.symbolPrefixCache.value)
                .build().configData;
            preCalculatedData.merge(symbolPrefixConfig);
        }
    }
    cacheConfig(currencyCode, configNames, configData) {
        // cache custom symbol overrides, if they were queried
        if (configNames.includes(dist$1.PriceFormat.CURRENCY_FORMAT)) {
            this.currencyFormatCache = { key: currencyCode, value: configData };
        }
        // cache the CurrencySymbolPosition override, if it was queried
        if (configNames.includes(dist$1.PriceFormat.CURRENCY_SYMBOL_POSITION)) {
            this.symbolPositionCache = {
                key: currencyCode,
                value: configData.getEnumValue(dist$1.CurrencySymbolPosition, dist$1.PriceFormatResult.CURRENCY_SYMBOL_POSITION)
            };
        }
        // cache TrailingZeroDisplay, if it was queried
        if (configNames.includes(dist$1.PriceFormat.TRAILING_ZERO_DISPLAY)) {
            this.trailingZeroDisplayCache = {
                key: currencyCode,
                value: configData.getEnumValue(dist$1.TrailingZeroDisplay, dist$1.PriceFormatResult.TRAILING_ZERO_DISPLAY)
            };
        }
        // cache SymbolPrefix, if it was queried
        if (configNames.includes(dist$1.PriceFormat.SYMBOL_PREFIX)) {
            this.symbolPrefixCache = {
                key: currencyCode,
                value: configData.getStringValue(dist$1.PriceFormatResult.SYMBOL_PREFIX)
            };
        }
    }
    getCurrencySymbolPositionFromConfig(configData) {
        const position = configData.getEnumValue(dist$1.CurrencySymbolPosition, dist$1.PriceFormatResult.CURRENCY_SYMBOL_POSITION);
        return convertFromConfig(position, CURRENCY_SYMBOL_POSITION_MAPPINGS) || "default" /* CurrencySymbolPosition.DEFAULT */;
    }
    buildQueryArgs(currencyCode) {
        if (OVERRIDE_SUPPORTED_CX_FAMILIES.has(this.cxTuple.cxFamily)) {
            return new dist$1.PriceFormatArgsBuilder()
                .withCurrency(currencyCode)
                .withCxFamily(this.cxTuple.cxFamily)
                .withCxId(this.cxTuple.cxId)
                .withLocale(this.locale)
                .withPriceFormatType(convertToConfig(this.type, PRICE_FORMAT_TYPE_MAPPINGS))
                .build();
        }
        else if (CXTuple.UNKNOWN.cxFamily === this.cxTuple.cxFamily) {
            return new dist$1.PriceFormatArgsBuilder()
                .withCurrency(currencyCode)
                .withLocale(this.locale)
                .withPriceFormatType(convertToConfig(this.type, PRICE_FORMAT_TYPE_MAPPINGS))
                .build();
        }
        else {
            return undefined;
        }
    }
    /**
     * Returns a NumberFormat object configured for the Currency, Type and Locale passed in.
     * @param {Currency} currency - The requested currency
     * @return {AmazonNumberFormat} The Amazon number format.
     */
    getFormat(currency) {
        // boolean flag to determine if the custom symbol overrides occur in this format.
        let symbolOverrideApplied = false;
        let insertSpaceInPattern = false;
        let currencySymbol;
        let symbolPositionOverride = "default" /* CurrencySymbolPosition.DEFAULT */;
        const configData = this.getConfigurationData(currency.currencyCode, [
            dist$1.PriceFormat.CURRENCY_FORMAT,
            dist$1.PriceFormat.SYMBOL_PREFIX,
            dist$1.PriceFormat.CURRENCY_SYMBOL_POSITION
        ]);
        if (shouldApplyCustomSymbol(this.type)) {
            const customSymbol = configData.getStringValue(dist$1.PriceFormatResult.SYMBOL);
            if (this.marketplaceDefaultCurrencyCode &&
                !(this.marketplaceDefaultCurrencyCode === currency.currencyCode) &&
                !customSymbol) {
                symbolOverrideApplied = true;
                currencySymbol = this.symbolPrefix + currency.currencyCode;
                /* Insert space between currency symbol and amount, since we are replacing
                   with ISO code.*/
                insertSpaceInPattern = true;
            }
            else if (customSymbol) {
                currencySymbol = customSymbol;
                symbolOverrideApplied = true;
                insertSpaceInPattern = configData.getBooleanValue(dist$1.PriceFormatResult.INSERT_SPACE_IN_PATTERN) || false;
            }
        }
        if (shouldApplySymbolPositionOverride(this.type)) {
            symbolPositionOverride =
                this.symbolPosition !== "default" /* CurrencySymbolPosition.DEFAULT */
                    ? this.symbolPosition
                    : this.resolveSymbolPositionOverride(currency.currencyCode);
        }
        return {
            numberFormat: this.getNumberFormatFromCacheOrCreate(this.locale, currency, this.type),
            currencySymbol,
            insertSpaceInPattern,
            symbolOverrideApplied,
            symbolPosition: symbolPositionOverride
        };
    }
    /**
     * Get Intl.NumberFormatPart result of price
     * @param {Price} price - The price to be formatted
     * @return {Intl.NumberFormatPart[]} Array of number format components
     */
    getNumberFormatParts(price) {
        const currency = price.currency;
        const amazonNumberFormat = this.getFormat(currency);
        const amount = roundIfHalfEven(price.amount, amazonNumberFormat.numberFormat.resolvedOptions().minimumFractionDigits);
        // Check whether amount is a number for consumers without un-type-checked content
        if (typeof amount !== 'number' || Number.isNaN(amount)) {
            throw new Error('Amount is not a number');
        }
        const roundedAmount = roundAmount(amount, this.type);
        let numberFormatParts = amazonNumberFormat.numberFormat.formatToParts(roundedAmount);
        if (this.type === "compact" /* Type.Compact */) {
            numberFormatParts = removeSpaceBetweenSymbolAndAmount(numberFormatParts);
        }
        else if (amazonNumberFormat.insertSpaceInPattern) {
            numberFormatParts = insertSpaceInFormat(numberFormatParts);
        }
        if (amazonNumberFormat.symbolPosition !== "default" /* CurrencySymbolPosition.DEFAULT */) {
            numberFormatParts = updateFormatWithSymbolPosition(this.type, numberFormatParts, amazonNumberFormat.symbolPosition);
        }
        if (!numberFormatParts) {
            throw new Error('Intl.NumberFormat formatToParts returned empty');
        }
        if (this.resolveTrailingZeroDisplay(currency.currencyCode) === "HIDE_IF_WHOLE" /* TrailingZeroDisplay.HIDE_IF_WHOLE */) {
            numberFormatParts = this.hideTrailingZeroIfInteger(price, amazonNumberFormat, numberFormatParts);
        }
        return numberFormatParts.map(({ type, value }) => {
            switch (type) {
                case CURRENCY_TYPE:
                    if (amazonNumberFormat.symbolOverrideApplied && amazonNumberFormat.currencySymbol) {
                        value = amazonNumberFormat.currencySymbol;
                    }
                    return { type, value };
                default:
                    return { type, value };
            }
        });
    }
    /**
     * When the TrailingZeroDisplay.HIDE_IF_WHOLE is set, hide the trailing zero after the decimal separator when
     * the price is an integer. For example, the `.00` will be removed for price 12.00.
     * @param numberFormatParts the array of price format parts that compose a price string
     * @return the array of price format that without decimal and fraction parts if above condition is true.
     *         Otherwise, nothing changes.
     */
    hideTrailingZeroIfInteger(price, formatter, numberFormatParts) {
        // Round the amount to cover the case when the price has small decimal value like 1.001
        // the trailing zero will be hidden when TrailingZeroDisplay.HIDE_IF_WHOLE is set,
        // so the formatted string will be $1 instead of $1.00.
        const roundAmount = price.amount.toFixed(formatter.numberFormat.resolvedOptions().minimumFractionDigits);
        if (roundAmount % 1 !== 0) {
            return numberFormatParts;
        }
        return numberFormatParts.filter(part => {
            return part.type !== DECIMAL_TYPE && part.type !== FRACTION_TYPE;
        });
    }
    getNumberFormatFromCacheOrCreate(locale, currency, type) {
        const key = this.serializeNumberFormatCacheKey(locale, currency, type);
        let numberFormat = this.numberFormatCache.get(key);
        if (numberFormat === undefined) {
            numberFormat = new Intl.NumberFormat(this.locale, createCurrencyNumberFormatOptions(currency, type));
            this.numberFormatCache.set(key, numberFormat);
        }
        return numberFormat;
    }
    serializeNumberFormatCacheKey(locale, currency, type) {
        return `${locale}-${currency.currencyCode}-${type}`;
    }
    /**
     * Get a price formatter for a given locale.
     * This will use the default Type (Type.AmazonSymbol) and
     * marketplace provider's specified settings to define the default marketplace for formatting.
     * @param {string} locale - The requested locale
     * @return {PriceFormat} a price formatter
     */
    static getInstance(locale) {
        return new this.Builder()
            .withLocale(locale)
            .withType("amazonSymbol" /* Type.AmazonSymbol */)
            .build();
    }
    /**
     * Get a price formatter for a given locale and type.
     * This will use marketplace provider's specified settings to define the default marketplace for formatting
     * @param {string} locale - The requested locale
     * @param {Type} type - The type for formatting price
     * @return {PriceFormat} a price formatter
     */
    static getInstanceByType(locale, type) {
        return new this.Builder()
            .withLocale(locale)
            .withType(type)
            .build();
    }
    /**
     * Get a price formatter for a given locale and marketplace id.
     * This will use the default Type (Type.AmazonSymbol) for formatting.
     * @param {string} locale - The requested locale
     * @param {string} marketplaceId - The marketplace id
     * @return {PriceFormat} a price formatter
     */
    static getInstanceByMarketplaceId(locale, marketplaceId) {
        return new this.Builder()
            .withLocale(locale)
            .withType("amazonSymbol" /* Type.AmazonSymbol */)
            .withMarketplace(new DefaultMarketplaceImpl(marketplaceId))
            .build();
    }
    /**
     * Get a price formatter for a given locale and marketplace object.
     * This will use the default Type (Type.AmazonSymbol) for formatting.
     * @param {string} locale - The requested locale
     * @param {Marketplace} marketplace - The marketplace object
     * @return {PriceFormat} a price formatter
     */
    static getInstanceByMarketplace(locale, marketplace) {
        return new this.Builder()
            .withLocale(locale)
            .withType("amazonSymbol" /* Type.AmazonSymbol */)
            .withMarketplace(marketplace)
            .build();
    }
    /**
     * Get a price formatter for a given locale, type and marketplace id.
     * @param {string} locale - The requested locale
     * @param {Type} type - The type for formatting price
     * @param {string} marketplaceId - The marketplace id
     * @return {PriceFormat} a price formatter
     */
    static getInstanceByTypeAndMarketplaceId(locale, type, marketplaceId) {
        return new this.Builder()
            .withLocale(locale)
            .withType(type)
            .withMarketplace(new DefaultMarketplaceImpl(marketplaceId))
            .build();
    }
    /**
     * Get a price formatter for a given locale, type and marketplace object.
     * @param {string} locale - The requested locale
     * @param {Type} type - The type for formatting price
     * @param {Marketplace} marketplace - The marketplace object
     * @return {PriceFormat} a price formatter
     */
    static getInstanceByTypeAndMarketplace(locale, type, marketplace) {
        return new this.Builder()
            .withLocale(locale)
            .withType(type)
            .withMarketplace(marketplace)
            .build();
    }
}
/**
 * Builder class to build the price format instance
 */
PriceFormat.Builder = (_a = class {
        constructor() { }
        /**
         * Set the {@link PriceFormat#locale}
         * @param {string} locale - the locale used for formatting price
         * @return {Builder} the builder instance
         */
        withLocale(locale) {
            this.locale = locale;
            return this;
        }
        /**
         * Set the {@link PriceFormat#type}
         * @param {Type} type - the type used for formatting price
         * @return {Builder} the builder instance
         */
        withType(type) {
            this.type = type;
            return this;
        }
        /**
         * Set the {@link PriceFormat#marketplace}
         * @param {Marketplace} marketplace - the marketplace used for formatting price
         * @return {Builder} the builder instance
         */
        withMarketplace(marketplace) {
            this.marketplace = marketplace;
            return this;
        }
        /**
         * Set the {@link PriceFormat#marketplace}
         * @param {string} marketplaceId - the obfuscated marketplace ID used for formatting price
         * @return {Builder} the builder instance
         */
        withMarketplaceId(marketplaceId) {
            this.marketplace = new DefaultMarketplaceImpl(marketplaceId);
            return this;
        }
        /**
         * Set the {@link PriceFormat#trailingZeroDisplay}
         * @param {TrailingZeroDisplay} trailingZeroDisplay - the trailingZeroDisplay used for formatting price
         *         - AUTO: Display trailing zeros according to the settings for minimum fraction and significant digits.
         *         - HIDE_IF_WHOLE: Same as AUTO, but hide trailing zeros after the decimal separator if they are all zero.
         * @return {Builder} the builder instance
         */
        withTrailingZeroDisplay(trailingZeroDisplay) {
            this.trailingZeroDisplay = trailingZeroDisplay;
            return this;
        }
        /**
         * Sets the Locale and possibly other attributes (Marketplace) of the
         * PriceFormat instance from the provided LocalizationContext.
         */
        withLocalizationContext(localizationContext) {
            this.locale = localizationContext.getLocale();
            this.localizationContext = localizationContext;
            const attrMap = localizationContext.asAttributesMap();
            const marketplace = attrMap.get(MARKETPLACE_ATTRIBUTE);
            if (marketplace instanceof Marketplace) {
                this.marketplace = marketplace;
            }
            return this;
        }
        /**
         * Builds the PriceFormat. This method can be called multiple times with different 'withXXX' calls in-between.
         * For params that can be derived from multiple inputs (e.g. locale can be obtained from withLocale or
         * withLocalizationContext), the input supplied in the last withXXX call will take precedence.
         * <p>
         * If LocalizationContext is null, creates a default LocalizationContext with JVM defaults.
         * If marketplace is null, use the default empty marketplace.
         * If 'locale' is null, use the marketplace default locale. If marketplace default locale is null, then use the default `und` locale.
         * If no type is specified, AMAZON_SYMBOL is used as the default Type.
         */
        build() {
            const marketplace = this.marketplace || MarketplaceCreator.provider().getMarketplace();
            const localizationContext = this.localizationContext || PriceFormat.Builder.DEFAULT_LC;
            return new PriceFormat(this.locale, this.type || "amazonSymbol" /* Type.AmazonSymbol */, marketplace, localizationContext, this.trailingZeroDisplay);
        }
    },
    _a.DEFAULT_LC = new LocalizationContextBuilder().build(),
    _a);
/**
 * Given array of number format components returns the displayable string
 * @param {Intl.NumberFormatPart[]} numberFormatParts Array of number format components
 */
function getDisplayString(numberFormatParts) {
    return numberFormatParts.map(({ value }) => value).reduce((string, part) => string + part);
}
/**
 * Given an array of number format components get the value of the given type
 * @param {Intl.NumberFormatPart[]} numberFormatParts Array of number format components
 * @param {Intl.NumberFormatPartTypes} numberFormatPartType Requested number format type
 */
function getNumberFormatPartValue(numberFormatParts, numberFormatPartType) {
    let value = '';
    for (const numberFormatPart of numberFormatParts) {
        if (numberFormatPartType === INTEGER_TYPE &&
            (numberFormatPart.type === INTEGER_TYPE || numberFormatPart.type === GROUP_TYPE)) {
            value += numberFormatPart.value;
        }
        else if (numberFormatPart.type === numberFormatPartType) {
            return numberFormatPart.value;
        }
    }
    return value ? value : undefined;
}
/**
 * Given a number format parts array find the field order of the to be displayed price
 * @param {Intl.NumberFormatPart[]} numberFormatParts Array of number format components
 * @param {Type} type Requested price format type
 */
function determineFieldOrder(numberFormatParts, type) {
    if (!hasFraction(numberFormatParts) || type === "compact" /* Type.Compact */) {
        return !isSymbolLast(numberFormatParts) ? 2 /* FieldOrder.SYMBOL_NUMBER */ : 5 /* FieldOrder.NUMBER_SYMBOL */;
    }
    if (!isSymbolLast(numberFormatParts)) {
        return hasDecimalSeparator(numberFormatParts)
            ? 0 /* FieldOrder.SYMBOL_NUMBER_SEPARATOR_DECIMAL */
            : 1 /* FieldOrder.SYMBOL_NUMBER_DECIMAL */;
    }
    else {
        return hasDecimalSeparator(numberFormatParts)
            ? 3 /* FieldOrder.NUMBER_SEPARATOR_DECIMAL_SYMBOL */
            : 4 /* FieldOrder.NUMBER_DECIMAL_SYMBOL */;
    }
}
/**
 * Update the pattern if necessary to have the currency symbol at the desired position.
 *
 * @param {Intl.NumberFormatPart[]} formatParts - Array of price format components
 * @param {Type} type - The requested formatting type
 * @param {CurrencySymbolPosition} symbolPosition - Custom Symbol position for override
 * @return {Intl.NumberFormatPart[]} Array containing the format components in the updated order
 */
function updateFormatWithSymbolPosition(type, formatParts, symbolPosition) {
    if (symbolPosition === "end" /* CurrencySymbolPosition.END */ && !isSymbolLast(formatParts)) {
        // If space is already present in the pattern, move it to the end of the format
        formatParts = updateSpacePositionInFormatParts(type, formatParts, symbolPosition, formatParts.length - 1, false);
        const symbolIndex = indexOfNumberFormatPart(formatParts, CURRENCY_TYPE, false);
        // Move currency symbol to the end of the price format
        formatParts = moveArrayElement(formatParts, symbolIndex, formatParts.length - 1);
    }
    else if (symbolPosition === "start" /* CurrencySymbolPosition.START */ && isSymbolLast(formatParts)) {
        let newSymbolIndex = 0;
        // In case there is a literal in starting.
        if (formatParts[newSymbolIndex].type === LITERAL_TYPE) {
            newSymbolIndex++;
        }
        // In case the number is negative.
        if (formatParts[newSymbolIndex].type === MINUS_SIGN_TYPE) {
            newSymbolIndex++;
        }
        // If space is already present in the pattern, move it to the start of the format
        formatParts = updateSpacePositionInFormatParts(type, formatParts, symbolPosition, newSymbolIndex, true);
        const currentSymbolIndex = indexOfNumberFormatPart(formatParts, CURRENCY_TYPE, true);
        // Move currency symbol to the start of the price format
        formatParts = moveArrayElement(formatParts, currentSymbolIndex, newSymbolIndex);
    }
    return formatParts;
}
/**
 * Update the pattern with correct space position (if found) as per the symbol position -
 *      1. it will be appended in end when custom symbol position is at end
 *      2. it will be appended in start when custom symbol position is at start
 * In case type is COMPACT space will be removed.
 *
 * @param {Intl.NumberFormatPart[]} formatParts - Array of price format components
 * @param {Type} type - The requested formatting type
 * @param {CurrencySymbolPosition} symbolPosition - Custom Symbol position for override
 * @param {number} newSpaceIndex - Index for updating the space position
 * @param {boolean} isSymbolLast - True in case symbol position is last false otherwise
 * @return {Intl.NumberFormatPart[]} Array containing the format components in the updated order
 */
function updateSpacePositionInFormatParts(type, formatParts, symbolPosition, newSpaceIndex, isSymbolLast) {
    if (hasSpace(formatParts)) {
        const currentSpaceIndex = indexOfNumberFormatPart(formatParts, LITERAL_TYPE, isSymbolLast);
        if (symbolPosition === "end" /* CurrencySymbolPosition.END */) {
            // Strip RTL characters from space (e.g. in case of Arabic locale)
            formatParts[currentSpaceIndex].value = formatParts[currentSpaceIndex].value.replace(SPECIAL_CHARS_REGEX, '');
        }
        // Update space position.
        formatParts = moveArrayElement(formatParts, currentSpaceIndex, newSpaceIndex);
        // Remove the space from the pattern for COMPACT type
        if (type === "compact" /* Type.Compact */) {
            formatParts.splice(newSpaceIndex, 1);
        }
    }
    return formatParts;
}
/**
 * remove the space between the currency symbol and numerical portion of the price format pattern.
 * @param formatParts - Array of price format components
 * @return Array of price format components without space in the pattern
 */
function removeSpaceBetweenSymbolAndAmount(formatParts) {
    if (hasSpace(formatParts)) {
        let spaceIndex;
        if (!isSymbolLast(formatParts)) {
            spaceIndex = indexOfNumberFormatPart(formatParts, LITERAL_TYPE, false);
        }
        else {
            spaceIndex = indexOfNumberFormatPart(formatParts, LITERAL_TYPE, true);
        }
        if (formatParts[spaceIndex].type === LITERAL_TYPE && formatParts[spaceIndex].value === NO_BREAK_SPACE) {
            formatParts.splice(spaceIndex, 1);
        }
    }
    return formatParts;
}
/**
 * Update the price format pattern with a space between the currency symbol and numerical
 * portion of the format if there already isn't a space present there.
 * @param {Intl.NumberFormatPart[]} formatParts - Array of price format components
 * @return {Intl.NumberFormatPart[]} Array of price format components with space in the pattern
 */
function insertSpaceInFormat(formatParts) {
    const literalPart = {
        type: LITERAL_TYPE,
        value: NO_BREAK_SPACE
    };
    if (!hasSpace(formatParts)) {
        let symbolIndex;
        if (!isSymbolLast(formatParts)) {
            symbolIndex = indexOfNumberFormatPart(formatParts, CURRENCY_TYPE, false);
            // Insert space after currency symbol
            formatParts.splice(symbolIndex + 1, 0, literalPart);
        }
        else {
            symbolIndex = indexOfNumberFormatPart(formatParts, CURRENCY_TYPE, true);
            // Insert space before currency symbol
            formatParts.splice(symbolIndex, 0, literalPart);
        }
    }
    return formatParts;
}
/**
 * Returns a NumberFormatOptions object configured for the currency and type passed in.
 * @param {Currency} currency - The requested currency
 * @param {Type} type - The requested formatting type
 * @return {Intl.NumberFormatOptions} The number format options
 */
function createCurrencyNumberFormatOptions(currency, type) {
    return Object.assign({ currency: currency.currencyCode }, createNumberFormatOptions(type));
}
/**
 * Returns a NumberFormatOptions object configured for the currency and type passed in
 * @param {Currency} currency - The requested currency
 * @param {Type} type - The requested formatting type
 * @return {Intl.NumberFormatOptions} The number format options
 */
function createNumberFormatOptions(type) {
    return Object.assign({ style: CURRENCY_STYLE, currencyDisplay: getCurrencyDisplay(type), useGrouping: getUseGrouping(type) }, getMinimumAndMaximumFractionDigits(type));
}
/**
 * Returns a CurrencyDisplay configured for the Type passed in.
 * @param {Type} type - The requested formatting type
 * @return {string} The currency display value
 */
function getCurrencyDisplay(type) {
    switch (type) {
        case "iso" /* Type.Iso */:
            return 'code';
        case "plural" /* Type.Plural */:
            return 'name';
        default:
            return 'symbol';
    }
}
/**
 * Returns whether to use grouping configured for the Type passed in.
 * @param {Type} type - The requested formatting type
 * @return {boolean} Boolean for whether to use grouping or not
 */
function getUseGrouping(type) {
    switch (type) {
        case "compact" /* Type.Compact */:
            return false;
        default:
            return true;
    }
}
/**
 * Returns what minimum and maximumFractionDigits to use configured for the Type passed in.
 * @param {Type} type - The requested formatting type
 * @return {Intl.NumberFormatOptions} The number format options
 */
function getMinimumAndMaximumFractionDigits(type) {
    switch (type) {
        case "compact" /* Type.Compact */:
            return {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0
            };
        default:
            return {};
    }
}
/**
 * Rounds the amount down or up based on Type and amount.
 * @param {number} amount - The requested amount
 * @param {Type} type - The requested formatting type
 * @return {number} The rounded amount
 */
function roundAmount(amount, type) {
    switch (type) {
        case "compact" /* Type.Compact */:
            // Rounding towards zero.
            if (amount < 0) {
                const roundedUp = Math.ceil(amount);
                // Math.ceil can result in '-0'
                return roundedUp ? roundedUp : 0;
            }
            return Math.floor(amount);
        default:
            return amount;
    }
}
/**
 * Returns whether to use a custom symbol in formatted price.
 * @param {Type} type - The requested formatting type
 */
function shouldApplyCustomSymbol(type) {
    switch (type) {
        case "iso" /* Type.Iso */:
            return false;
        case "plural" /* Type.Plural */:
            return false;
        default:
            return true;
    }
}
/**
 * Returns whether the given number format pattern contains a space in the currency format
 * @param {Intl.NumberFormatPart[]} formatPartType - Array of price format components
 */
function hasSpace(formatParts) {
    let spaceIndex = 1;
    if (!isSymbolLast(formatParts)) {
        // For Arabic locales, the first symbol can be a left-to-right mark of type 'literal'
        if (formatParts[spaceIndex - 1].type === LITERAL_TYPE) {
            spaceIndex++;
        }
        // For negative numbers, there can be a minus sign before the currency symbol
        if (formatParts[spaceIndex - 1].type === MINUS_SIGN_TYPE) {
            spaceIndex++;
        }
    }
    else {
        spaceIndex = formatParts.length;
        // Check for Literal, some Arabic locales have a Literal on end.
        if (formatParts[spaceIndex - 1].type === LITERAL_TYPE) {
            spaceIndex--;
        }
        // Check for space before currency symbol if symbol is on end.
        spaceIndex = spaceIndex - 2;
    }
    return formatParts[spaceIndex].type === LITERAL_TYPE;
}
/**
 * Returns whether the symbolPosition override needs to be applied for the given price format Type.
 * @param {Type} type - The requested price format type
 */
function shouldApplySymbolPositionOverride(type) {
    switch (type) {
        case "iso" /* Type.Iso */:
            return false;
        default:
            return true;
    }
}
/**
 * Returns whether the currency symbol is at the last (right) of the price format.
 * @param {Intl.NumberFormatPart[]} formatParts - Array of price format components
 * @return {boolean} true in case currency symbol is at last, otherwise false.
 */
function isSymbolLast(formatParts) {
    let symbolIndex = formatParts.length - 1;
    // For Arabic locales, the last symbol can be of type 'literal'.
    if (formatParts[symbolIndex].type === LITERAL_TYPE) {
        symbolIndex--;
    }
    return formatParts[symbolIndex].type === CURRENCY_TYPE;
}
/**
 * Checks whether given formatParts contains a fractional part
 * @param {Intl.NumberFormatPart[]} formatParts Array of price format components
 */
function hasFraction(formatParts) {
    return hasNumberFormatPart(formatParts, FRACTION_TYPE);
}
/**
 * Checks whether given formatParts contains a decimal separator part
 * @param {Intl.NumberFormatPart[]} formatParts Array of price format components
 */
function hasDecimalSeparator(formatParts) {
    return hasNumberFormatPart(formatParts, DECIMAL_TYPE);
}
/**
 * Checks whether given formatParts contains a part with the requested type.
 * @param {Intl.NumberFormatPart[]} formatParts Array of price format components
 * @param {Intl.NumberFormatPartTypes} type Requested type
 */
function hasNumberFormatPart(formatParts, type) {
    if (!formatParts) {
        throw new Error('Format Parts must not be emtpy');
    }
    for (const formatPart of formatParts) {
        if (formatPart.type === type) {
            return true;
        }
    }
    return false;
}
/**
 * Utility function to move elements from position A to position B within the given array.
 * @param {Intl.NumberFormatPart[]} array - Input array
 * @param {number} from - Index of element to be moved
 * @param {number} to - Destination index for the chosen element
 * @return {Intl.NumberFormatPart[]} array with rearranged elements as per given position of element
 */
function moveArrayElement(array, from, to) {
    if (to === from) {
        return array;
    }
    const target = array[from];
    const increment = to < from ? -1 : 1;
    for (let k = from; k !== to; k += increment) {
        array[k] = array[k + increment];
    }
    array[to] = target;
    return array;
}
/**
 * Utility function to find the index of the a given number format type within the currency format pattern.
 * @param {Intl.NumberFormatPart[]} formatParts - Array of price format components
 * @param {Intl.NumberFormatPartTypes} type - The input number format part type
 * @param {boolean} isSymbolLast - True in case symbol position is last false otherwise
 * @return {number} index of given number format type, -1 if not found
 */
function indexOfNumberFormatPart(formatParts, type, isSymbolLast) {
    let startIndex;
    let endIndex;
    let incrementCounter;
    if (isSymbolLast) {
        // Start the loop from end.
        startIndex = formatParts.length - 1;
        endIndex = 0;
        incrementCounter = -1;
    }
    else {
        // Start the loop from front.
        startIndex = 0;
        endIndex = formatParts.length - 1;
        incrementCounter = 1;
    }
    while (startIndex !== endIndex) {
        if (formatParts[startIndex].type === type) {
            // Ignore cases for 'literal' types as the first or last character (e.g. Arabic locale LTR mark)
            if (type === LITERAL_TYPE && (startIndex === 0 || startIndex === formatParts.length - 1)) {
                startIndex += incrementCounter;
                continue;
            }
            else {
                return startIndex;
            }
        }
        startIndex += incrementCounter;
    }
    // Check for last index.
    if (formatParts[startIndex].type === type) {
        return startIndex;
    }
    return -1;
}
/**
 * Utility function to apply halfEven rounding mode only when the rounding value is 0.5 after multiplication with scale,
 * for example 12.345 (1234.5 after multiplication with 100) will be rounded here with half even.
 * This is for consistency with rounding behavior of Java that applies HALF_EVEN rounding mode by default, while EXPAND rounding mode in typescript.
 * https://sim.amazon.com/issues/I18NFND-96
 *
 * @param price the price to format
 * @param fractionDigits the default fraction digits for the currency
 */
function roundIfHalfEven(price, fractionDigits) {
    const scale = Math.pow(10, fractionDigits ? fractionDigits : 0);
    const scaledAmount = Number((price * scale).toFixed(5));
    if (scaledAmount % 0.5 !== 0) {
        return price;
    }
    return Math.floor(scaledAmount) % 2 === 0 ? Math.floor(scaledAmount) / scale : Math.round(scaledAmount) / scale;
}

/**
 * Map PriceModifierType to i18n.price-format price type.
 *
 * @param {PriceModifierType} modifierType price modifier.
 * @returns {Type} i18n.price-format price type.
 */
function getI18nPriceType(modifierType) {
    switch (modifierType) {
        case 'compact':
            return "compact" /* Compact */;
        case 'in_sentence':
            return "inSentence" /* InSentence */;
        case 'iso':
            return "iso" /* Iso */;
        case 'plural':
            return "plural" /* Plural */;
        case 'aui':
        case 'amazon_symbol':
        case 'default':
        default:
            return "amazonSymbol" /* AmazonSymbol */;
    }
}
/**
 * I18n price formatter integrating with IUTypescript directly.
 */
class I18nPriceFormatter {
    /**
     * Constructor
     *
     * @param {LocalesOrLocalizationContext} localesOrLocalizationContext - A string
     * with a BCP 47 language tag, an array of one such string, or a localization context.
     * If it's not provided, then it's resolved to a default locale.
     *
     * @param {PriceFormatOptions} options - price format options
     */
    constructor(localesOrLocalizationContext, options) {
        if (!localesOrLocalizationContext) {
            localesOrLocalizationContext = new Intl.NumberFormat().resolvedOptions()
                .locale;
        }
        const type = options && options.style
            ? getI18nPriceType(options.style)
            : getI18nPriceType('default');
        let locale;
        let marketplace;
        const pfBuilder = new PriceFormat.Builder();
        if (isLocalizationContext(localesOrLocalizationContext)) {
            locale = localesOrLocalizationContext.getLocale();
            marketplace = localesOrLocalizationContext
                .asAttributesMap()
                .get(MARKETPLACE_ATTRIBUTE);
            const localizationContext = localesOrLocalizationContext;
            pfBuilder
                .withLocalizationContext(localizationContext)
                .withMarketplace(marketplace);
        }
        else {
            // TODO: Add logging functionality and log warn message here indicating
            // localesOrLocalizationContext[] is used as an array of one locale string at this stage,
            // so only the first locale string is picked from localesOrLocalizationContext[]
            locale =
                Array.isArray(localesOrLocalizationContext) ?
                    localesOrLocalizationContext.length === 0 ? new Intl.NumberFormat().resolvedOptions().locale : localesOrLocalizationContext[0]
                    : localesOrLocalizationContext;
            pfBuilder.withLocale(locale);
        }
        this.priceFormat = pfBuilder
            .withType(type)
            .build();
    }
    format(price) {
        return this.priceFormat.format(price);
    }
}

/*
Copyright (c) 2014, Yahoo! Inc. All rights reserved.
Copyrights licensed under the New BSD License.
See the accompanying LICENSE file for terms.
*/
// AMAZON_CHANGE_END
// -- MessageFormat --------------------------------------------------------
function mergeConfig(c1, c2) {
    if (!c2) {
        return c1;
    }
    return Object.assign(Object.assign(Object.assign({}, (c1 || {})), (c2 || {})), Object.keys(c1).reduce((all, k) => {
        all[k] = Object.assign(Object.assign({}, c1[k]), (c2[k] || {}));
        return all;
    }, {}));
}
function mergeConfigs(defaultConfig, configs) {
    if (!configs) {
        return defaultConfig;
    }
    return Object.keys(defaultConfig).reduce((all, k) => {
        all[k] = mergeConfig(defaultConfig[k], configs[k]);
        return all;
    }, Object.assign({}, defaultConfig));
}
function createFastMemoizeCache(store) {
    return {
        create() {
            return {
                has(key) {
                    return key in store;
                },
                get(key) {
                    return store[key];
                },
                set(key, value) {
                    store[key] = value;
                },
            };
        },
    };
}
function createDefaultFormatters(cache = {
    number: {},
    dateTime: {},
    pluralRules: {},
    // AMAZON_CHANGE_START
    price: {},
}) {
    return {
        getNumberFormat: memoize((...args) => new Intl.NumberFormat(...args), {
            cache: createFastMemoizeCache(cache.number),
            strategy: strategies.variadic,
        }),
        getDateTimeFormat: memoize((...args) => new Intl.DateTimeFormat(...args), {
            cache: createFastMemoizeCache(cache.dateTime),
            strategy: strategies.variadic,
        }),
        getPluralRules: memoize((...args) => new Intl.PluralRules(...args), {
            cache: createFastMemoizeCache(cache.pluralRules),
            strategy: strategies.variadic,
        }),
        // AMAZON_CHANGE_START
        getPriceFormat: memoize((...args) => new I18nPriceFormatter(...args), {
            cache: createFastMemoizeCache(cache.price),
            strategy: strategies.variadic,
        }),
    };
}
class IntlMessageFormat$1 {
    constructor(message, 
    // AMAZON_CHANGE_START
    localesOrLocalizationContext = IntlMessageFormat$1.defaultLocale, 
    // AMAZON_CHANGE_END
    overrideFormats, opts) {
        this.formatterCache = {
            number: {},
            dateTime: {},
            pluralRules: {},
            // AMAZON_CHANGE_START
            price: {},
        };
        this.format = (values) => {
            const parts = this.formatToParts(values);
            // Hot path for straight simple msg translations
            if (parts.length === 1) {
                return parts[0].value;
            }
            const result = parts.reduce((all, part) => {
                if (!all.length ||
                    part.type !== PART_TYPE.literal ||
                    typeof all[all.length - 1] !== 'string') {
                    all.push(part.value);
                }
                else {
                    all[all.length - 1] += part.value;
                }
                return all;
            }, []);
            if (result.length <= 1) {
                return result[0] || '';
            }
            return result;
        };
        this.formatToParts = (values) => formatToParts(this.ast, 
        // AMAZON_CHANGE_START
        this.localesOrLocalizationContext, 
        // AMAZON_CHANGE_END
        this.formatters, this.formats, values, undefined, this.message, undefined, this.resolvedLocales);
        // AMAZON_CHANGE_START
        /**
         * Format to VDOM parts for consumers requring rendering with VDOM.
         * It is consumer's responsibility to pass the "hyperscriptRuntime"
         * with hyperscript function "createElement". Message formatting
         * library would just invoke this hyperscript function upon detection
         * of the HTML tags.
         *
         * @param hyperscriptRuntime Hyperscript runtime with "createElement(...)" h function.
         * @param values Arguments.
         * @returns Formatter parts are returned with the aid of provided hyperscript.
         */
        this.formatToVdomParts = (hyperscriptRuntime, values) => formatToVdomParts(this.ast, this.localesOrLocalizationContext, this.formatters, this.formats, hyperscriptRuntime, values, undefined, this.message, this.resolvedLocales);
        this.resolvedOptions = () => ({
            locale: this.resolvedLocale.toLocaleString(),
        });
        this.getAst = () => this.ast;
        // Defined first because it's used to build the format pattern.
        // AMAZON_CHANGE_START
        this.localesOrLocalizationContext = localesOrLocalizationContext;
        this.resolvedLocales = IntlMessageFormat$1.resolveLocale(isLocalizationContext(localesOrLocalizationContext)
            ? localesOrLocalizationContext.getLocale()
            : localesOrLocalizationContext);
        if (Array.isArray(this.resolvedLocales)) {
            // @ts-ignore
            this.resolvedLocale = new Intl.Locale(this.resolvedLocales[0]);
        }
        else {
            // @ts-ignore
            this.resolvedLocale = new Intl.Locale(this.resolvedLocales);
        }
        // AMAZON_CHANGE_END
        if (typeof message === 'string') {
            this.message = message;
            if (!IntlMessageFormat$1.__parse) {
                throw new TypeError('IntlMessageFormat.__parse must be set to process `message` of type `string`');
            }
            // Parse string messages into an AST.
            this.ast = IntlMessageFormat$1.__parse(message, {
                ignoreTag: opts !== undefined ? opts.ignoreTag : undefined,
                locale: this.resolvedLocale,
            });
        }
        else {
            this.ast = message;
        }
        if (!Array.isArray(this.ast)) {
            throw new TypeError('A message must be provided as a String or AST.');
        }
        // Creates a new object with the specified `formats` merged with the default
        // formats.
        this.formats = mergeConfigs(IntlMessageFormat$1.formats, overrideFormats);
        this.formatters =
            (opts && opts.formatters) || createDefaultFormatters(this.formatterCache);
    }
    static get defaultLocale() {
        if (!IntlMessageFormat$1.memoizedDefaultLocale) {
            IntlMessageFormat$1.memoizedDefaultLocale =
                new Intl.NumberFormat().resolvedOptions().locale;
        }
        return IntlMessageFormat$1.memoizedDefaultLocale;
    }
}
IntlMessageFormat$1.memoizedDefaultLocale = null;
// @ts-ignore
IntlMessageFormat$1.resolveLocale = (locales) => {
    // @ts-ignore	  
    return LocaleUtil.getCanonicalLocaleString(locales);
};
IntlMessageFormat$1.__parse = parse;
// Default format options used as the prototype of the `formats` provided to the
// constructor. These are used when constructing the internal Intl.NumberFormat
// and Intl.DateTimeFormat instances.
IntlMessageFormat$1.formats = {
    number: {
        integer: {
            maximumFractionDigits: 0,
        },
        currency: {
            style: 'currency',
        },
        percent: {
            style: 'percent',
        },
    },
    date: {
        short: {
            month: 'numeric',
            day: 'numeric',
            year: '2-digit',
        },
        medium: {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        },
        long: {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        },
        full: {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        },
    },
    time: {
        short: {
            hour: 'numeric',
            minute: 'numeric',
        },
        medium: {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
        },
        long: {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            timeZoneName: 'short',
        },
        full: {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            timeZoneName: 'short',
        },
    },
    // AMAZON_CHANGE_START
    price: {
        default: {
            style: 'default',
        },
        aui: {
            style: 'aui',
        },
        iso: {
            style: 'iso',
        },
        plural: {
            style: 'plural',
        },
        amazon_symbol: {
            style: 'amazon_symbol',
        },
        compact: {
            style: 'compact',
        },
        in_sentence: {
            style: 'in_sentence',
        },
    },
};

/*
Copyright (c) 2014, Yahoo! Inc. All rights reserved.
Copyrights licensed under the New BSD License.
See the accompanying LICENSE file for terms.
*/
var IntlMessageFormat = IntlMessageFormat$1;

/**
 * Wrapper around actual message formatting library.
 */
//TODO: Support for timeZone with formatter.
class DefaultMessageFormat {
    /**
     * @param message can be represented with a string pattern or AST
     * representation of a message format element. `any` type is used,
     * so we don't take additional dependency on i18n.message-format-parser
     * @param context
     * @param options
     */
    constructor(message, context, options) {
        this.message = message;
        this.context = context;
        this.options = options;
    }
    /**
     * Format message and return plain string.
     * @param args arguments for formatting message.
     * @returns formatted string.
     */
    format(args) {
        if (this.formatter === undefined) {
            this.formatter = new IntlMessageFormat(this.message, this.context);
        }
        return this.formatter.format(args);
    }
}

/**
 * This is a temporary class to substitute {@link StaticMessageBundle}. This should be removed once we clean up the
 * {@link StaticMessageBundle} class, by removing Kata related functionalities.
 *
 * Immutable MessageBundle for localization context specific Panther resources at runtime.
 * Once MessageBundle is formed using fallback mechanism, it is cached.
 */
class StaticMessageBundle {
    constructor(messages, baseName, context) {
        this.messages = messages;
        this.baseName = baseName;
        this.context = context;
    }
    /**
     * Returns the message identified by key. If the messageKey is not found in the
     * bundle, error is thrown.
     *
     * @param messageKey key of the message in the bundle.
     * @returns message for provided key. message can be pre-parsed AST.
     * @throws Exception if message key not found in bundle.
     */
    getMessage(messageKey) {
        const message = this.messages.get(messageKey);
        if (message === undefined) {
            throw new Error(`Resource does not exist: bundle=${this.baseName}, key=${messageKey}, context=${this.context}`);
        }
        return message;
    }
    /**
     * Finds the message for messageKey in the bundle and format using ICU message formatting.
     * @param messageKey key of the message in the bundle.
     * @param args arguments for message formatting.
     * @returns Formatted message.
     * @throws Exception if message key if not found in bundle.
     */
    formatMessage(messageKey, args) {
        const message = this.getMessage(messageKey);
        return new DefaultMessageFormat(message, this.context).format(args);
    }
}

/**
 * Default values for the manifest.
 */
const DEFAULT_MANIFEST = Object.freeze({
    runtimeResourceFormat: 'JsonFileHierarchy',
    fallbackMechanismAttributes: [['i18nDomain']],
});

var loglevel$1 = {exports: {}};

/*
* loglevel - https://github.com/pimterry/loglevel
*
* Copyright (c) 2013 Tim Perry
* Licensed under the MIT license.
*/

(function (module) {
(function (root, definition) {
    if (module.exports) {
        module.exports = definition();
    } else {
        root.log = definition();
    }
}(commonjsGlobal, function () {

    // Slightly dubious tricks to cut down minimized file size
    var noop = function() {};
    var undefinedType = "undefined";
    var isIE = (typeof window !== undefinedType) && (typeof window.navigator !== undefinedType) && (
        /Trident\/|MSIE /.test(window.navigator.userAgent)
    );

    var logMethods = [
        "trace",
        "debug",
        "info",
        "warn",
        "error"
    ];

    // Cross-browser bind equivalent that works at least back to IE6
    function bindMethod(obj, methodName) {
        var method = obj[methodName];
        if (typeof method.bind === 'function') {
            return method.bind(obj);
        } else {
            try {
                return Function.prototype.bind.call(method, obj);
            } catch (e) {
                // Missing bind shim or IE8 + Modernizr, fallback to wrapping
                return function() {
                    return Function.prototype.apply.apply(method, [obj, arguments]);
                };
            }
        }
    }

    // Trace() doesn't print the message in IE, so for that case we need to wrap it
    function traceForIE() {
        if (console.log) {
            if (console.log.apply) {
                console.log.apply(console, arguments);
            } else {
                // In old IE, native console methods themselves don't have apply().
                Function.prototype.apply.apply(console.log, [console, arguments]);
            }
        }
        if (console.trace) console.trace();
    }

    // Build the best logging method possible for this env
    // Wherever possible we want to bind, not wrap, to preserve stack traces
    function realMethod(methodName) {
        if (methodName === 'debug') {
            methodName = 'log';
        }

        if (typeof console === undefinedType) {
            return false; // No method possible, for now - fixed later by enableLoggingWhenConsoleArrives
        } else if (methodName === 'trace' && isIE) {
            return traceForIE;
        } else if (console[methodName] !== undefined) {
            return bindMethod(console, methodName);
        } else if (console.log !== undefined) {
            return bindMethod(console, 'log');
        } else {
            return noop;
        }
    }

    // These private functions always need `this` to be set properly

    function replaceLoggingMethods(level, loggerName) {
        /*jshint validthis:true */
        for (var i = 0; i < logMethods.length; i++) {
            var methodName = logMethods[i];
            this[methodName] = (i < level) ?
                noop :
                this.methodFactory(methodName, level, loggerName);
        }

        // Define log.log as an alias for log.debug
        this.log = this.debug;
    }

    // In old IE versions, the console isn't present until you first open it.
    // We build realMethod() replacements here that regenerate logging methods
    function enableLoggingWhenConsoleArrives(methodName, level, loggerName) {
        return function () {
            if (typeof console !== undefinedType) {
                replaceLoggingMethods.call(this, level, loggerName);
                this[methodName].apply(this, arguments);
            }
        };
    }

    // By default, we use closely bound real methods wherever possible, and
    // otherwise we wait for a console to appear, and then try again.
    function defaultMethodFactory(methodName, level, loggerName) {
        /*jshint validthis:true */
        return realMethod(methodName) ||
               enableLoggingWhenConsoleArrives.apply(this, arguments);
    }

    function Logger(name, defaultLevel, factory) {
      var self = this;
      var currentLevel;
      defaultLevel = defaultLevel == null ? "WARN" : defaultLevel;

      var storageKey = "loglevel";
      if (typeof name === "string") {
        storageKey += ":" + name;
      } else if (typeof name === "symbol") {
        storageKey = undefined;
      }

      function persistLevelIfPossible(levelNum) {
          var levelName = (logMethods[levelNum] || 'silent').toUpperCase();

          if (typeof window === undefinedType || !storageKey) return;

          // Use localStorage if available
          try {
              window.localStorage[storageKey] = levelName;
              return;
          } catch (ignore) {}

          // Use session cookie as fallback
          try {
              window.document.cookie =
                encodeURIComponent(storageKey) + "=" + levelName + ";";
          } catch (ignore) {}
      }

      function getPersistedLevel() {
          var storedLevel;

          if (typeof window === undefinedType || !storageKey) return;

          try {
              storedLevel = window.localStorage[storageKey];
          } catch (ignore) {}

          // Fallback to cookies if local storage gives us nothing
          if (typeof storedLevel === undefinedType) {
              try {
                  var cookie = window.document.cookie;
                  var location = cookie.indexOf(
                      encodeURIComponent(storageKey) + "=");
                  if (location !== -1) {
                      storedLevel = /^([^;]+)/.exec(cookie.slice(location))[1];
                  }
              } catch (ignore) {}
          }

          // If the stored level is not valid, treat it as if nothing was stored.
          if (self.levels[storedLevel] === undefined) {
              storedLevel = undefined;
          }

          return storedLevel;
      }

      function clearPersistedLevel() {
          if (typeof window === undefinedType || !storageKey) return;

          // Use localStorage if available
          try {
              window.localStorage.removeItem(storageKey);
              return;
          } catch (ignore) {}

          // Use session cookie as fallback
          try {
              window.document.cookie =
                encodeURIComponent(storageKey) + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC";
          } catch (ignore) {}
      }

      /*
       *
       * Public logger API - see https://github.com/pimterry/loglevel for details
       *
       */

      self.name = name;

      self.levels = { "TRACE": 0, "DEBUG": 1, "INFO": 2, "WARN": 3,
          "ERROR": 4, "SILENT": 5};

      self.methodFactory = factory || defaultMethodFactory;

      self.getLevel = function () {
          return currentLevel;
      };

      self.setLevel = function (level, persist) {
          if (typeof level === "string" && self.levels[level.toUpperCase()] !== undefined) {
              level = self.levels[level.toUpperCase()];
          }
          if (typeof level === "number" && level >= 0 && level <= self.levels.SILENT) {
              currentLevel = level;
              if (persist !== false) {  // defaults to true
                  persistLevelIfPossible(level);
              }
              replaceLoggingMethods.call(self, level, name);
              if (typeof console === undefinedType && level < self.levels.SILENT) {
                  return "No console available for logging";
              }
          } else {
              throw "log.setLevel() called with invalid level: " + level;
          }
      };

      self.setDefaultLevel = function (level) {
          defaultLevel = level;
          if (!getPersistedLevel()) {
              self.setLevel(level, false);
          }
      };

      self.resetLevel = function () {
          self.setLevel(defaultLevel, false);
          clearPersistedLevel();
      };

      self.enableAll = function(persist) {
          self.setLevel(self.levels.TRACE, persist);
      };

      self.disableAll = function(persist) {
          self.setLevel(self.levels.SILENT, persist);
      };

      // Initialize with the right level
      var initialLevel = getPersistedLevel();
      if (initialLevel == null) {
          initialLevel = defaultLevel;
      }
      self.setLevel(initialLevel, false);
    }

    /*
     *
     * Top-level API
     *
     */

    var defaultLogger = new Logger();

    var _loggersByName = {};
    defaultLogger.getLogger = function getLogger(name) {
        if ((typeof name !== "symbol" && typeof name !== "string") || name === "") {
          throw new TypeError("You must supply a name when creating a logger.");
        }

        var logger = _loggersByName[name];
        if (!logger) {
          logger = _loggersByName[name] = new Logger(
            name, defaultLogger.getLevel(), defaultLogger.methodFactory);
        }
        return logger;
    };

    // Grab the current global log variable in case of overwrite
    var _log = (typeof window !== undefinedType) ? window.log : undefined;
    defaultLogger.noConflict = function() {
        if (typeof window !== undefinedType &&
               window.log === defaultLogger) {
            window.log = _log;
        }

        return defaultLogger;
    };

    defaultLogger.getLoggers = function getLoggers() {
        return _loggersByName;
    };

    // ES6 default export, for compatibility
    defaultLogger['default'] = defaultLogger;

    return defaultLogger;
}));
}(loglevel$1));

var loglevel = loglevel$1.exports;

const LOGGER_NAME = 'arb-js';
const LOGGER_PREFIX = 'arb-js';
const logger = loglevel.getLogger(LOGGER_NAME);
const logLevelLogger = {
    trace(...args) {
        this.output('trace', args);
    },
    debug(...args) {
        this.output('debug', args);
    },
    info(...args) {
        this.output('info', args);
    },
    warn(...args) {
        this.output('warn', args);
    },
    error(...args) {
        this.output('error', args);
    },
    isDebugEnabled() {
        return logger.getLevel() <= logger.levels.DEBUG;
    },
    isInfoEnabled() {
        return logger.getLevel() <= logger.levels.INFO;
    },
    output(type, args) {
        args[0] = `[${LOGGER_PREFIX}] ${args[0]}`;
        // @ts-ignore TS doesn't like accessing method via object property accessors
        logger[type](...args);
    },
};
var logger$1 = logLevelLogger;

const DEFAULTS = {};
/**
 * Remote resource reader for AsyncMessageBundle. Create an instance using {@link createFetchRemoteResourceReader} or
 * {@link createNodeHttpRemoteResourceReader}.
 */
class RemoteResourceReader {
    constructor(options) {
        this.RETRY_LIMIT = 1;
        /**
         * For caching resource requests by url — so we don't make duplicate requests
         * @private
         */
        this.loaded = {};
        this.options = Object.assign(Object.assign({}, DEFAULTS), options);
    }
    // TODO: Implement allowing to define individual fallback strategy on a bundle basis
    /**
     * @inheritDoc
     * @param _bundle
     */
    readManifest(_bundle) {
        return DEFAULT_MANIFEST;
    }
    /**
     * @inheritDoc
     * @param fallbackItem
     */
    async readResource(fallbackItem) {
        for (let i = 0; i <= this.RETRY_LIMIT; i++) {
            let url;
            try {
                url = this.options.resolveBundleUrl(this.options, fallbackItem);
                if (url === undefined) {
                    logger$1.error('No resource endpoint found. Fallback item: ', fallbackItem);
                    return;
                }
                if (this.loaded[url] === undefined) {
                    logger$1.info(`Fetching ${url} for fallback item: `, fallbackItem);
                    this.loaded[url] = this.options.resolveResourcePack(url);
                }
                else {
                    logger$1.info('Request already fetched for fallback item: ', fallbackItem);
                }
                const resourcePack = await this.loaded[url];
                // Log error message if resource pack is null or undefined.
                if (resourcePack == null) {
                    logger$1.error('Loading resource has failed. Fallback item: ', fallbackItem);
                    return;
                }
                return resourcePack;
            }
            catch (e) {
                if (i < this.RETRY_LIMIT && e.retry && url) {
                    logger$1.info(`Retrying request for ${url}. Fallback item: `, fallbackItem);
                    this.loaded[url] = undefined; // reset cache for the specific url
                    continue;
                }
                logger$1.error(`Failed to load resource. Error: ${e.message}. Fallback item: `, fallbackItem);
                break;
            }
        }
        return undefined;
    }
}

class ResourceFetchError extends Error {
    constructor(message, retry = false) {
        super(message);
        this.retry = retry;
        this.retry = retry;
    }
}

/**
 * Creates RemoteResourceReader instance that uses the browser fetch API to fetch resources.
 * @param options
 */
function createFetchRemoteResourceReader(options) {
    return new RemoteResourceReader(Object.assign({ async resolveResourcePack(url) {
            const response = await fetch(url);
            const { ok, status, statusText } = response;
            if (!ok) {
                const retry = status >= 500 && status < 600; // don't retry for 4xx codes
                logger$1.error(`Failed to fetch ${url}: %O`, { status, statusText });
                throw new ResourceFetchError(`Failed to fetch ${url}`, retry);
            }
            return response.json();
        } }, options));
}

async function measurePerformance(measureName, fn) {
    if (typeof performance !== 'undefined') {
        const start = performance.now();
        await fn();
        const end = performance.now();
        logger$1.info(`${measureName}: ${end - start} ms`);
    }
    else {
        console.time(measureName);
        await fn();
        console.timeEnd(measureName);
    }
}

/**
 * Message loader to load and cache the localization context specific Panther resources
 * with fallback mechanism.
 */
class AsyncCombinedMessageBundleLoader {
    /**
     * Create MessageLoader with the resource reader and the fallback handler. Resource reader
     * is used to read the Panther resources and the Manifest while forming the MessageBundle
     * using fallback mechanism.
     *
     * @param resourceReader  To read the Panther resources and the Manifest.
     * @param fallbackHandler  To form the fallback chain to load the resource.
     */
    constructor(resourceReader, fallbackHandler) {
        this.resourceReader = resourceReader;
        this.fallbackHandler = fallbackHandler;
        this.resourcesCache = new LoadingCache(this.resourceReader.readResource.bind(this.resourceReader), {
            maxSize: AsyncCombinedMessageBundleLoader.RESOURCE_CACHE_SIZE,
            keyFn: FallbackItem.cacheKey,
        });
    }
    /**
     * Load resources using the resource reader and fallback mechanism if not present in the cache. This method may
     * initiate multiple fetch requests for current locale, fallback locale & root locale.
     * @param bundle Panther resource bundle.
     * @param context Localization context.
     * @returns MessageBundle
     * @throws error for invalid manifest.
     */
    async load(bundle, context) {
        const contextMessages = new Map();
        const manifest = this.resourceReader.readManifest(bundle);
        if (!manifest) {
            throw new Error(CONSTANTS.noManifestError);
        }
        const fallbackChain = this.fallbackHandler.getFallbackChains(bundle, manifest.fallbackMechanismAttributes, context);
        // Start fetching all required resource packs
        logger$1.info(`Requesting resource packs for "${bundle}" bundle. Localization context: %0`, context);
        await measurePerformance(`Resource pack requests for "${bundle}" bundle`, async () => {
            const promises = [];
            for (const fallbackItem of fallbackChain) {
                promises.push(this.resourcesCache.get(fallbackItem));
            }
            await Promise.all(promises);
        });
        var fallbackChainCount = 0;
        for (const fallbackItem of fallbackChain) {
            fallbackChainCount++;
            const resourcePack = (await this.resourcesCache.get(fallbackItem));
            if (resourcePack === undefined ||
                resourcePack[fallbackItem.locale] === undefined ||
                resourcePack[fallbackItem.locale][fallbackItem.bundle] === undefined) {
                logger$1.debug(`No bundled resources were found for %0`, fallbackItem);
                continue;
            }
            const messages = resourcePack[fallbackItem.locale][fallbackItem.bundle];
            for (const [id, value] of Object.entries(messages)) {
                if (!contextMessages.has(id)) {
                    contextMessages.set(id, this.findMessage(value, fallbackItem));
                }
            }
        }
        if (fallbackChainCount === 0) {
            logger$1.error('The generated locale fallback chain was empty.'
                + 'This is bad as no context messages could be populated in MessageBundle.');
        }
        return new StaticMessageBundle(contextMessages, bundle, context);
    }
    /**
     * Find assignable message from ResourcePackValue which may hold variant overrides.
     * @param value resource pack value with variant overrides
     * @param fallbackItem
     * @private
     */
    findMessage(value, fallbackItem) {
        const valueType = typeof value;
        if (valueType === 'string') {
            return value;
        }
        else if (valueType === 'object') {
            const variant = this.findMatchingVariant(value, fallbackItem);
            // Fallback to original value if no variant was found
            return variant ? variant.value : value.value;
        }
        throw new Error('Unsupported resource pack value type');
    }
    /**
     * Traverse overrides from the last entry and return the first match
     * @param puffjWithOverrides
     * @param fallbackItem
     * @private
     */
    findMatchingVariant(puffjWithOverrides, fallbackItem) {
        const overrides = puffjWithOverrides.overrides;
        for (let i = overrides.length - 1; i >= 0; i--) {
            const override = overrides[i];
            let isContextFulfilled = true;
            for (const [variantName, variantValue] of Object.entries(override.localizationContext)) {
                if (fallbackItem.variants &&
                    (!fallbackItem.variants.has(variantName) || fallbackItem.variants.get(variantName) !== variantValue)) {
                    isContextFulfilled = false;
                    break;
                }
            }
            if (isContextFulfilled) {
                return override;
            }
        }
        return undefined;
    }
}
/**
 * Maximum number of resources to cache.
 */
AsyncCombinedMessageBundleLoader.RESOURCE_CACHE_SIZE = 512;

/**
 * MessageBundleManager that loads MessageBundle from a resource pack that may consist of multiple locales & bundles.
 */
class AsyncCombinedMessageBundleManager {
    /**
     * Creates an instance of a combined message bundle manager.
     * @param localizationContextProvider
     * @param asyncResourceReader resource pack reader
     */
    constructor(localizationContextProvider, asyncResourceReader) {
        this.localizationContextProvider = localizationContextProvider;
        this.asyncResourceReader = asyncResourceReader;
        const fallbackGen = new ResourceFallbackHandler();
        this.bundleLoader = new AsyncCombinedMessageBundleLoader(asyncResourceReader, fallbackGen);
        this.bundleCache = new LoadingCache(this.generateMessageBundle.bind(this), {
            maxSize: AsyncCombinedMessageBundleManager.MESSAGE_BUNDLE_CACHE_SIZE,
            keyFn: MessageBundleCacheKey.generateCacheKey,
        });
    }
    /**
     * Generates message bundle using MessageLoader.
     * @param bundleKey
     * @protected
     */
    async generateMessageBundle(bundleKey) {
        return this.bundleLoader.load(bundleKey.bundle, bundleKey.context);
    }
    /**
     * Load Panther resources in the form of Message bundle and cache.
     * @param bundle Panther message bundle name
     * @param context localization context
     */
    async getBundle(bundle, context) {
        const currentContext = context || this.localizationContextProvider.getLocalizationContext();
        const messageBundleKey = new MessageBundleCacheKey(bundle, currentContext);
        const messageBundle = await this.bundleCache.get(messageBundleKey);
        return messageBundle;
    }
}
/**
 * Maximum number of bundles to cache.
 */
// TODO: Make this heuristic configurable?
AsyncCombinedMessageBundleManager.MESSAGE_BUNDLE_CACHE_SIZE = 512;

// TODO: Needs to be able to resolve the correct url if localeBranches was
//  configured to something like: fr-CA: [fr-CA, fr-FR, fr]; fr: [..., fr]
/**
 * Get resource pack url from manifest file.
 * @param arbManifest
 * @param fallbackItem
 */
function getUrlFromManifest(arbManifest, fallbackItem) {
    var localeData = arbManifest && arbManifest.endpointMap && arbManifest.endpointMap[fallbackItem.locale];
    return localeData && localeData[fallbackItem.bundle];
}
/**
 * Create a message bundle manager to retrieve compiled PUFF-J
 * resources in run time. mbm.getBundle() call will initiate request
 * to download compiled PUFF-J files. By default it will fetch by
 * prefixing the option.urlPrefix to the generated file name (en.json) using
 * browser's fetch() method.
 *
 * Usage:
 * const arbManifest = .... // import build time generated manifest file
 * const mbm = createAsyncMessageBundleManager({
 *   arbManifest: arbManifest,
 *   localizationContextProvider: readFromSlateToken,
 *   urlPrefix: 'translations',
 * });
 * const bundle = await mbm.getBundle('com.amazon.i18n.bundle');
 * const str = bundle.getMessage('helloWorld');
 *
 * @param options
 */
function createAsyncMessageBundleManager(options) {
    var remoteReaderOptions = {};
    if (options.resolveBundleUrl) {
        remoteReaderOptions.resolveBundleUrl = function (_options, fallbackItem) {
            return options.resolveBundleUrl(getUrlFromManifest(options.arbManifest, fallbackItem), fallbackItem.locale, fallbackItem.bundle);
        };
    }
    else {
        remoteReaderOptions.resolveBundleUrl = function (_options, fallbackItem) {
            var _a;
            var prefix = (_a = options.urlPrefix) !== null && _a !== void 0 ? _a : "";
            // this slash ensures we will request URLs relative to base URL
            if (prefix[0] !== "/") {
                prefix = "/" + prefix;
            }
            if (prefix && prefix[prefix.length - 1] !== "/") {
                prefix += "/";
            }
            return prefix + getUrlFromManifest(options.arbManifest, fallbackItem);
        };
    }
    if (options.resolveResourcePack) {
        remoteReaderOptions.resolveResourcePack = options.resolveResourcePack;
    }
    var remoteResourceReader = createFetchRemoteResourceReader(remoteReaderOptions);
    return new AsyncCombinedMessageBundleManager(new CallbackLocalizationContextProvider(options.localizationContextProvider), remoteResourceReader);
}

export { AttributeKey, AttributesMapLocalizationContext, AttributesMapLocalizationContextProvider, AttributesProviderBuilder, CXTuple, CallbackLocalizationContextProvider, CoreAttributes, DEFAULT_LOCALE_ATTRIBUTE, I18N_DOMAIN_ATTRIBUTE, LOCALE_ATTRIBUTE, LocalizationContextBuilder, LocalizationContextImplementation, MARKETPLACE_ATTRIBUTE, RetailLocalizationContext, RetailLocalizationContextBuilder, StaticAttributesProvider, TIME_ZONE_ATTRIBUTE, createAsyncMessageBundleManager };
